import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import i18n from '../../../i18n';
import './visionCliente.css';
import * as I from './vcIcons';
import {
  CLASES_SEMANA, EXTRAS, RELAJACIONES, CHARLAS, PAUTAS,
  BIBLIOTECA_CLASES, HORAS_SEMANALES, ASISTENCIA_DIAS,
} from './vcMock';
import { crearCheckoutReingreso, loadCanalData, addFavorito, removeFavorito, registrarClaseHecha, desmarcarClaseHecha, registrarReproduccion, registrarPermanencia, getClasePlayUrl, getMiMembresia, getMiEmail, getMiBloqueoDeuda, getRotativoPortada, getClasesMpdAgregado, getClasesBiblioteca, getBienvenidaVista, getTutorialReproductor, deboPreguntarValoracion, enviarVotoValoracion, valoracionAhoraNo, registrarValoracionMostrada, getCanalApps } from './canalApi';
import ValoracionPopup from './ValoracionPopup'; // Campaña "Opiniones Google" (Ann 2026-07-24)

// Etiqueta legible de cada sección del canal (para el cuadro de mando de permanencia).
const SECCION_LABELS = {
  inicio: 'Inicio', metodo: 'Inicio (Método)', mpd: 'Clases MPD', pautas: 'Pautas',
  biblioteca: 'Biblioteca', perfil: 'Mi perfil', progreso: 'Progreso', favoritos: 'Favoritos',
  novedades: 'Novedades', recompensas: 'Recompensas', consejos: 'Consejos', fisio: 'Fisioterapia',
  apps: 'Aplicaciones terapéuticas', 'apps:rutinas': 'Aplicaciones terapéuticas',
  'cat:relajaciones': 'Relajaciones', 'cat:consejos': 'Consejos ergonómicos',
  'cat:testimonios': 'Testimonios', 'cat:30min': 'Clases de 30 min',
  'cat:crecimiento': 'Crecimiento', 'cat:corporativo': 'Corporativo', 'cat:endirecto': 'En directo',
};
function labelDeSeccion(scr) {
  if (!scr) return '';
  if (SECCION_LABELS[scr]) return SECCION_LABELS[scr];
  if (scr.startsWith('area:')) return 'Área · ' + scr.slice(5);
  if (scr.startsWith('cat:')) return scr.slice(4);
  return scr;
}
import TESTIMONIOS_LASOCI from '../../../data/testimoniosLaSoci';
import ReservaServicioModal from '../../client/ReservaServicioModal';
import axios from 'axios';
import SelectorIdioma from '../../SelectorIdioma';
import ChangePasswordModal from '../../client/ChangePasswordModal';
import catalogoCa from '../../../i18n/catalogo';
import { track } from '../../../utils/track'; // rastreador de comportamiento
import CambiarTarjetaModal from '../../canal/CambiarTarjetaModal';

import RutinasMefir from './RutinasMefir'; // Aplicaciones terapéuticas (Instituto MEFIR)

/* ============================================================
   VISIÓN CLIENTE — preview del frontal del suscriptor del
   Canal Pilar Domínguez, embebido en el admin de Canal Vídeos.
   Aislado bajo .vc-shell. Datos desde vcMock (sustituibles por CRM).
   Controles de preview (tipo de usuario / dispositivo / acceso Método)
   son chrome de admin, NO forman parte de la vista del suscriptor.
   ============================================================ */

// Carga de fuentes Google (Fraunces + Outfit) una sola vez.
const FONTS_HREF = 'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600&family=Outfit:wght@300;400;500;600;700&display=swap';
function useCanalFonts() {
  useEffect(() => {
    if (document.querySelector(`link[data-vc-fonts]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = FONTS_HREF;
    link.setAttribute('data-vc-fonts', '1');
    document.head.appendChild(link);
  }, []);
}

// Avatar de la marca / paciente
const Avatar = ({ letter = 'L' }) => <div className="vc-avatar">{letter}</div>;

// Capitaliza una frase en MAYÚSCULAS a normal (1ª letra de cada palabra). Para testimonios.
const capitalizar = (s = '') => String(s).toLowerCase().split(' ')
  .map(w => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w)).join(' ');
// ¿El ítem es un testimonio? (la categoría real viaja en tipoEtiqueta, no en tipo)
const esTestimonio = (it) => !!it && (it.tipoEtiqueta || it.tipo) === 'Testimonios';

// Designación de clase COLOREADA (regla de Ann 2026-07-15, igual en tarjetas, hero
// y reproductor): INTENSIDAD en su color (Suave teal claro · Estándar verde acento),
// TIPO en blanco (Paralelo/Apertura/Mixta) y NOMBRE DEL TÉCNICO en gris claro.
const designacionColoreada = (nombre) => {
  if (!nombre) return nombre;
  const TIPOS = ['paralelo', 'apertura', 'mixta'];
  return nombre.split(' ').map((w, i) => {
    const k = w.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[.,]/g, '');
    const m = catalogoCa(w); // en 'ca' se ESCRIBE Suau/Estàndard/… (Ann 2026-07-15); el color se detecta sobre el ES de BD
    if (k === 'suave') return <span key={i} className="vc-wlvl suave">{m} </span>;
    if (k === 'estandar') return <span key={i} className="vc-wlvl estandar">{m} </span>;
    if (TIPOS.includes(k)) return <span key={i}>{m} </span>;
    return <span key={i} className="vc-wtec">{m} </span>;
  });
};

// Animación de BIENVENIDA (Ann 2026-07-13): confetti + "petardo" al entrar al canal.
// 1 vez por sesión. Botón "Lanza confeti..." la relanza. Cierra al clic fuera o sola (~7s).
function VcBienvenida({ nombre = '', modo = 'card', onDone = () => {} }) {
  const { t } = useTranslation('canal');
  const [ronda, setRonda] = useState(0);
  // modo 'card' (al LLEGAR): tarjeta de bienvenida + confeti; no se cierra sola.
  // modo 'confeti' (botón de la cabecera): SOLO confeti, sin mensaje; se cierra sola.
  useEffect(() => {
    if (modo !== 'confeti') return;
    const t = setTimeout(onDone, 3600);
    return () => clearTimeout(t);
  }, [modo, ronda]);
  const cols = ['#3f9c74', '#5fc9a8', '#e8c98a', '#7aa2f7', '#f2a6a0', '#ffffff'];
  const piezas = Array.from({ length: 52 }, (_, i) => ({
    left: Math.random() * 100,
    delay: Math.random() * 0.7,
    dur: 2.4 + Math.random() * 1.9,
    rot: Math.random() * 360,
    col: cols[i % cols.length],
    size: 7 + Math.random() * 7,
    round: i % 2 === 0,
  }));
  return (
    <div className="vc-welcome" onClick={onDone} role="dialog" aria-label={t('bienvenida.aria')}>
      <div className="vc-welcome-confetti" key={ronda} aria-hidden="true">
        {piezas.map((p, i) => (
          <span key={i} className="vc-conf" style={{
            left: `${p.left}%`, animationDelay: `${p.delay}s`, animationDuration: `${p.dur}s`,
            background: p.col, width: p.size, height: p.size,
            borderRadius: p.round ? '50%' : '2px', transform: `rotate(${p.rot}deg)`,
          }} />
        ))}
      </div>
      {modo === 'card' && (
        <div className="vc-welcome-card" onClick={(e) => e.stopPropagation()}>
          <div className="vc-welcome-burst" key={ronda} aria-hidden="true"><span /><span /><span /><span /><span /></div>
          <h2>{t('bienvenida.titulo', { nombre: nombre ? `, ${nombre}` : '' })}</h2>
          <p>{t('bienvenida.celebracion')}</p>
          <div className="vc-welcome-actions">
            <button type="button" className="vc-welcome-btn" onClick={() => setRonda((r) => r + 1)}>{t('bienvenida.lanzaMas')}</button>
            <button type="button" className="vc-welcome-entrar" onClick={onDone}>{t('bienvenida.entrar')}</button>
          </div>
        </div>
      )}
    </div>
  );
}

const VideoCard = ({ item, onPlay, isFav, onToggleFav, selSemana = false }) => {
  const { t } = useTranslation('canal');
  return (
  <div className="vc-vcard" onClick={() => onPlay(item)}>
    <div className="vc-thumb">
      <div className={`grad ${item.grad || 'g1'}`} />
      {selSemana && (
        <span className="vc-wsel" title={t('card.seleccion')} aria-label={t('card.seleccion')}>{t('card.semana')}</span>
      )}
      {item.thumbnail
        ? <img className="vc-thumb-img" alt="" loading="lazy"
            /* YouTube: la miniatura estándar (hqdefault) trae BANDAS NEGRAS pegadas y
               no llena la tarjeta (Ann 2026-07-25). Se pide la grande 16:9 (hq720) y,
               si ese vídeo no la tiene, se cae a la estándar. */
            src={/img\.youtube\.com/.test(item.thumbnail) ? item.thumbnail.replace(/\/(hqdefault|mqdefault|sddefault)\.jpg/, '/hq720.jpg') : item.thumbnail}
            onLoad={(e) => {
              /* Si el vídeo NO tiene miniatura grande, YouTube devuelve un CARTÓN GRIS
                 de 120px con éxito (el onError no salta). Se detecta por tamaño y se
                 cae a la miniatura estándar (Ann 2026-07-25, captura). */
              const el = e.currentTarget;
              if (!el.dataset.fb && el.naturalWidth <= 120 && /img\.youtube\.com/.test(el.src)) { el.dataset.fb = '1'; el.src = item.thumbnail; }
            }}
            onError={(e) => {
              const el = e.currentTarget;
              if (!el.dataset.fb && /img\.youtube\.com/.test(item.thumbnail)) { el.dataset.fb = '1'; el.src = item.thumbnail; }
              else { el.style.display = 'none'; }
            }} />
        : item.video
          ? <div className="vc-thumb-img vc-thumb-ph" aria-hidden="true"><svg width="34" height="34" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg></div>
          : null}
      {item.hecha && <div className="vc-done"><I.IcCheck2 /></div>}
      {onToggleFav && (
        <button type="button" className={`vc-vfav ${isFav && isFav(item) ? 'on' : ''}`}
          onClick={(e) => { e.stopPropagation(); onToggleFav(item); }}
          title={isFav && isFav(item) ? t('card.quitarFav') : t('card.marcarFav')}
          aria-label={t('card.favorita')}>
          <I.IcHeart />
        </button>
      )}
      <div className="vc-play"><I.IcPlay /></div>
      {item.profesor && <span className="vc-wclass-prof">{String(item.profesor).trim().split(/\s+/)[0]}</span>}
      {item.duracion ? <span className="vc-dur">{item.duracion} min</span> : null}
      {item.progreso && item.video ? <div className="vc-vbar"><i style={{ width: `${item.progreso * 100}%` }} /></div> : null}
    </div>
    {item.nivel && item.modalidad ? (
      /* Misma nomenclatura que el dashboard: nivel coloreado + modalidad. */
      <h5 className="vc-vtitle">
        <span className={`vc-wlvl ${item.nivel}`}>{catalogoCa(item.nivel === 'suave' ? 'Suave' : 'Estándar')}</span>{' '}
        <span className={`vc-wmod${item.modalidad === 'Mixta' ? ' mixta' : ''}`}>{catalogoCa(item.modalidad)}</span>
      </h5>
    ) : <h5>{item.tipo === 'clase' ? designacionColoreada(item.nombre) : catalogoCa(item.nombre)}</h5>}
    <div className="vc-vmeta">
      <span className="vc-area-pill">{item.zona}</span>
      {item.duracion ? <span className="pillz"><I.IcClock />{item.duracion} min</span> : null}
    </div>
  </div>
  );
};

/* Pantalla de un ÁREA seleccionada desde el menú. Reutiliza VideoCard (mismo render
   protegido que el resto): al pulsar → onPlay → ScreenReproductor. Sin descarga ni
   URL cruda. El backend ya filtró por membresía; aquí solo se pinta lo recibido. */
function ScreenArea({ area, onPlay, isFav, onToggleFav, cargando = false }) {
  const { t } = useTranslation('canal');
  if (!area) {
    // Regla Ann: NUNCA "Área no disponible". Si carga → aviso de espera; si no, nada.
    return cargando
      ? (<div className="vc-page-head vc-reveal"><h1><b>{t('area.cargando')}</b></h1></div>)
      : null;
  }
  const items = area.items || [];
  return (
    <>
      <div className="vc-page-head vc-reveal">
        <h1><b>{area.titulo_canal || area.label}</b></h1>
        {(area.subtitulo_canal || area.descripcion) ? <p>{area.subtitulo_canal || area.descripcion}</p> : null}
      </div>
      {items.length === 0 ? (
        <div className="vc-page-head"><p>{cargando ? t('area.cargandoVideos') : ''}</p></div>
      ) : (
        <section className="vc-grid">
          {items.map(e => <VideoCard key={e.id} item={e} onPlay={onPlay} isFav={isFav} onToggleFav={onToggleFav} />)}
        </section>
      )}
    </>
  );
}

const SectionHead = ({ title, sub, action, onAction }) => (
  <div className="vc-sec-head vc-reveal">
    <h3>{title} {sub && <span>· {sub}</span>}</h3>
    {action && <span className="vc-see-all" onClick={onAction}>{action} <I.IcArrow /></span>}
  </div>
);

/* ---------------- CONFIG DE NAVEGACIÓN (única fuente: sidebar + drawer móvil) ---------------- */
function getNavConfig(userType, accesoMetodo, areas = [], irAPerfil = false) {
  // labelKey/group = claves i18n del namespace canal (se resuelven con t() al pintar)
  // ── MI PERFIL en el lateral (Ann 17-jul-2026) ────────────────────────────
  // Ann: "¿dónde te pedí que pusieras Mi Perfil en los Club? EN EL LATERAL".
  // POR QUÉ: la clienta CLUB entra DIRECTA al canal (decisión de Ann, 15-jul: el
  // menú no le aporta nada al aterrizar). Consecuencia: su "Mi Perfil" quedaba
  // escondido detrás del botón «Volver» y casi no lo encontraba.
  // Aquí es donde irá su sitio de pagos (facturas, cobros, cancelar).
  // `id: 'perfil'` NO es una pantalla del canal: el perfil vive en el DASHBOARD.
  // Al pulsarlo se avisa hacia arriba (onIrAPerfil) y el dashboard cambia de
  // sección. Por eso solo se pinta si hay a quién avisar.
  const perfilNav = irAPerfil
    ? [{ id: 'perfil', labelKey: 'sidebar.miPerfil', Icon: I.IcUser, group: 'sidebar.grupoCuenta' }]
    : [];
  const metodoNav = [
    { id: 'inicio', labelKey: 'nav.inicio', Icon: I.IcHome, group: 'sidebar.grupoMetodo' },
    { id: 'biblioteca', labelKey: 'sidebar.videoteca', Icon: I.IcLibrary, group: 'sidebar.grupoMetodo' },
    { id: 'progreso', labelKey: 'nav.miProgreso', Icon: I.IcChart, group: 'sidebar.grupoMetodo' },
    { id: 'favoritos', labelKey: 'sidebar.favoritos', Icon: I.IcHeart, group: 'sidebar.grupoMetodo' },
    { id: 'recompensas', labelKey: 'sidebar.recompensas', Icon: I.IcStar, group: 'sidebar.grupoMetodo' },
    { id: 'consejos', labelKey: 'sidebar.consejosPilar', Icon: I.IcVideo, group: 'sidebar.grupoDescubre' },
    // { id: 'novedades', label: 'Novedades', Icon: I.IcStar, group: 'Descubre' },  // desactivado (Ann): Nuevo en Pilar Domínguez fuera del menú
  ];
  const pacienteNav = [
    { id: 'pautas', labelKey: 'sidebar.misPautas', Icon: I.IcCheckList, group: 'sidebar.grupoRecuperacion' },
    { id: 'progreso', labelKey: 'nav.miProgreso', Icon: I.IcChart, group: 'sidebar.grupoRecuperacion' },
    ...(accesoMetodo ? [{ id: 'metodo', labelKey: 'sidebar.elMetodo', Icon: I.IcVideoPanel, group: 'sidebar.grupoRecuperacion' }] : []),
    { id: 'fisio', labelKey: 'sidebar.miFisio', Icon: I.IcChat, group: 'sidebar.grupoContacto' },
  ];
  // Áreas publicadas que el cliente puede ver (ya filtradas por membresía y sin vacías
  // en el backend /canal-areas/cliente). El front SOLO pinta lo recibido → cada área
  // se convierte en un item del menú (id 'area:<key>').
  const areaNav = (areas || [])
    .filter(a => a && a.key)
    .map(a => ({ id: `area:${a.key}`, label: a.label || a.key, Icon: I.IcVideoPanel, group: 'sidebar.grupoAreas' }));
  const base = userType === 'metodo' ? metodoNav : pacienteNav;
  const nav = [...base, ...areaNav, ...perfilNav];
  const groups = [...new Set(nav.map(n => n.group))];
  return { nav, groups };
}

/* ---------------- SIDEBAR ---------------- */
function Sidebar({ userType, accesoMetodo, screen, setScreen, botones = [], areas = [], nombre = '', inicial = 'C' }) {
  const { t } = useTranslation('canal');
  const { nav, groups } = getNavConfig(userType, accesoMetodo, areas, true);
  const sub = userType === 'metodo' ? t('sidebar.canalMetodo') : t('sidebar.miRehabilitacion');
  const role = userType === 'metodo' ? t('sidebar.roleMetodo') : t('sidebar.rolePaciente');

  return (
    <aside className="vc-sidebar">
      <div className="vc-brand">
        <div className="vc-brand-mark"><I.IcBrand /></div>
        <div>
          <div className="vc-brand-name">Pilar Domínguez</div>
          <div className="vc-brand-sub">{sub}</div>
        </div>
      </div>
      <nav className="vc-nav">
        {groups.map(g => (
          <React.Fragment key={g}>
            <div className="vc-nav-label">{t(g)}</div>
            {nav.filter(n => n.group === g).map(n => (
              <div key={n.id} className={`vc-nav-item ${screen === n.id ? 'active' : ''}`} onClick={() => setScreen(n.id)}>
                <n.Icon />{n.labelKey ? t(n.labelKey) : n.label}
              </div>
            ))}
          </React.Fragment>
        ))}
        {/* Botones del canal (configurados en el admin) */}
        {botones.length > 0 && (
          <React.Fragment>
            <div className="vc-nav-label" style={{ marginTop: 22 }}>{t('sidebar.enlaces')}</div>
            {botones.map((b, i) => (
              <a key={b.id || i} className="vc-nav-item" href={b.url || '#'}
                target={b.abrir_en_nueva_pestana ? '_blank' : undefined}
                rel={b.abrir_en_nueva_pestana ? 'noopener noreferrer' : undefined}>
                <I.IcLink />{b.texto}
              </a>
            ))}
          </React.Fragment>
        )}
      </nav>
      <div className="vc-foot">
        <div className="vc-patient-card">
          <Avatar letter={inicial} />
          <div><div className="nm">{nombre || t('sidebar.tuCuenta')}</div><div className="rl">{role}</div></div>
        </div>
      </div>
    </aside>
  );
}

function MobileTop({ userType, inicial = 'C', onMenu }) {
  const { t } = useTranslation('canal');
  const sub = userType === 'metodo' ? t('sidebar.canalMetodo') : t('sidebar.miRehabilitacion');
  return (
    <div className="vc-mobile-top">
      <button type="button" className="vc-burger" onClick={onMenu} aria-label={t('sidebar.abrirMenu')}>
        <I.IcMenu />
      </button>
      <div className="vc-brand" style={{ gap: 11, flex: 1 }}>
        <div className="vc-brand-mark"><I.IcBrand /></div>
        <div><div className="mt-name">Pilar Domínguez</div><div className="mt-sub">{sub}</div></div>
      </div>
      <Avatar letter={inicial} />
    </div>
  );
}

/* ---------------- MENÚ MÓVIL (drawer hamburguesa · TODOS los items) ---------------- */
function MobileMenu({ open, onClose, userType, accesoMetodo, screen, setScreen, botones = [], areas = [], nombre = '', inicial = 'C' }) {
  const { t } = useTranslation('canal');
  const { nav, groups } = getNavConfig(userType, accesoMetodo, areas);
  const sub = userType === 'metodo' ? t('sidebar.canalMetodo') : t('sidebar.miRehabilitacion');
  const role = userType === 'metodo' ? t('sidebar.roleMetodo') : t('sidebar.rolePaciente');
  if (!open) return null;
  const go = (id) => { setScreen(id); onClose(); };
  return (
    <div className="vc-drawer-overlay" onClick={onClose}>
      <aside className="vc-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="vc-drawer-head">
          <div className="vc-brand">
            <div className="vc-brand-mark"><I.IcBrand /></div>
            <div>
              <div className="vc-brand-name">Pilar Domínguez</div>
              <div className="vc-brand-sub">{sub}</div>
            </div>
          </div>
          <button type="button" className="vc-drawer-close" onClick={onClose} aria-label={t('sidebar.cerrarMenu')}>
            <I.IcClose />
          </button>
        </div>
        <nav className="vc-nav vc-drawer-nav">
          {groups.map(g => (
            <React.Fragment key={g}>
              <div className="vc-nav-label">{t(g)}</div>
              {nav.filter(n => n.group === g).map(n => (
                <div key={n.id} className={`vc-nav-item ${screen === n.id ? 'active' : ''}`} onClick={() => go(n.id)}>
                  <n.Icon />{n.labelKey ? t(n.labelKey) : n.label}
                </div>
              ))}
            </React.Fragment>
          ))}
          {botones.length > 0 && (
            <React.Fragment>
              <div className="vc-nav-label" style={{ marginTop: 22 }}>{t('sidebar.enlaces')}</div>
              {botones.map((b, i) => (
                <a key={b.id || i} className="vc-nav-item" href={b.url || '#'}
                  target={b.abrir_en_nueva_pestana ? '_blank' : undefined}
                  rel={b.abrir_en_nueva_pestana ? 'noopener noreferrer' : undefined}
                  onClick={onClose}>
                  <I.IcLink />{b.texto}
                </a>
              ))}
            </React.Fragment>
          )}
        </nav>
        <div className="vc-foot">
          <div className="vc-patient-card">
            <Avatar letter={inicial} />
            <div><div className="nm">{nombre || t('sidebar.tuCuenta')}</div><div className="rl">{role}</div></div>
          </div>
        </div>
      </aside>
    </div>
  );
}

/* ══════════ NAV ESTILO NETFLIX (barra superior + cajón "Mi espacio") ══════════ */
// Navegación superior (categorías del canal). Las marcadas soon=true aún no
// tienen contenido → pantalla "Próximamente" honesta (nada inventado).
const TOP_NAV = [
  { id: 'inicio', labelKey: 'nav.inicio' },
  { id: 'mpd', labelKey: 'nav.clasesMpd' },
  { id: 'cat:relajaciones', labelKey: 'nav.relajaciones' },
  { id: 'cat:consejos', labelKey: 'nav.consejosBreves' },
  { id: 'cat:30min', labelKey: 'nav.clases30', soon: true },
  { id: 'cat:crecimiento', labelKey: 'nav.crecimiento', soon: true },
  { id: 'cat:corporativo', labelKey: 'nav.corporativo', soon: true },
  { id: 'cat:endirecto', labelKey: 'nav.enDirecto', soon: true },
  // { id: 'cat:testimonios', label: 'Testimonios' },  // desactivado (Ann 2026-07-14): fuera del menú — ya salen abajo del dashboard; volverá cuando haya testimonios de otro tipo
  // { id: 'novedades', label: 'Nuevo en Pilar Domínguez' },  // desactivado (Ann): Nuevo en Pilar Domínguez fuera del menú
];
// "Mi espacio" = lo propio del usuario (cajón lateral derecho).
const MI_ESPACIO = [
  { id: 'favoritos', labelKey: 'nav.misFavoritas', Icon: I.IcHeart },
  { id: 'progreso', labelKey: 'nav.miProgreso', Icon: I.IcChart, soon: true }, // Próximamente (Ann)
  // 'Mis compras' quitado de momento (Ann)
  // «Opinar» (Ann 2026-07-25): abre el popup de valoración a demanda, para que la
  // clienta pueda dejar su opinión/reseña CUANDO QUIERA, no solo cuando salta solo.
  { id: 'opinar', label: 'Opinar', Icon: I.IcStar },
];

// SOLO CLUB (spec de Ann 2026-07-20). La socia Club entra directa a Canal Vídeos
// y NUNCA ve la rejilla del canal, así que su «Mi perfil» y su «Salir» viven
// AQUÍ, en Mi Espacio. (Premium los tiene en la rejilla, por eso no se le añaden.)
// Además, en «Mi perfil» está la BAJA, que por ley debe ser accesible.
const MI_ESPACIO_CLUB = [
  { id: 'perfil', label: 'Mi perfil', Icon: I.IcUser },
  { id: 'salir', label: 'Salir', Icon: I.IcUser },
];

function TopNav({ screen, setScreen, onMiEspacio, inicial = 'C', items = TOP_NAV }) {
  const { t } = useTranslation('canal');
  const [navOpen, setNavOpen] = useState(false); // cajón de categorías (móvil/tablet)
  const navRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  const activo = (id) => screen === id || (id === 'inicio' && (screen === 'metodo' || screen === 'inicio'));
  // Al pinchar una categoría se navega y el cajón se cierra INMEDIATAMENTE (Ann).
  const go = (id) => { setScreen(id); setNavOpen(false); };
  const updateArrows = () => {
    const el = navRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };
  useEffect(() => {
    updateArrows();
    // Recalcular tras el pintado y ante cualquier cambio de tamaño del contenido del
    // menú (categorías/áreas que cargan async, fuentes…): si no, la barra desborda
    // pero las flechas se quedan ocultas.
    const raf = requestAnimationFrame(updateArrows);
    const el = navRef.current;
    if (!el) return () => cancelAnimationFrame(raf);
    el.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateArrows) : null;
    if (ro) ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
      if (ro) ro.disconnect();
    };
  }, [items.length]);
  const scrollNav = (dir) => { if (navRef.current) navRef.current.scrollBy({ left: dir * 240, behavior: 'smooth' }); };
  return (
    <header className="vcn-top">
      {/* Hamburguesa: solo en tablet/móvil (CSS). Abre las categorías en cajón. */}
      <button className="vcn-burger" onClick={() => setNavOpen(true)} aria-label={t('nav.menuCategorias')}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
      </button>
      {/* Navegación horizontal (escritorio) con flechas para desplazar a izq./der. */}
      <div className="vcn-navwrap">
        <button className={`vcn-navprev ${canLeft ? 'show' : ''}`} onClick={() => scrollNav(-1)} aria-label={t('nav.categoriasAnteriores')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
        </button>
        <nav className="vcn-nav" ref={navRef}>
          {items.map(n => (
            <button key={n.id} className={`vcn-link ${activo(n.id) ? 'on' : ''}`} onClick={() => setScreen(n.id)}>
              {n.labelKey ? t(n.labelKey) : n.label}
            </button>
          ))}
        </nav>
        <button className={`vcn-navnext ${canRight ? 'show' : ''}`} onClick={() => scrollNav(1)} aria-label={t('nav.verMasCategorias')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
        </button>
      </div>
      <button className="vcn-me" onClick={onMiEspacio}>
        <span className="vcn-me-av">{inicial}</span>
        <span className="vcn-me-tx">{t('nav.miEspacio')}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
      </button>

      {/* Cajón de categorías (móvil/tablet) al pulsar la hamburguesa */}
      {navOpen && (
        <div className="vcn-navdrawer-ov" onClick={() => setNavOpen(false)}>
          <aside className="vcn-navdrawer" onClick={(e) => e.stopPropagation()}>
            <div className="vcn-navdrawer-head"><span>{t('nav.explorar')}</span><button className="vcn-drawer-x" onClick={() => setNavOpen(false)} aria-label={t('nav.cerrar')}>×</button></div>
            <nav className="vcn-drawer-nav">
              {items.map(n => (
                <button key={n.id} className={`vcn-drawer-item ${activo(n.id) ? 'on' : ''}`} onClick={() => go(n.id)}>
                  <span>{n.labelKey ? t(n.labelKey) : n.label}</span>
                </button>
              ))}
            </nav>
          </aside>
        </div>
      )}
    </header>
  );
}

function MiEspacioDrawer({ open, onClose, screen, setScreen, nombre = '', inicial = 'C', membresia = '', onMiPerfil = null, onSalir = null, onOpinar = null, esClub = false }) {
  const { t } = useTranslation('canal');
  if (!open) return null;
  // Al pinchar un ítem: navega Y cierra el cajón, para que se vea el contenido
  // (p. ej. «Mis favoritas»). Antes se quedaba abierto tapando la pantalla (Ann 2026-07-19).
  const go = (id) => { setScreen(id); onClose(); };
  return (
    <div className="vcn-drawer-ov" onClick={onClose}>
      <aside className="vcn-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="vcn-drawer-head">
          <span className="vcn-me-av lg">{inicial}</span>
          <div>
            <div className="nm">{nombre || t('nav.miEspacio')}</div>
            {membresia
              ? <div className="rl"><span className="vc-premium">{membresia}</span></div>
              : <div className="rl">{t('nav.tuZonaPersonal')}</div>}
          </div>
          <button className="vcn-drawer-x" onClick={onClose} aria-label={t('nav.cerrar')}>×</button>
        </div>
        <nav className="vcn-drawer-nav">
          {(esClub ? [...MI_ESPACIO, ...MI_ESPACIO_CLUB] : MI_ESPACIO).map(n => (
            <button key={n.id} className={`vcn-drawer-item ${screen === n.id ? 'on' : ''}${n.soon ? ' soon' : ''}`}
              onClick={() => {
                if (n.soon) return;
                // 'perfil' SÍ es una pantalla del Canal (ScreenMiPerfil): se abre
                // DENTRO, con la marca del Método. Ann 2026-07-20: «que se vaya a
                // la página mi perfil dentro del canal» · «no puede ser que vayan
                // al perfil premium». Por eso NO se salta fuera con onMiPerfil.
                if (n.id === 'salir' && onSalir) { onClose(); onSalir(); return; }
                // «Opinar» no es una pantalla: abre el popup de valoración a demanda.
                if (n.id === 'opinar' && onOpinar) { onClose(); onOpinar(); return; }
                go(n.id);
              }} disabled={!!n.soon}>
              <n.Icon /><span>{n.labelKey ? t(n.labelKey) : n.label}</span>{n.soon && <span className="vcn-soon">{t('nav.proximamente')}</span>}
            </button>
          ))}
        </nav>
      </aside>
    </div>
  );
}

function ProgressRing({ pct, value, label, centerLabel = '', stats, note }) {
  // circunferencia ≈ 408; offset = 408 * (1 - pct)
  const off = Math.round(408 * (1 - pct));
  return (
    <div className="vc-ring-panel vc-reveal">
      <div className="ph">{label}</div>
      <div className="vc-ring-wrap">
        <svg width="160" height="160" viewBox="0 0 160 160">
          <defs><linearGradient id="vcrg" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#4fb88c" /><stop offset="1" stopColor="#0d5538" /></linearGradient></defs>
          <circle className="vc-ring-bg" cx="80" cy="80" r="65" />
          <circle className="vc-ring-fg" cx="80" cy="80" r="65" style={{ '--vc-ring-off': off }} />
        </svg>
        <div className="vc-ring-center"><div className="pct">{value}</div><div className="pl">{centerLabel}</div></div>
      </div>
      {stats && (
        <div className="vc-ring-stats">
          {stats.map((st, i) => <div className="vc-ring-stat" key={i}><div className="v">{st.v}</div><div className="l">{st.l}</div></div>)}
        </div>
      )}
      {note && <div className="vc-ring-note"><I.IcShield />{note}</div>}
    </div>
  );
}

/* ================= SCREEN: INICIO (Método) ================= */

/* ═══════ «NUEVO EN EL CANAL PILAR DOMÍNGUEZ» — novedades personalizadas ═══════
   Cada clienta ve SOLO lo marcado como novedad por la admin y que a ella aún no
   se le ha agotado: cada pieza le dura 7 DÍAS DE CONEXIÓN (el backend lleva el
   reloj por clienta; aquí solo pintamos). Formato propio: carril horizontal con
   halo de marca — distinto a todo, integrado al 100%. */
function NovedadesRail({ onPlay, clasesSemana = [], extras = [], relajaciones = [], charlas = [], areas = [] }) {
  const { t } = useTranslation('canal');
  const [novedades, setNovedades] = useState(null);
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch('/api/canal-novedades-personales/mias', { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok && vivo) { const d = await res.json(); setNovedades(d.novedades || []); }
        else if (vivo) setNovedades([]);
      } catch (e) { if (vivo) setNovedades([]); }
    })();
    return () => { vivo = false; };
  }, []);
  const items = useMemo(() => {
    if (!novedades || !novedades.length) return [];
    const areaItems = (areas || []).flatMap(a => a.items || []);
    const pools = {
      clase: [...(clasesSemana || [])],
      charla: [...(charlas || [])],
      relajacion: [...(relajaciones || []), ...areaItems],
      recomendacion: [...(extras || []), ...areaItems],
      consejo: [...(extras || []), ...areaItems],
      fisioterapia: areaItems,
      area_ficha: areaItems,
    };
    const out = [];
    novedades.forEach(n => {
      const pool = pools[n.tipo] || [];
      const it = pool.find(c => c && (String(c.id) === n.id || String(c.contenidoId) === n.id));
      if (it) out.push({ ...it, _dias: n.dias_restantes });
    });
    return out;
  }, [novedades, clasesSemana, extras, relajaciones, charlas, areas]);
  if (!items.length) return null;
  return (
    <div className="vc-nuevo vc-reveal">
      <div className="vc-nuevo-head">
        <span className="vc-nuevo-dot" aria-hidden="true" />
        <div>
          <h3>{t('nuevo.titulo')}</h3>
        </div>
      </div>
      <div className="vc-nuevo-rail">
        {items.map((it, i) => (
          <div className="vc-nuevo-item" key={it.id || i}>
            {/* Letra gigante de fondo: M · P · D en bucle (marca Método Pilar Domínguez) */}
            <span className="vc-nuevo-letra" aria-hidden="true">{'MPD'[i % 3]}</span>
            <div className="vc-nuevo-card" onClick={() => onPlay(it)}>
              {/* CÁPSULA VERTICAL (Ann 2026-07-25): formato distinto al resto = reclamo */}
              <div className="vc-nuevo-thumb">
                {it.thumbnail
                  ? <img src={it.thumbnail} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  : <div className="vc-nuevo-ph" />}
                <span className="vc-play"><I.IcPlay /></span>
                {it.duracion ? <span className="vc-dur">{it.duracion} min</span> : null}
                <div className="vc-nuevo-scrim">
                  <h5>{catalogoCa(it.nombre || it.titulo || '')}</h5>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ScreenInicioMetodo({
  onPlay, onNavigate = () => {},
  clasesSemana = CLASES_SEMANA, extras = EXTRAS, relajaciones = RELAJACIONES,
  comentarioSemanal = '',
  anuncio = null, consejoVideo = null, consejoPersona = null,
  favoritos = [], clasesData = [], relajData = [], extrasData = [],
  actividad = null, semanaInicio = null, semanaFin = null, charlas = [], nombre = '',
  isFav = () => false, onToggleFav = () => {}, areas = [], cargando = false,
}) {
  const { t } = useTranslation('canal');
  const _n = nombre ? `, ${nombre}` : '';
  // «Ver todo» de Más vídeos despliega EN EL SITIO (Ann 2026-07-25: antes se iba a otra parte)
  const [verMasVideos, setVerMasVideos] = useState(false);
  // ── Saludo motivador según la rutina del Método (2 clases/semana) ──
  // Estados: sin empezar · 1 de 2 · clase a medias por retomar · completada · racha.
  const RUTINA = (actividad && actividad.rutina_semanal) || 2;
  const eventos = (actividad && actividad.eventos) || [];
  const enSemana = (e) => {
    if (!semanaInicio || !semanaFin || !e.fecha) return false;
    const d = String(e.fecha).slice(0, 10);
    return d >= semanaInicio && d <= semanaFin;
  };
  const hechasSet = new Set(eventos.filter(e => e.completada && enSemana(e)).map(e => e.contenido_id));
  const hechasSemana = hechasSet.size;
  const pendiente = eventos.find(e => !e.completada && enSemana(e) && !hechasSet.has(e.contenido_id)) || null;
  const racha = (actividad && actividad.racha_semanas) || 0;
  const saludo = (() => {
    if (pendiente) {
      const m = pendiente.modalidad || pendiente.titulo || t('home.saludo.claseFallback');
      return { h: t('home.saludo.casiH', { nombre: _n }), p: <Trans t={t} i18nKey="home.saludo.casiP" values={{ m }} components={{ b: <b /> }} /> };
    }
    if (hechasSemana >= RUTINA) {
      return { h: t('home.saludo.completadaH', { nombre: _n }), p: <Trans t={t} i18nKey="home.saludo.completadaP" values={{ rutina: RUTINA }} components={{ b: <b /> }} /> };
    }
    if (hechasSemana >= 1) {
      return { h: t('home.saludo.bienH', { nombre: _n }), p: <Trans t={t} i18nKey="home.saludo.bienP" values={{ hechas: hechasSemana, rutina: RUTINA }} components={{ b: <b /> }} /> };
    }
    if (racha >= 8) {
      return { h: t('home.saludo.bravoH', { nombre: _n }), p: <Trans t={t} i18nKey="home.saludo.bravoP" components={{ b: <b /> }} /> };
    }
    if (racha >= 2) {
      return { h: t('home.saludo.constanciaH', { nombre: _n }), p: <Trans t={t} i18nKey="home.saludo.constanciaP" values={{ racha }} components={{ b: <b /> }} /> };
    }
    return { h: t('home.saludo.comencemosH', { nombre: _n }), p: <Trans t={t} i18nKey="home.saludo.comencemosP" values={{ rutina: RUTINA }} components={{ b: <b /> }} /> };
  })();
  // ── «La clase que estás haciendo» (Ann 2026-07-17): la ÚLTIMA dejada a
  // medias, leída de las posiciones que guarda el reproductor (v1 localStorage,
  // clave vc_pos_<clienta>_<id>). Solo si lleva ≥1 min y no está casi acabada.
  const aMedias = useMemo(() => {
    try {
      const pool = [...(clasesSemana || []), ...(clasesData || [])].filter(Boolean);
      let mejor = null;
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (!k || !k.startsWith('vc_pos_')) continue;
        let v = null;
        try { v = JSON.parse(localStorage.getItem(k)); } catch (e) { continue; }
        const seg = typeof v === 'number' ? v : (v && v.t);
        const ts = (v && v.ts) || 0;
        if (!seg || seg < 60) continue;
        const clase = pool.find(c => k.endsWith('_' + String(c.contenidoId || c.id || '')));
        if (!clase) {
          // 🔴 NORMA Ann (2026-07-18): si la clase ya NO está publicada en vivo
          // (rotó la semana), «desaparece todo… se queda a cero, se borra».
          // Guardia: solo se borra con los pools CARGADOS y con contenido — nunca
          // por un fallo/tardanza de carga (se perdería la posición sin motivo).
          const visible = [...pool, ...(relajData || []), ...(extrasData || [])]
            .some(c => c && k.endsWith('_' + String(c.contenidoId || c.id || '')));
          if (!cargando && pool.length > 0 && !visible) {
            try { localStorage.removeItem(k); i -= 1; } catch (e) { /* noop */ }
          }
          continue;
        }
        const durS = (clase.duracion || 0) * 60;
        if (durS && seg > durS * 0.92) continue;
        if (!mejor || ts > mejor.ts) mejor = { clase, seg, ts, clave: k };
      }
      return mejor;
    } catch (e) { return null; }
  }, [clasesSemana, clasesData, relajData, extrasData, cargando]);
  const fmtSeg = (x) => `${Math.floor(x / 60)}:${String(Math.floor(x % 60)).padStart(2, '0')}`;


  // Clase sugerida = misma intensidad/nivel que la última que hizo + modalidad
  // CONTRARIA (Apertura↔Paralelo). Si la última fue Mixta → alternar a la
  // modalidad MENOS practicada. Sin coincidencia exacta → mismo nivel, otra
  // modalidad. Sin historial → la que marque la admin (o la última de la semana).
  // Pool del HERO: excluye clases de técnicos dados de baja (#1176) — no se recomiendan
  // en la portada, aunque siguen apareciendo en el listado de la semana.
  const clasesAptas = clasesSemana.filter(c => c && c.tecnicoActivo !== false);
  const fallbackRec = clasesAptas.find(c => c.recomendada) || clasesAptas[clasesAptas.length - 1] || {};
  const ultima = actividad && actividad.ultima;
  const recomendada = (() => {
    if (!ultima || !ultima.modalidad) return fallbackRec;
    const nivel = (ultima.nivel || '').toLowerCase();
    let objetivo;
    if (ultima.modalidad === 'Apertura') objetivo = 'Paralelo';
    else if (ultima.modalidad === 'Paralelo') objetivo = 'Apertura';
    else {
      const c = actividad.conteo_modalidades || {};
      objetivo = (c.Apertura || 0) <= (c.Paralelo || 0) ? 'Apertura' : 'Paralelo';
    }
    const mismoNivel = (c) => (c.nivel || '').toLowerCase() === nivel;
    return clasesAptas.find(c => mismoNivel(c) && c.modalidad === objetivo)
        || clasesAptas.find(c => mismoNivel(c) && c.modalidad !== ultima.modalidad)
        || clasesAptas.find(c => mismoNivel(c))
        || fallbackRec;
  })();
  // ¿Hay una clase sugerida REAL? Si la semana no tiene clases, no inventamos un
  // hero vacío (antes mostraba "Estándar · Mixta" falso y un botón muerto).
  const haySugerida = !!(recomendada && (recomendada.nombre || recomendada.contenidoId || recomendada.video));
  // Nº REAL de clases de la semana (no un 5 fijo): tras el filtro por membresía del
  // backend, un cliente restringido ve menos. Contamos solo las que tienen contenido.
  const clasesConContenido = (clasesSemana || []).filter(
    (c) => c && (c.id || c.contenidoId || c.nombre || c.modalidad || c.video)
  );
  // Dos bloques (Ann 2026-07-13): las clases FIJAS de la semana (las que define la
  // directora técnica) y las EXTRAS. La marca "Selección de la semana" va en TODAS las
  // fijas (incluida la de un técnico no activo tipo David: es una clase de la semana);
  // el técnico no activo SOLO se excluye del HERO grande, no de la banda (Ann 2026-07-13).
  // [comentario original] La marca "Selección de la semana" solo va en las
  // fijas y NUNCA en una clase de un técnico dado de baja (flag tecnicoActivo, backend #1176).
  const clasesFijas = clasesConContenido.filter((c) => !c.esExtra);
  const clasesExtras = clasesConContenido.filter((c) => c.esExtra);
  const renderWClass = (c) => (
    <div className="vc-wclass vc-reveal" key={c.id} onClick={() => onPlay(c)}>
      {/* La miniatura SIEMPRE ocupa su espacio (cuadro verde corporativo de fondo) para
          que la fila cuadre aunque una clase no tenga foto. Si la foto FALLA al cargar
          → además el mensaje "Error al cargar la clase". Sin foto (normal) → solo el cuadro. */}
      <div className="vc-wclass-thumb">
        {!c.esExtra && (
          <span className="vc-wsel" title={t('card.seleccion')} aria-label={t('card.seleccion')}>{t('card.semana')}</span>
        )}
        {c.thumbnail && (
          <img src={c.thumbnail} alt="" loading="lazy"
            onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement.classList.add('vc-thumb-err'); }} />
        )}
        <span className="vc-wclass-play"><I.IcPlay /></span>
        {c.profesor && <span className="vc-wclass-prof">{String(c.profesor).trim().split(/\s+/)[0]}</span>}
        <span className="vc-wclass-err">{t('card.errorCarga')}</span>
      </div>
      <div className="vc-wtop">
        <h4 className="vc-wtitle">
          <span className={`vc-wlvl ${c.nivel}`}>{catalogoCa(c.nivel === 'suave' ? 'Suave' : 'Estándar')}</span>{' '}
          <span className={`vc-wmod${c.modalidad === 'Mixta' ? ' mixta' : ''}`}>{catalogoCa(c.modalidad)}</span>
        </h4>
        <span className={`vc-wcheck ${c.hecha ? 'done' : 'todo'}`}>{c.hecha && <I.IcCheck2 />}</span>
      </div>
      {c.nombre && c.nombre !== `${c.nivel} · ${c.modalidad}` && (
        <div className="vc-wname" title={catalogoCa(c.nombre)}>{catalogoCa(c.nombre)}</div>
      )}
      <div className="wbot">
        <div className="wdur"><I.IcClock />{c.duracion ? `${c.duracion} min · ` : ''}{c.hecha ? t('card.hecha') : c.recomendada ? t('card.recomendadaHoy') : t('card.pendiente')}</div>
        <button
          className={`vc-wfav ${isFav(c) ? 'on' : ''}`}
          onClick={(e) => { e.stopPropagation(); onToggleFav(c); }}
          title={isFav(c) ? t('card.quitarFav') : t('card.marcarFav')}
          aria-label={isFav(c) ? t('card.quitarFav') : t('card.marcarFav')}
        >
          <I.IcHeart />
        </button>
      </div>
    </div>
  );
  return (
    <>
      {/* El HeroRotativo (portada superior) se renderiza ARRIBA DEL MENÚ, a nivel de frame
          (ver más abajo), por decisión de Ann: el bloque arriba y el menú debajo. */}

      {/* Tablón viejo (AnuncioBanner /wp/activo) RETIRADO (OK Ann 2026-07-08):
          el HeroRotativo de arriba lo sustituye (migración a rotativo tipo Amazon/Apple). */}

      <div className="vc-topbar vc-reveal">
        <div className="vc-greet">
          <h1>{saludo.h}</h1>
          <p>{saludo.p}</p>
        </div>
        <div className="vc-topbar-actions">
          <div className="vc-search"><I.IcSearch /><input placeholder={t('home.buscarPlaceholder')} /></div>
          <div className="vc-icon-btn"><span className="dot" /><I.IcBell /></div>
        </div>
      </div>

      {aMedias && (
        <section className="vc-seguir vc-reveal" data-testid="seguir-card">
          <div className="vc-seguir-thumb" onClick={() => onPlay(aMedias.clase)} role="button" tabIndex={0}>
            {aMedias.clase.thumbnail
              ? <img src={aMedias.clase.thumbnail} alt="" loading="lazy" />
              : <div className="vc-thumb-ph" aria-hidden="true"><svg width="34" height="34" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg></div>}
            <div className="vc-seguir-bar" aria-hidden="true"><span style={{ width: `${aMedias.clase.duracion ? Math.min(100, Math.round(aMedias.seg / (aMedias.clase.duracion * 60) * 100)) : 35}%` }} /></div>
          </div>
          <div className="vc-seguir-info">
            <span className="vc-seguir-tag"><span className="pulse" />{t('home.seguir.tag')}</span>
            <h3>{designacionColoreada(aMedias.clase.nombre || `${(aMedias.clase.nivel || '') === 'suave' ? 'Suave' : 'Estándar'} · ${aMedias.clase.modalidad || ''}`)}</h3>
            <p className="vc-seguir-meta">{t('home.seguir.enMinuto', { min: fmtSeg(aMedias.seg) })}{aMedias.clase.duracion ? ` · ${t('home.seguir.quedan', { min: Math.max(1, Math.round(aMedias.clase.duracion - aMedias.seg / 60)) })}` : ''}</p>
            <div className="vc-seguir-cta">
              <button className="vc-btn-play" onClick={() => onPlay(aMedias.clase)}><I.IcPlay />{t('home.seguir.continuar')}</button>
              <button className="vc-btn-ghost" onClick={() => { try { localStorage.removeItem(aMedias.clave); } catch (e) { /* noop */ } onPlay(aMedias.clase); }}>{t('home.seguir.deNuevo')}</button>
            </div>
          </div>
        </section>
      )}

      <section className="vc-hero">
        <div className="vc-hero-feature vc-reveal">
          {haySugerida && (recomendada.thumbnail || recomendada.video) && (
            <div className="vc-hero-bg">
              {recomendada.thumbnail
                ? <img src={recomendada.thumbnail} alt="" />
                : <div className="vc-thumb-ph" aria-hidden="true"><svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg></div>}
            </div>
          )}
          <span className="vc-tag"><span className="pulse" />{t('home.tagRecomendada')}</span>
          {haySugerida ? (
            <>
              <h2>{designacionColoreada(recomendada.nombre || `${recomendada.nivel === 'suave' ? 'Suave' : 'Estándar'} · ${recomendada.modalidad || 'Mixta'}`)}</h2>
              <div className="vc-hero-meta">
                {recomendada.duracion ? <span><I.IcClock />{recomendada.duracion} min</span> : null}
                {recomendada.nivel ? <span><I.IcShield />{t('home.nivel')} {recomendada.nivel === 'suave' ? 'suave' : 'estándar'}</span> : null}
                {recomendada.modalidad ? <span><I.IcWave />{recomendada.modalidad}</span> : null}
              </div>
              <div className="vc-hero-cta">
                <button className="vc-btn-play" onClick={() => onPlay(recomendada)}><I.IcPlay />{t('home.empezarClase')}</button>
              </div>
            </>
          ) : (
            <>
              <h2>{cargando ? t('home.cargandoRecomendada') : t('home.sinRecomendada')}</h2>
              <div className="vc-hero-meta"><span>{cargando ? t('home.preparandoCanal') : t('home.cuandoPublique')}</span></div>
            </>
          )}
        </div>
        <ProgressRing pct={RUTINA ? Math.min(1, hechasSemana / RUTINA) : 0} value={`${hechasSemana}/${RUTINA}`} label={t('home.tuSemana')} centerLabel={t('progreso.clasesHechas')}
          stats={[{ v: String(racha), l: t('home.rachaStat', { count: racha }) }, { v: String(hechasSemana), l: t('home.hechasStat') }]} />
      </section>

      {/* «Lo nuevo del Canal» — carril personalizado (Ann 2026-07-25), justo
          DEBAJO de la clase recomendada */}
      <NovedadesRail onPlay={onPlay} clasesSemana={clasesSemana} extras={extras}
        relajaciones={relajaciones} charlas={charlas} areas={areas} />

      {/* Sección SIEMPRE visible. Muestra el consejo real de la semana
          (notas_generales); si aún no hay, un vacío honesto (nunca ejemplo). */}
      <div className="vc-note vc-reveal">
        <div className="vc-note-av">
          {consejoPersona?.foto_url
            ? <img src={consejoPersona.foto_url} alt={consejoPersona.nombre || ''} className="vc-note-av-img" />
            : <I.IcPerson />}
        </div>
        <div className="vc-note-body">
          <div className="vc-note-label">
            <span className="liveish" />
            {consejoPersona?.nombre
              ? t('home.consejosDe', { nombre: consejoPersona.nombre })
              : t('home.consejosSemana')}
          </div>
          <div className="vc-note-text">
            {comentarioSemanal || <span style={{ opacity: 0.55, fontStyle: 'italic' }}>{cargando ? t('home.cargandoConsejos') : t('home.sinConsejos')}</span>}
          </div>
        </div>
        {consejoVideo && (
          <button className="vc-note-btn" onClick={() => onPlay({ video: consejoVideo, nombre: t('home.consejosSemana') })}>
            <I.IcPlay />{t('home.verEnVideo')}
          </button>
        )}
      </div>

      {clasesFijas.length > 0 && (
        <>
          <SectionHead title={t('home.tusClases')} sub={t('home.seRenuevan')} action={t('biblioteca.titulo')} onAction={() => onNavigate('biblioteca')} />
          <section className="vc-week-grid">
            {clasesFijas.map(renderWClass)}
          </section>
        </>
      )}

      {clasesExtras.length > 0 && (
        <>
          <SectionHead title={t('home.tusExtras')} sub={t('home.extrasSub')} />
          <section className="vc-week-grid">
            {clasesExtras.map(renderWClass)}
          </section>
        </>
      )}

      {/* Tus favoritas — REALES del cliente, resueltas contra TODA la biblioteca
          (aparecen aunque la clase no esté en la semana publicada). Si no hay
          favoritos, la sección no se muestra. */}
      {(() => {
        const tipoLabel = (tp) => tp === 'relajacion' ? t('home.tipoRelajacion') : tp === 'recomendacion' ? t('home.tipoVidaDiaria') : tp === 'charla' ? t('home.masVideos') : t('home.tipoClase');
        const poolFor = (t) => t === 'clase' ? (clasesData || [])
          : t === 'relajacion' ? (relajData || [])
          : t === 'recomendacion' ? (extrasData || [])
          : t === 'charla' ? (charlas || [])
          : [...(relajData || []), ...(extrasData || []), ...(charlas || []), ...(clasesData || [])];
        const favs = (favoritos || []).map((f) => ({
          f,
          item: poolFor(f.tipo).find(x => String(x.contenidoId ?? x.id) === String(f.contenido_id)) || null,
        }));
        if (favs.length === 0) return null;
        return (
          <>
            <SectionHead title={t('home.tusFavoritas')} sub={t('home.favoritasSub')} action={t('home.cambiarFavoritas')} onAction={() => onNavigate('favoritos')} />
            {/* CUADRÍCULA (orden Ann 2026-07-18): como el formato de clases —
                cuadraditos uno al lado de otro, título DEBAJO, 2 filas de 5.
                SOLO favoritas; el resto de secciones no se toca. */}
            <section className="vc-favs-q">
              {favs.slice(0, 10).map(({ f, item }) => {
                const abrir = item || (f.tipo === 'clase'
                  ? { tipo: 'clase', contenidoId: f.contenido_id, nombre: f.titulo || t('home.tipoClase') }
                  : null);
                const puedeAbrir = !!(abrir && (abrir.tipo === 'clase' || abrir.video || abrir.embedUrl));
                return (
                <div className="vc-favq-card vc-reveal" key={`${f.tipo}:${f.contenido_id}`}
                  onClick={() => { if (puedeAbrir) onPlay(abrir); }}
                  style={{ cursor: puedeAbrir ? 'pointer' : 'default' }}>
                  <div className="vc-favq-thumb">
                    {(item && item.thumbnail) || f.thumbnail_url
                      ? <img src={(item && item.thumbnail) || f.thumbnail_url} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      : <div className="vc-favq-ph" aria-hidden="true"><svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg></div>}
                    <span className="vc-favq-play" aria-hidden="true"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg></span>
                    {(item && (item.profesor || item.tecnico)) ? <span className="vc-favq-tag">{item.profesor || item.tecnico}</span>
                      : <span className="vc-favq-tag">{(item && item.zona) || (f.tipo === 'clase' ? t('home.tipoClase') : t('home.tipoRelajacion'))}</span>}
                  </div>
                  <h4 className="vc-favq-titulo">{f.tipo === 'clase'
                    ? designacionColoreada((item && item.nombre) || f.titulo || t('home.contenido'))
                    : (catalogoCa((item && item.nombre) || f.titulo) || t('home.contenido'))}</h4>
                  <p className="vc-favq-sub">{(item && item.zona) || (item && f.titulo && item.nombre !== f.titulo ? f.titulo : '') || (f.tipo === 'clase' ? t('home.tipoClase') : t('home.tipoRelajacion'))}</p>
                  {item && item.duracion ? <div className="vc-favq-meta"><I.IcClock />{item.duracion} min</div> : null}
                  <button type="button" className="vc-favq-heart" title={t('card.quitarFav')} aria-label={t('card.quitarFav')}
                    onClick={(e) => { e.stopPropagation(); onToggleFav({ tipo: f.tipo, contenidoId: f.contenido_id, nombre: (item && item.nombre) || f.titulo || t('home.contenido') }); }}>
                    <I.IcHeart />
                  </button>
                </div>
                );
              })}
            </section>
          </>
        );
      })()}

      {/* Cada sección se muestra SOLO si tiene contenido: si la membresía del
          cliente no da acceso, el array llega vacío y no pintamos ni el título
          (antes quedaba un enunciado suelto sin nada debajo). */}
      {/* Etiqueta UNIFICADA con el área built-in del menú ("Consejos ergonómicos"):
          antes el dashboard rotulaba "Extras…" y el área del menú "Consejos ergonómicos"
          para el MISMO contenido (recomendaciones). Ahora el título coincide en ambas vistas. */}
      {/* ÁREAS en el ORDEN GLOBAL que fija el admin: se itera la lista `areas` TAL CUAL
          llega — nada de pintar unas built-in fijas arriba y las dinámicas después. Las dos
          áreas del sistema (recomendaciones/relajaciones) conservan su título/contenido
          dedicado, pero salen en la POSICIÓN que Ann haya ordenado. Solo se muestran áreas
          CON contenido (regla: nada vacío ni "no disponible"). */}
      {(areas || []).filter(area => {
        if (!area) return false;
        if (area.key === 'recomendaciones') return extras.length > 0;
        if (area.key === 'relajaciones') return relajaciones.length > 0;
        return (area.items || []).length > 0;
      }).map(area => {
        if (area.key === 'recomendaciones') return (
          <div key="recomendaciones">
            <SectionHead title={area.enunciado_dashboard || area.label || t('home.consejosErgonomicos')} sub={area.sub_dashboard || ''} action={t('home.verTodo')} onAction={() => onNavigate('biblioteca')} />
            <section className="vc-grid">{extras.slice(0, 4).map(e => <VideoCard key={e.id} item={e} onPlay={onPlay} isFav={isFav} onToggleFav={onToggleFav} />)}</section>
          </div>
        );
        if (area.key === 'relajaciones') return (
          <div key="relajaciones">
            <SectionHead title={area.enunciado_dashboard || area.label || t('home.relajacionesGuiadas')} sub={area.sub_dashboard || ''} action={t('home.verTodo')} onAction={() => onNavigate('biblioteca')} />
            <section className="vc-grid">{relajaciones.slice(0, 4).map(e => <VideoCard key={e.id} item={e} onPlay={onPlay} isFav={isFav} onToggleFav={onToggleFav} />)}</section>
          </div>
        );
        return (
        <div key={area.key}>
          <SectionHead title={area.enunciado_dashboard || area.titulo_canal || area.label} sub={area.sub_dashboard || ''} />
          <section className="vc-grid">{(area.items || []).slice(0, 8).map(e => <VideoCard key={e.id} item={e} onPlay={onPlay} isFav={isFav} onToggleFav={onToggleFav} />)}</section>
        </div>
        );
      })}

      {/* Testimonio destacado RETIRADO (Ann 2026-07-13): fuera del canal de vídeos. */}

      {(() => {
        const esTest = (c) => (c.tipoEtiqueta || c.tipo) === 'Testimonios';
        const metodo = charlas.filter(c => !esTest(c));
        const testimonios = charlas.filter(esTest);
        return (
          <>
            {metodo.length > 0 && <>
              <SectionHead title={t('home.masVideos')} sub={t('home.masVideosSub')}
                action={metodo.length > 4 ? (verMasVideos ? t('home.verMenos') : t('home.verTodo')) : undefined}
                onAction={() => setVerMasVideos(v => !v)} />
              {/* Mismas tarjetas que el resto del canal (Ann 2026-07-25): antes era un
                  listado tipo fila (Talks) y quedaba descolgado del resto del dashboard. */}
              <section className="vc-grid">
                {(verMasVideos ? metodo : metodo.slice(0, 4)).map(c => (
                  <VideoCard key={c.id} item={{ ...c, zona: catalogoCa(c.tipoEtiqueta || c.tipo) }}
                    onPlay={onPlay} isFav={isFav} onToggleFav={onToggleFav} />
                ))}
              </section>
            </>}
            {/* Testimonios — MISMO bloque que la landing (rejilla con enlaces directos IG/TikTok, pestaña nueva) */}
            <SectionHead title={t('home.testimonios')} sub={t('home.testimoniosSub')} />
            <div className="vc-lasoci" data-i18n-exento="true">
              {TESTIMONIOS_LASOCI.filter(ts => /\/(video|reel|p)\//.test(ts.url || '')).map((ts, i) => (
                <a key={i} href={ts.url} target="_blank" rel="noopener noreferrer" className={`cell${ts.big ? ' big' : ''}`}>
                  <img src={ts.img} alt={t('home.testimonioReal')} loading="lazy" />
                  <span className="play"><svg width="12" height="12" viewBox="0 0 24 24" fill="#fff"><path d="M8 5v14l11-7z" /></svg></span>
                </a>
              ))}
            </div>
          </>
        );
      })()}
    </>
  );
}

/* Testimonios · mosaico/collage de citas, con la frase contundente visible y el
   texto completo desplegable al tocar. Estilo distinto al resto del canal. */
function TestimoniosMosaico({ testimonios = [] }) {
  const [playingId, setPlayingId] = useState(null); // solo UN testimonio se reproduce a la vez
  // Mosaico de MINIATURAS (sin texto): solo testimonios con imagen o vídeo, pegados y pequeños.
  const conImagen = testimonios.filter(t => !!(t.video || t.embedUrl || t.thumbnail));
  if (!conImagen.length) return null;
  return (
    <div className="vc-testi-mosaic vc-reveal">
      {conImagen.map((t, i) => {
        const key = t.id || i;
        const frase = t.nombre || '';
        const reproducible = !!(t.video || t.embedUrl);
        const isPlaying = playingId === key;
        return (
          <div key={key} className={`vc-testi vc-testi-video tone-${i % 4}`}>
            <div className="vc-testi-thumb">
              {isPlaying && reproducible ? (
                t.video ? (
                  <video src={t.video} className="vc-testi-player" controls autoPlay playsInline
                    controlsList="nodownload" onContextMenu={(e) => e.preventDefault()} />
                ) : (
                  <iframe className="vc-testi-player" title={frase || 'Testimonio'}
                    src={`${t.embedUrl}${t.embedUrl.includes('?') ? '&' : '?'}autoplay=1`}
                    frameBorder="0" allow="autoplay; encrypted-media; fullscreen" allowFullScreen />
                )
              ) : (
                <button type="button" className="vc-testi-thumb-btn"
                  onClick={() => reproducible && setPlayingId(key)}>
                  {t.thumbnail
                    ? <img src={t.thumbnail} alt="" loading="lazy" />
                    : <div className="vc-testi-thumb-grad" />}
                  {reproducible && <span className="vc-testi-play"><I.IcPlay /></span>}
                </button>
              )}
            </div>
          </div>
        );
      })}
      {/* Tarjeta CTA: invita al cliente a enviar su propio testimonio por WhatsApp */}
      <a className="vc-testi vc-testi-cta" href="https://wa.me/34623036731?text=Hola,%20quiero%20compartir%20mi%20experiencia%20con%20el%20M%C3%A9todo%20Pilar%20Dom%C3%ADnguez"
         target="_blank" rel="noopener noreferrer">
        <span className="vc-testi-cta-ic"><I.IcChat /></span>
        <span className="vc-testi-cta-txt">¿Tienes tu propia historia?</span>
        <span className="vc-testi-cta-sub">Envíanos la tuya por WhatsApp</span>
      </a>
    </div>
  );
}

/* Talks (listado tipo fila de «Más vídeos») RETIRADO (Ann 2026-07-25):
   ahora esa sección usa las mismas tarjetas VideoCard que el resto del canal. */

/* ================= SCREEN: PAUTAS (Paciente) ================= */
function ScreenPautas({ onPlay, accesoMetodo, nombre = '' }) {
  const { t } = useTranslation('canal');
  return (
    <>
      <div className="vc-topbar vc-reveal">
        <div className="vc-greet">
          <h1>{t('pautas.hola')}{nombre ? <>, <b>{nombre}</b></> : ''}</h1>
          <p>{t('pautas.intro')}</p>
        </div>
      </div>

      <section className="vc-hero">
        <div className="vc-hero-feature vc-reveal">
          <span className="vc-tag"><span className="pulse" />{t('pautas.tagHoy')}</span>
          <h2>{t('pautas.tituloDemo')}</h2>
          <div className="vc-hero-meta">
            <span><I.IcClock />15 min</span>
            <span><I.IcCheckList />{t('pautas.ejercicios')}</span>
            <span><I.IcChat />{t('pautas.pautadoPor')}</span>
          </div>
          <div className="vc-hero-cta">
            <button className="vc-btn-play" onClick={onPlay}><I.IcPlay />{t('pautas.empezarPauta')}</button>
            <span className="vc-hero-prog"><Trans t={t} i18nKey="pautas.completados" components={{ b: <b /> }} /></span>
          </div>
        </div>
        <ProgressRing pct={0.72} value="72%" label={t('pautas.adherencia')} centerLabel={t('pautas.estaSemana')}
          note={t('pautas.fisioVe')} />
      </section>

      <div className="vc-note vc-reveal">
        <div className="vc-note-av"><I.IcPerson /></div>
        <div className="vc-note-body">
          <div className="vc-note-label">{t('pautas.mensajeDe')}</div>
          <div className="vc-note-text">{t('pautas.mensajeTexto', { nombre: nombre ? `, ${nombre}` : '' })}</div>
        </div>
        <button className="vc-note-btn ghost"><I.IcChat />{t('pautas.responder')}</button>
      </div>

      <SectionHead title={t('pautas.misPautas')} sub={t('pautas.misPautasSub')} action={t('pautas.verTodas')} />
      <section className="vc-grid">
        {PAUTAS.map(p => (
          <div className="vc-vcard vc-reveal" key={p.id} onClick={onPlay}>
            <div className="vc-thumb">
              <div className="grad gclase" />
              {p.hecha && <div className="vc-done"><I.IcCheck2 /></div>}
              <div className="vc-play"><I.IcPlay /></div>
              <span className="vc-reps-pill">{p.series} × {p.reps}</span>
              {p.progreso ? <div className="vc-vbar"><i style={{ width: `${p.progreso * 100}%` }} /></div> : null}
            </div>
            <h5>{catalogoCa(p.nombre)}</h5>
            <div className="vc-vmeta"><span className="vc-area-pill">{p.zona}</span><span className="pillz">{t('pautas.series', { n: p.series })}</span></div>
          </div>
        ))}
      </section>

      {accesoMetodo && (
        <>
          <SectionHead title={t('pautas.elMetodo')} sub={t('pautas.metodoSub')} action={t('home.verTodo')} />
          <div className="vc-metodo-banner vc-reveal">
            <div className="mi"><I.IcShieldCheck /></div>
            <p><Trans t={t} i18nKey="pautas.metodoBanner" components={{ b: <b /> }} /></p>
          </div>
          <section className="vc-grid">
            {[{ id: 'm1', nombre: 'Suave · Paralelo', zona: 'Método', duracion: 18, grad: 'gclase' },
              { id: 'm2', nombre: 'Suave · Apertura', zona: 'Método', duracion: 20, grad: 'gclase' },
              { id: 'm3', nombre: 'Meditación guiada', zona: 'Calma', duracion: 10, grad: 'gmed' },
              { id: 'm4', nombre: 'Cómo sentarte en la oficina', zona: 'Vida diaria', duracion: 4, grad: 'g2' }]
              .map(e => <VideoCard key={e.id} item={e} onPlay={onPlay} />)}
          </section>
        </>
      )}
    </>
  );
}

/* ================= SCREEN: BIBLIOTECA ================= */
function ScreenBiblioteca({
  onPlay,
  clasesData = BIBLIOTECA_CLASES, extrasData = EXTRAS, relajData = RELAJACIONES, charlasData = CHARLAS,
}) {
  const { t } = useTranslation('canal');
  const [filtro, setFiltro] = useState('Todo');
  const [q, setQ] = useState('');
  const chips = ['Todo', 'Suave', 'Estándar', 'Paralelo', 'Apertura', 'Mixta', 'Meditación', 'Vida diaria', '< 15 min', 'Charla', 'Entrevista', 'Corporativo', 'Testimonios'];

  // Normaliza minúsculas + sin acentos: 'Estándar' y 'estandar' (semana) coinciden.
  const norm = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const eq = (a, b) => norm(a) === norm(b);
  const chipMatch = (it) => {
    switch (filtro) {
      case 'Todo': return true;
      case 'Suave': case 'Estándar': return eq(it.nivel, filtro);
      case 'Paralelo': case 'Apertura': case 'Mixta': return eq(it.modalidad, filtro);
      case 'Meditación': return eq(it.zona, 'Meditación');
      case 'Vida diaria': return eq(it.zona, 'Vida diaria');
      case '< 15 min': return it.duracion && it.duracion < 15;
      case 'Charla': case 'Entrevista': case 'Corporativo': case 'Testimonios':
        return eq(it.tipoEtiqueta, filtro);
      default: return false;
    }
  };
  const textMatch = (it) => !q || norm(it.nombre).includes(norm(q)) || norm(catalogoCa(it.nombre)).includes(norm(q));
  const apply = (arr) => arr.filter(it => chipMatch(it) && textMatch(it));

  const clases = apply(clasesData);
  const extras = apply(extrasData);
  const relaj = apply(relajData);
  const charlas = apply(charlasData);
  const total = clases.length + extras.length + relaj.length + charlas.length;

  const Carrusel = ({ items, gradFn, tagFn }) => (
    <div className="vc-carousel">
      {items.map(it => {
        const testi = esTestimonio(it);
        return (
        <div className={`vc-lib-card${testi ? ' vc-testi-card' : ''}`} key={it.id} onClick={() => onPlay(it)}>
          <div className="vc-lib-thumb">
            <div className={`g ${gradFn(it)}`} />
            {it.thumbnail
              ? <img className="vc-lib-img" src={it.thumbnail} alt="" loading="lazy" />
              : it.video
                ? <div className="vc-lib-img vc-thumb-ph" aria-hidden="true"><svg width="30" height="30" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg></div>
                : null}
            <div className="pl"><I.IcPlay /></div>
            {it.duracion ? <span className="vc-dur dur">{it.duracion} min</span> : null}
          </div>
          <h5 className={testi ? 'vc-cap' : ''} data-i18n-exento={testi ? 'true' : undefined}>{testi ? capitalizar(it.nombre) : catalogoCa(it.nombre)}</h5>
          {testi && it.persona ? <div className="vc-testi-persona">{t('biblioteca.testimonioDe', { persona: it.persona })}</div> : null}
          <div className="vc-lib-tags">{tagFn(it)}</div>
        </div>
        );
      })}
    </div>
  );
  return (
    <>
      <div className="vc-page-head vc-reveal"><h1><b>{t('biblioteca.titulo')}</b></h1><p>{t('biblioteca.sub')}</p></div>
      <div className="vc-searchbar"><I.IcSearch /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('biblioteca.buscarPlaceholder')} /></div>
      <div className="vc-filters">
        {chips.map(c => <span key={c} className={`vc-chip ${filtro === c ? 'active' : ''}`} onClick={() => setFiltro(c)}>{c === 'Todo' ? t('biblioteca.chipTodo') : catalogoCa(c)}</span>)}
      </div>

      {total === 0 && (
        <p style={{ color: 'var(--txt-faint)', fontSize: 14, padding: '30px 4px' }}>
          {q ? t('biblioteca.sinResultadosQ', { q }) : t('biblioteca.sinResultadosFiltro')}
        </p>
      )}

      {clases.length > 0 && <>
        <SectionHead title={t('biblioteca.todasClases')} sub={t('biblioteca.todasClasesSub')} />
        <Carrusel items={clases} gradFn={() => 'gclase'}
          tagFn={(it) => <span className={`vc-ltag ${it.nivel === 'Estándar' ? 'g' : ''}`}>{catalogoCa(it.nivel)}</span>} />
      </>}

      {extras.length > 0 && <>
        <SectionHead title={t('biblioteca.extras')} sub={t('biblioteca.extrasSub')} />
        <Carrusel items={extras} gradFn={() => 'gclase'} tagFn={() => <span className="vc-ltag">{t('biblioteca.tagVidaDiaria')}</span>} />
      </>}

      {relaj.length > 0 && <>
        <SectionHead title={t('biblioteca.relajaciones')} sub={t('biblioteca.relajacionesSub')} />
        <Carrusel items={relaj} gradFn={() => 'gmed'} tagFn={(it) => <span className="vc-ltag">{it.zona}</span>} />
      </>}

      {charlas.filter(c => (c.tipoEtiqueta || c.tipo) !== 'Testimonios').length > 0 && <>
        <SectionHead title={t('home.masVideos')} sub={t('biblioteca.masVideosSub')} />
        <Carrusel items={charlas.filter(c => (c.tipoEtiqueta || c.tipo) !== 'Testimonios')} gradFn={() => 'gclase'} tagFn={(it) => <span className="vc-ltag g">{catalogoCa(it.tipoEtiqueta || it.tipo)}</span>} />
      </>}

      {charlas.filter(c => (c.tipoEtiqueta || c.tipo) === 'Testimonios').length > 0 && <>
        <SectionHead title={t('home.testimonios')} sub={t('biblioteca.testimoniosSub')} />
        <Carrusel items={charlas.filter(c => (c.tipoEtiqueta || c.tipo) === 'Testimonios')} gradFn={() => 'gclase'} tagFn={() => <span className="vc-ltag vc-testi-badge">{t('biblioteca.badgeTestimonio')}</span>} />
      </>}
    </>
  );
}

/* ================= SCREEN: PROGRESO ================= */
function Heatmap() {
  // 17 semanas x 7 días; nivel determinista (sin Math.random para estabilidad)
  const cols = [];
  for (let w = 0; w < 17; w++) {
    const cells = [];
    for (let d = 0; d < 7; d++) {
      const seed = (w * 7 + d);
      let lvl = 0;
      const m = seed % 5;
      if (w > 11) lvl = (seed % 4) + 1;
      else if (m > 1) lvl = (seed % 3) + 1;
      cells.push(<div className={`vc-hm-cell ${lvl ? 'l' + Math.min(lvl, 4) : ''}`} key={d} />);
    }
    cols.push(<div className="vc-hm-col" key={w}>{cells}</div>);
  }
  return <div className="vc-heatmap">{cols}</div>;
}

function ScreenProgreso() {
  const { t } = useTranslation('canal');
  const [mounted, setMounted] = useState(false);
  useEffect(() => { const t = setTimeout(() => setMounted(true), 80); return () => clearTimeout(t); }, []);
  const stats = [
    { v: '26 h', l: t('progreso.enElMetodo'), Icon: I.IcClock },
    { v: '8', l: t('progreso.diasRacha'), Icon: I.IcFlame },
    { v: '37', l: t('progreso.clasesHechas'), Icon: I.IcCheckList },
    { v: '11', l: t('progreso.semanasActivas'), Icon: I.IcCalendar },
  ];
  const badges = [
    { t: t('progreso.primeraSemana'), p: t('progreso.completada'), Icon: I.IcStar },
    { t: t('progreso.diezHoras'), p: t('progreso.conseguido'), Icon: I.IcClock },
    { t: t('progreso.racha7'), p: t('progreso.conseguido'), Icon: I.IcFlame },
    { t: t('progreso.clases50'), p: t('progreso.paraDesbloquear'), Icon: I.IcLock, locked: true },
  ];
  return (
    <>
      <div className="vc-page-head vc-reveal"><h1><Trans t={t} i18nKey="progreso.titulo" components={{ b: <b /> }} /></h1><p>{t('progreso.sub')}</p></div>
      <div className="vc-stat-grid">
        {stats.map((s, i) => (
          <div className="vc-bstat vc-reveal" key={i}><div className="ic"><s.Icon /></div><div className="v">{s.v}</div><div className="l">{s.l}</div></div>
        ))}
      </div>
      <div className="vc-panel vc-reveal">
        <div className="vc-panel-h"><h3>{t('progreso.tuActividad')}</h3><div className="leg">{t('progreso.menos')}
          <span className="sq" style={{ background: 'var(--surface-2)' }} />
          <span className="sq" style={{ background: 'rgba(63,156,116,.3)' }} />
          <span className="sq" style={{ background: 'rgba(63,156,116,.55)' }} />
          <span className="sq" style={{ background: 'rgba(63,156,116,.8)' }} />
          <span className="sq" style={{ background: 'var(--accent)' }} /> {t('progreso.mas')}</div></div>
        <Heatmap />
      </div>
      <div className="vc-panel vc-reveal">
        <div className="vc-panel-h"><h3>{t('progreso.clasesPorSemana')}</h3><div className="leg">{t('progreso.ultimas10')}</div></div>
        <div className="vc-chart">
          {HORAS_SEMANALES.map((v, i) => (
            <div className="vc-bar-wrap" key={i}>
              <div className="vc-bar-track"><div className="vc-bar" style={{ height: mounted ? `${v / 5 * 100}%` : '0%' }} /></div>
              <span>S{i + 1}</span>
            </div>
          ))}
        </div>
      </div>
      <SectionHead title={t('progreso.tusLogros')} />
      <div className="vc-badges">
        {badges.map((b, i) => (
          <div className={`vc-badge ${b.locked ? 'locked' : ''}`} key={i}><div className="bic"><b.Icon /></div><h5>{b.t}</h5><p>{b.p}</p></div>
        ))}
      </div>
    </>
  );
}

/* ================= SCREEN: FAVORITOS ================= */
function ScreenFavoritos({ onPlay, favoritos = [], clasesData = [], relajData = [], extrasData = [], charlasData = [], areas = [], isFav = () => false, onToggleFav = () => {} }) {
  const { t } = useTranslation('canal');
  const tipoLabel = (tp) => tp === 'relajacion' ? t('favoritos.tipoRelaj') : tp === 'recomendacion' ? t('home.tipoVidaDiaria') : tp === 'charla' ? t('home.masVideos') : t('home.tipoClase');
  // Tipos CONOCIDOS → su pool. Cualquier OTRO tipo (p. ej. 'area', con el que se
  // guardan los corazones de las secciones de ÁREA como Músicas) → se busca por id
  // en TODAS las bibliotecas (los ids coinciden con su ficha real). ERROR CRÍTICO
  // cazado por Ann 2026-07-17: una canción marcada desde su sección no salía aquí.
  const _TIPOS_CON_CUBO = ['clase', 'relajacion', 'recomendacion', 'charla'];
  const poolFor = (t) => t === 'clase' ? (clasesData || [])
    : t === 'relajacion' ? (relajData || [])
    : t === 'recomendacion' ? (extrasData || [])
    : t === 'charla' ? (charlasData || [])
    : [...(relajData || []), ...(extrasData || []), ...(charlasData || []), ...(clasesData || [])];

  // Resolver cada favorito contra la biblioteca COMPLETA (no solo la semana): así
  // una clase favorita se ve y se reproduce aunque NO esté en la programación actual.
  const resolve = (f) => {
    const real = poolFor(f.tipo).find(x => String(x.contenidoId ?? x.id) === String(f.contenido_id));
    // Forzamos tipo/contenidoId a los de la CLAVE guardada: así "quitar" siempre borra la
    // clave correcta (tipo:contenido_id) sin depender de qué campo trajera el pool.
    if (real) return { ...real, tipo: f.tipo, contenidoId: f.contenido_id,
      // Portada: la del pool y, si no trae, la OFICIAL del backend (#1346:
      // carátula elegida > fotograma > por defecto). NUNCA hueco (Ann 18-jul).
      thumbnail: real.thumbnail || f.thumbnail_url || null };
    // Favorito HUÉRFANO (Ann 2026-07-13, bug didizuin): la clase rotó fuera de la semana
    // y no resuelve contra ningún pool. Se PINTA igualmente con nombre de respaldo y
    // corazón que quita, para que NUNCA quede un favorito "atascado" que no se puede borrar.
    // Y es REPRODUCIBLE (Guardián 2026-07-13): list_favoritos ya resuelve video_url y
    // thumbnail_url aunque la clase NO esté publicada → la tarjeta muestra su miniatura
    // real y el play funciona (para clases, goPlayer refresca además el play-url).
    return {
      id: `${f.tipo}:${f.contenido_id}`,
      tipo: f.tipo,
      contenidoId: f.contenido_id,
      nombre: f.titulo || t('favoritos.contenidoFallback'),
      // Pastilla honesta: si el tipo no es de las 4 familias, nada de «Clase».
      zona: _TIPOS_CON_CUBO.includes(f.tipo) ? tipoLabel(f.tipo) : t('favoritos.tipoRelaj'),
      grad: f.tipo === 'clase' ? 'gclase' : f.tipo === 'relajacion' ? 'gmed' : 'g1',
      video: f.video_url || null,
      thumbnail: f.thumbnail_url || null,
      huerfano: true,
    };
  };
  // FAMILIA REAL de cada favorito (Ann 2026-07-18: «dentro de mis favoritos están
  // mis favoritos, no tiene sentido» — fuera el cajón de sastre): si el tipo no es
  // de las 4 familias (p. ej. 'area'), se clasifica por el pool donde RESUELVE su
  // ficha (una música va con Relajaciones; una clase, con Clases del Método).
  // DEDUPE por contenido: el mismo vídeo marcado por dos caminos sale UNA vez.
  const _en = (arr, cid) => (arr || []).some(x => String(x.contenidoId ?? x.id) === cid);
  const grupos = { clase: [], relajacion: [], recomendacion: [], charla: [], otros: [] };
  const _vistos = new Set();
  (favoritos || []).forEach((f) => {
    const cid = String(f.contenido_id);
    if (_vistos.has(cid)) return;
    const item = resolve(f);
    if (!item) return;
    let fam = _TIPOS_CON_CUBO.includes(f.tipo) ? f.tipo : null;
    if (!fam && !item.huerfano) {
      fam = _en(clasesData, cid) ? 'clase' : _en(relajData, cid) ? 'relajacion'
        : _en(extrasData, cid) ? 'recomendacion' : _en(charlasData, cid) ? 'charla' : 'otros';
    }
    // Ann 2026-07-18: «dentro de la pestaña Mis Favoritos no puede estar la
    // sección Más favoritos» → NO existe cajón aparte: lo inclasificable va con
    // Relajaciones (los corazones de área son músicas/relajaciones casi siempre).
    if (!fam || fam === 'otros') fam = 'relajacion';
    _vistos.add(cid);
    grupos[fam].push(item);
  });
  const clases = grupos.clase, relaj = grupos.relajacion, vida = grupos.recomendacion, charlas = grupos.charla;
  // Titular CONFIGURADO por Ann para la sección de relajaciones (gestor de áreas):
  // el grupo usa su «enunciado de la sección», no un rótulo genérico.
  const _areaRelaj = (areas || []).find(a => a && a.key === 'relajaciones');
  const tituloRelaj = (_areaRelaj && (_areaRelaj.titulo_canal || _areaRelaj.nombre_menu)) || t('favoritos.relajGuiadas');
  const total = clases.length + relaj.length + vida.length + charlas.length;

  if (total === 0) {
    return (
      <div className="vc-page-head vc-reveal">
        <h1><Trans t={t} i18nKey="favoritos.titulo" components={{ b: <b /> }} /></h1>
        <p>{t('favoritos.vacio')}</p>
      </div>
    );
  }

  const Seccion = ({ title, sub, items }) => items.length === 0 ? null : (
    <>
      <SectionHead title={title} sub={sub} />
      <section className="vc-grid">{items.map((item, i) => <VideoCard key={item.id || i} item={item} onPlay={onPlay} isFav={isFav} onToggleFav={onToggleFav} />)}</section>
    </>
  );

  return (
    <>
      <div className="vc-page-head vc-reveal"><h1><Trans t={t} i18nKey="favoritos.titulo" components={{ b: <b /> }} /></h1><p>{t('favoritos.sub')}</p></div>
      <Seccion title={t('favoritos.clasesMetodo')} sub={clases.length === 1 ? t('favoritos.tuFavorita') : t('favoritos.tusNFavoritas', { n: clases.length })} items={clases} />
      <Seccion title={tituloRelaj} items={relaj} />
      <Seccion title={t('home.consejosErgonomicos')} sub={t('favoritos.vidaDiariaSub')} items={vida} />
      <Seccion title={t('home.masVideos')} items={charlas} />
    </>
  );
}

/* ================= SCREEN: NOVEDADES ================= */
function ScreenNovedades({ novedades = null, cargando = false }) {
  const { t } = useTranslation('canal');
  // FUENTE ÚNICA (mensaje/novedad al cliente, editor de Controles). Sin demo:
  // si no hay novedad activa, se muestra un vacío honesto.
  const items = (Array.isArray(novedades) && novedades.length) ? novedades : [];
  return (
    <>
      <div className="vc-page-head vc-reveal"><h1><b>{t('novedades.titulo')}</b></h1><p>{t('novedades.sub')}</p></div>
      {items.length === 0 ? (
        <div className="vc-page-head"><p>{cargando ? t('novedades.cargando') : t('novedades.vacio')}</p></div>
      ) : (
      <div className="vc-feed">
        {items.map(n => {
          const Wrap = n.enlace ? 'a' : 'div';
          const linkProps = n.enlace ? { href: n.enlace, target: '_blank', rel: 'noopener noreferrer' } : {};
          return (
            <Wrap className="vc-feed-item vc-reveal" key={n.id} {...linkProps}>
              <div className="vc-feed-ic">
                {n.imagen_url ? <img src={n.imagen_url} alt="" /> : <I.IcSpark />}
              </div>
              <div className="vc-feed-body"><h5>{n.titulo}</h5>{n.texto && <p>{n.texto}</p>}</div>
              {n.enlace && <span className="vc-feed-time"><I.IcArrow /></span>}
            </Wrap>
          );
        })}
      </div>
      )}
    </>
  );
}

/* ================= SCREEN: RECOMPENSAS ================= */
function ScreenRecompensas({ nombre = '' }) {
  const { t } = useTranslation('canal');
  const codigo = `${(nombre || 'PILAR').toUpperCase().replace(/\s+/g, '')}-PD25`;
  const days = [];
  for (let d = 1; d <= 30; d++) {
    const k = ASISTENCIA_DIAS[d];
    days.push(<div className={`vc-day ${k || ''}`} key={d}>{d}</div>);
  }
  return (
    <>
      <div className="vc-page-head vc-reveal"><h1><Trans t={t} i18nKey="recompensas.titulo" components={{ b: <b /> }} /></h1><p>{t('recompensas.sub')}</p></div>

      <div className="vc-tier vc-reveal">
        <div className="vc-tier-top">
          <div className="vc-medal"><I.IcMedal /></div>
          <div className="vc-tier-name">
            <div className="lv">{t('recompensas.tuNivel')}</div>
            <h2>{t('recompensas.oro')}</h2>
            <p>{t('recompensas.oroTexto')}</p>
          </div>
        </div>
        <div className="vc-tier-prog">
          <div className="vc-tp-row"><span>{t('recompensas.nivelOro')}</span><span><Trans t={t} i18nKey="recompensas.ptsPara" components={{ b: <b /> }} /></span></div>
          <div className="vc-tp-bar"><i style={{ width: '82%' }} /></div>
        </div>
        <div className="vc-tp-perks">
          <span className="vc-perk"><I.IcCheck />{t('recompensas.reservaPrioritaria')}</span>
          <span className="vc-perk"><I.IcCheck />{t('recompensas.contenidoExclusivo')}</span>
          <span className="vc-perk"><I.IcClock />{t('recompensas.embajadoraMes')}</span>
        </div>
      </div>

      <SectionHead title={t('recompensas.asistencia')} sub={t('recompensas.asistenciaSub')} />
      <div className="vc-att-grid vc-reveal">
        <div className="vc-att-stat"><div className="vc-att-ic pres"><I.IcUsers /></div><div><div className="v">6</div><div className="l">{t('recompensas.clasesPresenciales')}</div></div></div>
        <div className="vc-att-stat"><div className="vc-att-ic onl"><I.IcMonitor /></div><div><div className="v">12</div><div className="l">{t('recompensas.clasesOnline')}</div></div></div>
        <div className="vc-att-stat"><div className="vc-att-ic streak"><I.IcFlame /></div><div><div className="v">{t('recompensas.dias8')}</div><div className="l">{t('recompensas.deRacha')}</div></div></div>
      </div>
      <div className="vc-month vc-reveal">
        <div className="vc-month-h"><h4>{t('recompensas.junio')}</h4>
          <div className="vc-month-leg">
            <span><span className="vc-dot pres" />{t('recompensas.presencial')}</span>
            <span><span className="vc-dot onl" />{t('recompensas.online')}</span>
            <span><span className="vc-dot no" />{t('recompensas.sinClase')}</span>
          </div>
        </div>
        <div className="vc-days">{days}</div>
      </div>

      <SectionHead title={t('recompensas.tusMedallas')} />
      <div className="vc-badges">
        <div className="vc-badge"><div className="bic"><I.IcCheck /></div><h5>{t('recompensas.mesPerfecto')}</h5><p>{t('recompensas.sinFaltar')}</p></div>
        <div className="vc-badge"><div className="bic"><I.IcUsers /></div><h5>{t('recompensas.diezPresenciales')}</h5><p>{t('recompensas.conseguido')}</p></div>
        <div className="vc-badge"><div className="bic"><I.IcFlame /></div><h5>{t('recompensas.racha7')}</h5><p>{t('recompensas.conseguido')}</p></div>
        <div className="vc-badge locked"><div className="bic"><I.IcLock /></div><h5>{t('recompensas.cienClases')}</h5><p>{t('recompensas.teFaltan')}</p></div>
      </div>

      <SectionHead title={t('recompensas.invitaGana')} sub={t('recompensas.invitaSub')} />
      <div className="vc-refer vc-reveal">
        <div className="vc-refer-grid">
          <div>
            <h2><Trans t={t} i18nKey="recompensas.comparte" components={{ b: <b /> }} /></h2>
            <p className="sub">{t('recompensas.comparteSub')}</p>
            <div className="vc-gives">
              <div className="vc-give"><div className="who">{t('recompensas.tuGanas')}</div><div className="what">{t('recompensas.mesGratis')}<span>{t('recompensas.porAlta')}</span></div></div>
              <div className="vc-give"><div className="who">{t('recompensas.amigoGana')}</div><div className="what">{t('recompensas.clasePresGratis')}<span>{t('recompensas.sinCompromiso')}</span></div></div>
            </div>
            <div className="vc-code-box">
              <div className="vc-code">{codigo} <I.IcCopy /></div>
              <button className="vc-btn-share"><I.IcShare />{t('recompensas.compartirWhatsApp')}</button>
            </div>
          </div>
          <div className="vc-refer-side">
            <div className="vc-rs-h">{t('recompensas.tusInvitaciones')}</div>
            <div className="vc-rs-stat"><span className="n">3</span><span className="t">{t('recompensas.altas')}</span></div>
            <div className="vc-rs-prog">{t('recompensas.desbloqueas')}</div>
            <div className="vc-rs-bar"><i style={{ width: '60%' }} /></div>
          </div>
        </div>
      </div>
    </>
  );
}


/* ── REPRODUCTOR PROPIO (encargo Ann/Guardián 2026-07-15 · maqueta aprobada) ──
   Sustituye a los controles nativos del <video> (fuera el menú de 3 puntos, la
   velocidad y el PiP): play/pausa · ±10s (MANTENER pulsado = avance acelerado
   estilo Netflix) · barra de progreso + tiempos · volumen/silencio · «Empezar
   de nuevo» · pantalla completa DEL CONTENEDOR → la marca de agua viaja dentro
   (en iPhone cae al fullscreen nativo del sistema, límite de iOS).
   «Seguir donde lo dejaste»: posición por clienta+vídeo (localStorage, v1).
   Marca «letras de cristal» ~3% (Ann: «el 3 es lo suyo») — sustituye a la marca
   del 12%: letras casi invisibles con filo direccional, prueba de trazabilidad. */
const fmtTiempo = (s) => {
  if (!isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60), ss = Math.floor(s % 60);
  return `${m}:${String(ss).padStart(2, '0')}`;
};
function VideoPlayerPropio({ src, poster, marcaAgua = '', videoKey = '', esClase = false, demoTour = false, onProgreso = () => {}, onError = () => {}, onEnded = () => {} }) {
  const { t } = useTranslation('canal');
  const wrapRef = useRef(null);
  const vRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const [muted, setMuted] = useState(false);
  const [vol, setVol] = useState(1);
  const [fs, setFs] = useState(false);
  const [quietos, setQuietos] = useState(false);      // controles ocultos reproduciendo
  const [reanudado, setReanudado] = useState(null);   // aviso «sigues donde lo dejaste»
  const [arrastrando, setArrastrando] = useState(false); // arrastrando la barra → miniatura + tiempo
  const [avisoSalud, setAvisoSalud] = useState(false); // >1h pausada (clases): recomendar calentar desde el principio
  // Regla de Ann (2026-07-16): el contador cuenta desde la ÚLTIMA pausa; cada
  // play lo pone a cero y la SIGUIENTE pausa arranca uno nuevo.
  const demoTimerRef = useRef(null);   // TOUR: el play es una cata de 3s que se pausa sola (Ann)
  const pausaDesdeRef = useRef(0);     // cuándo empezó la pausa actual (0 = reproduciendo)
  const avisoVistoRef = useRef(false); // ya avisó en esta pausa → el siguiente play reproduce
  const prevRef = useRef(null);       // <video> en miniatura para previsualizar el minuto (Ann 2026-07-16)
  const prevSeekRef = useRef(0);      // acelerador: no pedir más de ~4 fotogramas/seg al arrastrar
  const previsualiza = (t2) => {
    const pv = prevRef.current; if (!pv) return;
    const ahora = Date.now();
    if (ahora - prevSeekRef.current > 250) { prevSeekRef.current = ahora; try { pv.currentTime = t2; } catch (e) { /* noop */ } }
  };
  const guardaRef = useRef(0);
  const quietoTimer = useRef(null);
  const holdRef = useRef({ timer: null, veloz: null, fue: false });
  const posKey = `vc_pos_${(marcaAgua || 'anon').replace(/\s+/g, '_')}_${videoKey || 'v'}`;

  const despierta = () => {
    setQuietos(false);
    clearTimeout(quietoTimer.current);
    quietoTimer.current = setTimeout(() => { if (vRef.current && !vRef.current.paused) setQuietos(true); }, 2600);
  };
  useEffect(() => () => { clearTimeout(quietoTimer.current); clearTimeout(demoTimerRef.current); clearInterval(holdRef.current.veloz); clearTimeout(holdRef.current.timer); }, []);
  useEffect(() => {
    const onFs = () => setFs(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  const toggle = () => {
    const v = vRef.current; if (!v) return;
    if (v.paused) {
      // ¿Reanuda una CLASE tras MÁS DE UNA HORA en pausa? Primero el aviso del
      // calentamiento (una sola vez por pausa); el siguiente toque ya reproduce.
      if (esClase && !avisoVistoRef.current && pausaDesdeRef.current > 0
          && Date.now() - pausaDesdeRef.current > 3600 * 1000) {
        avisoVistoRef.current = true;
        setReanudado(fmtTiempo(v.currentTime));
        setAvisoSalud(true);
        despierta();
        return;
      }
      v.play().catch(() => { /* pausa rápida o cata del tour: interrumpir el play es normal */ });
    } else { v.pause(); }
    despierta();
  };
  const salta = (d) => {
    const v = vRef.current; if (!v) return;
    v.currentTime = Math.max(0, Math.min(v.duration || 0, v.currentTime + d));
    setCur(v.currentTime);
    despierta();
  };
  // toque = ±10s · MANTENER = seek continuo acelerando (10→20→40 s/paso)
  const holdStart = (dir) => {
    const h = holdRef.current; h.fue = false;
    h.timer = setTimeout(() => {
      h.fue = true; let paso = 10;
      h.veloz = setInterval(() => { salta(dir * paso); paso = Math.min(40, paso * 2); }, 350);
    }, 420);
  };
  const holdEnd = (dir) => {
    const h = holdRef.current;
    clearTimeout(h.timer); clearInterval(h.veloz);
    if (!h.fue) salta(dir * 10);
    h.fue = false;
  };
  // «Lo dejo aquí» (Ann 2026-07-16): guarda el minuto YA y enseña la pestañita
  // verde como confirmación — la clienta se va tranquila de que quedó guardado.
  const loDejoAqui = () => {
    const v = vRef.current; if (!v) return;
    v.pause();
    try { localStorage.setItem(posKey, JSON.stringify({ t: v.currentTime, ts: Date.now() })) } catch (e) { /* noop */ }
    setReanudado(fmtTiempo(v.currentTime));
    despierta();
  };
  const empezarDeNuevo = () => {
    const v = vRef.current; if (!v) return;
    v.currentTime = 0; try { localStorage.removeItem(posKey); } catch (e) { /* noop */ }
    v.play().catch(() => { /* interrumpido: normal */ }); setReanudado(null); despierta();
  };
  const pantallaCompleta = () => {
    const w = wrapRef.current, v = vRef.current;
    if (document.fullscreenElement) { document.exitFullscreen?.(); return; }
    if (w && w.requestFullscreen) w.requestFullscreen();
    else if (v && v.webkitEnterFullscreen) v.webkitEnterFullscreen(); // iPhone: nativo, límite iOS
    despierta();
  };
  const cambiaVol = (nv) => {
    const v = vRef.current; if (!v) return;
    v.volume = nv; v.muted = nv === 0;
    setVol(nv); setMuted(nv === 0);
  };
  const pct = dur > 0 ? (cur / dur) * 100 : 0;

  // iconos SVG corporativos (trazo 2, línea redonda) — nunca emojis
  const Ic = {
    play: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5.5v13l11-6.5z" /></svg>,
    pausa: <svg viewBox="0 0 24 24" fill="currentColor"><rect x="6.5" y="5.5" width="3.6" height="13" rx="1" /><rect x="14" y="5.5" width="3.6" height="13" rx="1" /></svg>,
    m10: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5.5A7 7 0 1 1 5 12" /><path d="M5.5 5.5v4h4" /><text x="9.2" y="16.4" fontSize="7.2" fill="currentColor" stroke="none" fontWeight="700">10</text></svg>,
    p10: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M13 5.5A7 7 0 1 0 19 12" /><path d="M18.5 5.5v4h-4" /><text x="9.2" y="16.4" fontSize="7.2" fill="currentColor" stroke="none" fontWeight="700">10</text></svg>,
    inicio: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12a7 7 0 1 1-7-7" /><path d="M12 2.8 9 5l3 2.2" /><rect x="5" y="10.4" width="3" height="4.4" rx="0.6" fill="currentColor" stroke="none" /></svg>,
    volOn: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M4 9.5v5h3.5L12 18V6L7.5 9.5z" fill="currentColor" stroke="none" /><path d="M15.5 9a4.2 4.2 0 0 1 0 6M18 6.8a7.6 7.6 0 0 1 0 10.4" /></svg>,
    volOff: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M4 9.5v5h3.5L12 18V6L7.5 9.5z" fill="currentColor" stroke="none" /><path d="M15.5 9.5l5 5M20.5 9.5l-5 5" /></svg>,
    fsIn: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" /></svg>,
    fsOut: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" /></svg>,
  };
  const Btn = ({ ic, label, onClick, onPointerDown, onPointerUp, onPointerLeave }) => (
    <button type="button" className="vcp-btn" aria-label={label} title={label}
      onClick={onClick} onPointerDown={onPointerDown} onPointerUp={onPointerUp} onPointerLeave={onPointerLeave}>
      {ic}
    </button>
  );

  return (
    <div ref={wrapRef} className={`vcp ${quietos ? 'quietos' : ''} ${fs ? 'fs' : ''}`}
      onMouseMove={despierta} onTouchStart={despierta}>
      <video
        ref={vRef}
        className="vc-player-video"
        src={src}
        poster={poster || undefined}
        playsInline
        preload="metadata"
        disablePictureInPicture
        onContextMenu={(e) => e.preventDefault()}
        onClick={toggle}
        onPlay={() => {
          setPlaying(true); setReanudado(null); setAvisoSalud(false);
          pausaDesdeRef.current = 0; avisoVistoRef.current = false; despierta();
          // Durante el TOUR el play es solo una CATA: 3 segundos y se pausa solo.
          clearTimeout(demoTimerRef.current);
          if (demoTour) demoTimerRef.current = setTimeout(() => { const v = vRef.current; if (v && !v.paused) v.pause(); }, 2000); // 2s — Ann 16-jul: "3s es un rollo"
        }}
        onPause={() => { setPlaying(false); pausaDesdeRef.current = Date.now(); }} // cada pausa arranca SU contador
        onLoadedMetadata={(e) => {
          const v = e.currentTarget; setDur(v.duration || 0);
          try {
            const crudo = localStorage.getItem(posKey);
            let pos = NaN, ts = 0;
            try { const j = JSON.parse(crudo); pos = Number(j.t); ts = Number(j.ts) || 0; }
            catch (e2) { pos = parseFloat(crudo); } // formato antiguo (solo segundos)
            if (pos > 10 && v.duration && pos < v.duration - 15) {
              // «Lo dejaste aquí» (Ann 2026-07-16): cursor en el minuto exacto y
              // pestañita verde con Seguir / Empezar de nuevo. NO se esconde sola.
              v.currentTime = pos; setCur(pos);
              setReanudado(fmtTiempo(pos));
              // AVISO TERAPÉUTICO (Ann 2026-07-16): en CLASES, si pasó MÁS DE UNA HORA
              // desde la pausa, recomendar empezar desde el principio (calentamiento).
              setAvisoSalud(esClase && ts > 0 && (Date.now() - ts) > 3600 * 1000);
            }
          } catch (err) { /* noop */ }
        }}
        onTimeUpdate={(e) => {
          const v = e.currentTarget; setCur(v.currentTime);
          const ahora = Date.now();
          if (ahora - guardaRef.current > 3000) {
            guardaRef.current = ahora;
            try { localStorage.setItem(posKey, JSON.stringify({ t: v.currentTime, ts: Date.now() })) } catch (err) { /* noop */ }
          }
          onProgreso(e);
        }}
        onEnded={() => { setPlaying(false); setQuietos(false); try { localStorage.removeItem(posKey); } catch (err) { /* noop */ } onEnded(); }}
        onError={onError}
      />

      {/* Marca «letras de cristal» ~3%: hueca, filo en un lado, SIEMPRE dentro del
          contenedor → visible también en pantalla completa. Trazabilidad, no adorno. */}
      <div className="vcp-cristal" aria-hidden="true">
        <span>Canal Pilar Domínguez</span>
        {marcaAgua && <span className="nom">{marcaAgua}</span>}
      </div>

      {/* pestañita VERDE «Lo dejaste aquí» (Ann 2026-07-16): minuto exacto +
          Seguir / Empezar de nuevo. Se queda hasta que la clienta decide. */}
      {reanudado && (avisoSalud ? (
        /* Variante TERAPÉUTICA (>1h pausada una clase): recomendar calentar desde el
           principio. El botón destacado es «Empezar desde el principio». */
        <div className="vcp-reanudado vcp-rea-salud" role="dialog" aria-label={t('player.saludTitulo')}>
          <strong className="vcp-rea-salud-t">{t('player.saludTitulo')}</strong>
          <span className="vcp-rea-salud-x">{t('player.saludTexto')}</span>
          <span className="vcp-rea-salud-btns">
            <button type="button" className="vcp-rea-seguir" onClick={empezarDeNuevo}>{t('player.saludEmpezar')}</button>
            <button type="button" className="vcp-rea-inicio" onClick={toggle}>{t('player.saludSeguir', { min: reanudado })}</button>
          </span>
        </div>
      ) : (
        <div className="vcp-reanudado" role="dialog" aria-label={t('player.loDejasteAqui', { min: reanudado })}>
          <span className="vcp-rea-txt">{t('player.loDejasteAqui', { min: reanudado })}</span>
          <button type="button" className="vcp-rea-seguir" onClick={toggle}>
            <svg viewBox="0 0 24 24" fill="currentColor" width="13" height="13" aria-hidden="true"><path d="M8 5.5v13l11-6.5z" /></svg>
            {t('player.seguir')}
          </button>
          <button type="button" className="vcp-rea-inicio" onClick={empezarDeNuevo}>{t('player.empezarDeNuevo')}</button>
        </div>
      ))}

      {/* PLAY grande centrado en pausa (tocar el centro = reanudar) */}
      {!playing && (
        <button type="button" className="vcp-bigplay" aria-label={t('player.reproducir')} title={t('player.reproducir')} onClick={toggle}>
          {Ic.play}
        </button>
      )}

      {/* barra de controles PROPIA */}
      <div className="vcp-bar">
        <input className="vcp-prog" type="range" min="0" max={dur || 0} step="0.1" value={cur}
          aria-label={t('player.progreso')}
          style={{ '--pct': `${pct}%` }}
          onPointerDown={() => setArrastrando(true)}
          onPointerUp={() => { setArrastrando(false); const pv = prevRef.current; if (pv) { try { pv.currentTime = cur; } catch (e) { /* noop */ } } }}
          onChange={(e) => { const v = vRef.current; const t2 = Number(e.target.value); if (v) { v.currentTime = t2; setCur(v.currentTime); } previsualiza(t2); despierta(); }} />
        {/* MINIATURA del minuto al arrastrar (Ann 2026-07-16): un segundo <video> mudo
            del MISMO enlace (misma sesión y blindaje) que solo busca el fotograma. */}
        {arrastrando && (
          <div className="vcp-seekprev" aria-hidden="true">
            <video ref={prevRef} src={src} muted playsInline preload="metadata" tabIndex={-1} />
            <div className="vcp-seekprev-t">{fmtTiempo(cur)}</div>
          </div>
        )}
        <div className="vcp-row">
          <div className="vcp-l">
            <Btn ic={playing ? Ic.pausa : Ic.play} label={playing ? t('player.pausa') : t('player.reproducir')} onClick={toggle} />
            <Btn ic={Ic.m10} label={t('player.atras10')}
              onPointerDown={() => holdStart(-1)} onPointerUp={() => holdEnd(-1)} onPointerLeave={() => { clearTimeout(holdRef.current.timer); clearInterval(holdRef.current.veloz); }} />
            <Btn ic={Ic.p10} label={t('player.adelante10')}
              onPointerDown={() => holdStart(1)} onPointerUp={() => holdEnd(1)} onPointerLeave={() => { clearTimeout(holdRef.current.timer); clearInterval(holdRef.current.veloz); }} />
            <Btn ic={Ic.inicio} label={t('player.empezarDeNuevo')} onClick={empezarDeNuevo} />
            <span className="vcp-time">{fmtTiempo(cur)} <em>/ {fmtTiempo(dur)}</em></span>
          </div>
          <div className="vcp-r">
            <button type="button" className="vcp-btn vcp-dejar" onClick={loDejoAqui}
              title={t('player.loDejoAquiTip')} aria-label={t('player.loDejoAquiTip')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16" aria-hidden="true"><path d="M6 3.5h12v17l-6-4.5-6 4.5z" /></svg>
              <span>{t('player.loDejoAqui')}</span>
            </button>
            <Btn ic={muted || vol === 0 ? Ic.volOff : Ic.volOn} label={muted ? t('player.sonido') : t('player.silenciar')}
              onClick={() => { const v = vRef.current; if (v) { v.muted = !v.muted; setMuted(v.muted); } }} />
            <input className="vcp-vol" type="range" min="0" max="1" step="0.05" value={muted ? 0 : vol}
              aria-label={t('player.volumen')} onChange={(e) => cambiaVol(Number(e.target.value))} />
            <Btn ic={fs ? Ic.fsOut : Ic.fsIn} label={fs ? t('player.salirPantalla') : t('player.pantallaCompleta')} onClick={pantallaCompleta} />
          </div>
        </div>
      </div>
    </div>
  );
}


/* ── TOUR del reproductor (Ann 2026-07-16: «que se mueva: aquí esto, aquí lo
   otro — una cosa chula») ──────────────────────────────────────────────────
   Un FOCO redondeado que viaja suavemente de control en control con su
   tarjetita explicativa. Sale UNA vez por persona (localStorage) la primera
   vez que abre un vídeo; «Saltar» disponible siempre. Bilingüe es/ca. */
function TourReproductor({ objetivo = null, onFin = () => {} }) {
  const { t } = useTranslation('canal');
  const [paso, setPaso] = useState(0);
  const [caja, setCaja] = useState(null);
  // Si la persona PRUEBA el control iluminado, el velo se aclara casi del todo
  // (la atención va a lo que ocurre); «Siguiente» devuelve la oscuridad (Ann).
  const [probando, setProbando] = useState(false);
  useEffect(() => {
    const alTocar = (e) => {
      if (!caja) return;
      const x = e.clientX, y = e.clientY;
      if (x >= caja.x && x <= caja.x + caja.w && y >= caja.y && y <= caja.y + caja.h) setProbando(true);
    };
    window.addEventListener('pointerdown', alTocar, true);
    return () => window.removeEventListener('pointerdown', alTocar, true);
  }, [caja]);
  const PASOS = [
    { clave: 'play' }, { clave: 'barra' }, { clave: 'dejar' },
    { clave: 'hecha' }, { clave: 'fav' }, { clave: 'salto' },
  ];
  useEffect(() => {
    let intentos = 0;
    let timer = null;
    let latido = null;
    const coloca = () => {
      // Reintenta hasta que los controles estén en el DOM (el primer render va antes).
      const objetivos = ['.vcp-bigplay', '.vcp-prog', '.vcp-dejar', '.vcp-check', '.vc-btn-ghost.fav', '.vcp-alacabar'];
      // fallback del paso 1: si el play grande no está (vídeo ya en marcha), ilumina el vídeo
      const sel = objetivos[paso];
      const el = sel && (objetivo || document).querySelector(sel);
      let el2 = el;
      if (!el2 && paso === 0) el2 = (objetivo || document).querySelector('.vcp video');
      if (!el2) {
        if (sel && intentos < 20) { intentos += 1; timer = setTimeout(coloca, 250); }
        else if (!sel) setCaja(null);
        return;
      }
      el2.scrollIntoView({ block: 'center', behavior: 'smooth' });
      const mide = () => {
        const r2 = el2.getBoundingClientRect();
        setCaja(prev => {
          const nueva = { x: r2.left - 10, y: r2.top - 10, w: r2.width + 20, h: r2.height + 20 };
          return prev && Math.abs(prev.x - nueva.x) < 1 && Math.abs(prev.y - nueva.y) < 1
            && Math.abs(prev.w - nueva.w) < 1 && Math.abs(prev.h - nueva.h) < 1 ? prev : nueva;
        });
      };
      setTimeout(mide, 260); // tras el scroll suave
      // latido: el foco SIGUE al control aunque la página se mueva (Ann: «si te
      // mueves, el foco se desplazaba» — ahora se recoloca solo).
      latido = setInterval(mide, 250);
    };
    coloca();
    window.addEventListener('resize', coloca);
    return () => { clearTimeout(timer); clearInterval(latido); window.removeEventListener('resize', coloca); };
    // eslint-disable-next-line
  }, [paso]);
  if (!PASOS.length || !caja) return null;
  const ultimo = paso === PASOS.length - 1;
  const abajo = caja.y < window.innerHeight / 2;
  const W = window.innerWidth, H = window.innerHeight;
  return (
    <div className={`vct ${probando ? 'probando' : ''}`} role="dialog" aria-label={t('tour.aria')}>
      {/* VELO oscuro en 4 paneles alrededor del foco: concentra la atención y
          deja el control ILUMINADO y LIBRE para probarlo (Ann 2026-07-16).
          Los paneles viajan con transición suave al cambiar de paso. */}
      <div className="vct-velo vct-velo-t" style={{ left: 0, top: 0, width: W, height: Math.max(0, caja.y) }} />
      <div className="vct-velo vct-velo-b" style={{ left: 0, top: caja.y + caja.h, width: W, height: Math.max(0, H - caja.y - caja.h) }} />
      <div className="vct-velo vct-velo-l" style={{ left: 0, top: caja.y, width: Math.max(0, caja.x), height: caja.h }} />
      <div className="vct-velo vct-velo-r" style={{ left: caja.x + caja.w, top: caja.y, width: Math.max(0, W - caja.x - caja.w), height: caja.h }} />
      {/* anillo del foco (solo decorativo, no bloquea el control) */}
      <div className="vct-foco" style={{ left: caja.x, top: caja.y, width: caja.w, height: caja.h, borderRadius: PASOS[paso].clave === 'play' ? '50%' : 16 }} />
      <div className="vct-card" style={{
        left: Math.max(12, Math.min(caja.x, window.innerWidth - 332)),
        // tope arriba Y abajo: la tarjeta NUNCA se corta con el borde de la pantalla
        top: Math.max(12, Math.min(abajo ? caja.y + caja.h + 14 : caja.y - 190, window.innerHeight - 205)),
      }}>
        <span className="vct-n">{paso + 1} / {PASOS.length}</span>
        <strong>{t(`tour.${PASOS[paso].clave}T`)}</strong>
        <p>{t(`tour.${PASOS[paso].clave}X`)}</p>
        <div className="vct-btns">
          <button type="button" className="vct-saltar" onClick={onFin}>{t('tour.saltar')}</button>
          <button type="button" className="vct-sig" onClick={() => { setProbando(false); if (ultimo) onFin(); else setPaso(paso + 1); }}>
            {ultimo ? t('tour.listo') : t('tour.siguiente')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ================= SCREEN: REPRODUCTOR ================= */
const fmtVistas = (n) => n >= 1000 ? `${(n / 1000).toFixed(1).replace('.0', '')}k` : `${n}`;
function ScreenReproductor({ onBack, item, isFav = false, onToggleFav = () => {}, onClaseHecha = () => {}, onClaseDeshecha = () => {}, sugerencias = [], onPlay = () => {}, vistas = {}, marcaAgua = '' }) {
  const { t: tc } = useTranslation('common'); // i18n de la marca de agua (cero fugas)
  const { t } = useTranslation('canal');
  const hasVideo = !!(item && item.video);
  // URL S3 caducada u otro fallo del <video controlsList="nodownload noremoteplayback" disablePictureInPicture onContextMenu={(e) => e.preventDefault()}>: aviso honesto + reintento (onPlay/goPlayer
  // vuelve a pedir play-url FRESCO para las clases). Antes: pantalla negra sin explicación.
  const [videoError, setVideoError] = useState(false);
  useEffect(() => { setVideoError(false); }, [item && (item.contenidoId || item.id)]);
  // Al abrir un video, subir SIEMPRE arriba (Ann 2026-07-28: entraba a mitad de pagina y el Volver no se veia)
  useEffect(() => { try { window.scrollTo({ top: 0, behavior: 'instant' }); } catch (e) { window.scrollTo(0, 0); } }, [item && (item.contenidoId || item.id)]);
  // Tour del reproductor (Ann 2026-07-16): AUTO solo para gente NUEVA del canal y
  // UNA vez POR PERSONA (registro en servidor, mismo patrón que el confeti #1290);
  // los veteranos no lo sufren de golpe — tienen el botón «Tutorial» para lanzarlo.
  const [tourVivo, setTourVivo] = useState(false);
  useEffect(() => {
    if (!item || !(item.video || item.embedUrl) || esTestimonio(item)) return;
    try { if (localStorage.getItem('vc_tour_reproductor')) return; } catch (e) { /* noop */ }
    let vivo = true;
    getTutorialReproductor().then((lanzar) => { if (vivo && lanzar) setTourVivo(true); });
    return () => { vivo = false; };
    // eslint-disable-next-line
  }, [item && (item.contenidoId || item.id)]);
  const finTour = () => {
    setTourVivo(false);
    try { localStorage.setItem('vc_tour_reproductor', '1'); } catch (e) { /* noop */ }
    track('tutorial_reproductor_visto', 'reproductor'); // por PERSONA: no vuelve a auto-lanzarse nunca
  };
  // Check VERDE de «clase hecha» (logro, no navegación) — manual o automático (2/3 del vídeo).
  const [hechaUI, setHechaUI] = useState(false);
  const [animo, setAnimo] = useState('');   // frase viva según el progreso al marcarla
  useEffect(() => {
    setHechaUI(false); setAnimo('');
    // Salto AUTOMÁTICO sin marcar (Ann 2026-07-23): al abrir una clase que tiene una
    // relajación en "Para complementar" reproducible, se marca SOLA como «al acabar»,
    // así al terminar la clase salta la relajación sin que la clienta toque nada.
    // Sigue pudiendo cambiarla o quitarla con el botón «Al acabar». No aplica si el
    // vídeo abierto ES un complemento (evita encadenar relajación→relajación).
    const comps = (item && item.complementos) || [];
    const idx = comps.findIndex(c => c && (c.video_url || c.embed_url));
    const primera = idx >= 0 ? comps[idx] : null;
    if (item && item.tipo !== 'complemento' && primera) {
      setAlAcabar({
        contenidoId: primera.id || `comp-${idx}`, id: primera.id || `comp-${idx}`,
        nombre: primera.titulo, tipo: 'complemento',
        video: primera.video_url || null, embedUrl: primera.embed_url || null,
        thumbnail: primera.thumbnail || '', zona: primera.zona || '', descripcion: primera.subtitulo || '',
      });
    } else {
      setAlAcabar(null);
    }
  }, [item && (item.contenidoId || item.id)]);
  const fraseAnimo = (frac) => frac >= 0.66 ? t('player.animoCompleta') : frac >= 0.3 ? t('player.animoMedia') : t('player.animoInicio');
  // Sugerencia marcada «Al acabar»: al terminar el vídeo se reproduce ESA (solo una).
  const [alAcabar, setAlAcabar] = useState(null);
  const alTerminarVideo = () => { if (alAcabar) onPlay(alAcabar); };
  const [compartido, setCompartido] = useState(false);
  const compartirClase = async () => {
    const url = 'https://canal.metodopilardominguez.com';
    const titulo = `${item?.nombre || t('player.claseFallback')} · Canal Método Pilar Domínguez`;
    try {
      if (navigator.share) { await navigator.share({ title: titulo, url }); return; }
      await navigator.clipboard.writeText(`${titulo} — ${url}`);
      setCompartido(true);
      setTimeout(() => setCompartido(false), 2000);
    } catch (e) { /* usuario canceló el share nativo: no es error */ }
  };
  const hasEmbed = !!(item && item.embedUrl);
  const testi = esTestimonio(item);               // ¿es un testimonio? (cambia título/acciones/contexto)
  const ch = item && item.comoHacerla;            // "Cómo hacerla" real (o null)
  const comp = (item && item.complementos) || []; // "Para complementar" real
  const consejo = (item && item.consejoPilar) || null; // vídeo-consejo de la directora (propio de la clase)
  const hayConsejo = !!(consejo && (consejo.texto || consejo.video_url || consejo.embed_url));
  // ── Campos de la ficha: reales si hay item; ejemplo solo en preview sin item ──
  const fNivel = (n) => catalogoCa(n === 'suave' ? 'Suave' : n === 'estandar' ? 'Estándar' : n);
  const cats = (item && item.categorias && item.categorias.length) ? item.categorias
    : (item ? [] : [t('player.catMovilidad'), 'Método P. Domínguez']);
  const duracionLabel = item ? (item.duracion ? `${item.duracion} min` : null) : t('player.duracionDemo');
  const nivelLabel = item ? (item.nivel ? fNivel(item.nivel) : null) : t('player.nivelTodos');
  const materialLabel = item ? (item.material || null) : t('player.sinMaterial');
  const descripcion = item ? (item.descripcion || '')
    : t('player.descDemo');
  const cp = item ? item.consejoPilar
    : { texto: t('player.cpDemo'), embedUrl: null, video: null };
  const cpVideo = cp && (cp.embedUrl || cp.video);
  // Una clase se considera HECHA al consumir > 2/3 del vídeo (se avisa una vez).
  // Si la deja a medias, al salir se registra como "no completada" para poder
  // animarla a retomarla. Solo aplica a clases.
  const doneRef = useRef(false);
  const progRef = useRef(0);
  useEffect(() => {
    doneRef.current = false;
    progRef.current = 0;
    const it = item;
    return () => {
      if (!doneRef.current && it && it.tipo === 'clase' && progRef.current > 0.05) {
        onClaseHecha(it, { completada: false, progreso: progRef.current });
      }
    };
  }, [item && item.contenidoId]);
  const onTimeUpdate = (e) => {
    const v = e.currentTarget;
    if (!item || !v.duration) return;
    const frac = v.currentTime / v.duration;
    if (frac > progRef.current) progRef.current = frac; // progreso: TODOS los tipos (frases vivas)
    if (item.tipo !== 'clase') return; // el registro automático de «realizada» sigue siendo solo de clases
    if (!doneRef.current && frac > 2 / 3) {
      doneRef.current = true;
      setHechaUI(true);
      setAnimo(t('player.animoCompleta'));
      onClaseHecha(item, { completada: true, progreso: frac });
    }
  };
  return (
    <div className="vc-player-wrap">
      {tourVivo && hasVideo && !testi && <TourReproductor onFin={finTour} />}
      <div className="vc-pl-top">
        <div className="vc-back" onClick={onBack}>
          {/* Pildora BIEN VISIBLE, igual que en las rutinas guiadas (Ann 2026-07-28) */}
          <button type="button" className="vc-back-pill"><I.IcBack /> {t('player.volver')}</button>
          <div className="vc-crumb"><b>{t('player.crumbMetodo')}</b> &nbsp;·&nbsp; {catalogoCa(item?.nombre) || t('player.crumbFallback')}</div>
        </div>
        <div className="vc-pill-derecha">
          {/* botón VISIBLE para relanzar el tour cuando quiera (Ann 2026-07-16) */}
          {(item && (item.video || item.embedUrl) && !testi) && (
            <button type="button" className="vc-pill-tut" onClick={() => setTourVivo(true)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M9.6 9a2.4 2.4 0 1 1 3.4 2.2c-.7.4-1 .8-1 1.6" /><circle cx="12" cy="16.4" r=".7" fill="currentColor" stroke="none" /></svg>
              <span>{t('tour.boton')}</span>
            </button>
          )}
          <div className="vc-pill-prot"><I.IcShieldCheck /><span>{t('player.protegido')}</span></div>
        </div>
      </div>
      <div className="vc-pl-grid">
        <div>
          {testi && item?.persona && (
            <div className="vc-testi-context" data-i18n-exento="true">
              <span className="vc-testi-context-q">&ldquo;</span>
              <div className="vc-testi-context-name">{item.persona}</div>
              {descripcion && <div className="vc-testi-context-desc">{descripcion}</div>}
            </div>
          )}
          <div className="vc-player">
            {hasEmbed ? (
              <iframe
                className="vc-player-video"
                src={item.embedUrl}
                title={catalogoCa(item.nombre) || t('player.videoTag')}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : hasVideo && videoError ? (
              <div style={{ display: 'grid', placeItems: 'center', gap: 12, minHeight: 220, textAlign: 'center' }}>
                <p style={{ margin: 0 }}>{t('player.noCarga')}</p>
                <button className="vc-btn-ghost" onClick={() => { setVideoError(false); onPlay(item); }}>
                  <I.IcPlay />{t('player.reintentar')}
                </button>
              </div>
            ) : hasVideo ? (
              <>
                <VideoPlayerPropio
                  src={item.video}
                  poster={item.thumbnail}
                  esClase={item.tipo === 'clase'}
                  demoTour={tourVivo}
                  marcaAgua={marcaAgua}
                  videoKey={String(item.contenidoId || item.id || '')}
                  onProgreso={onTimeUpdate}
                  onEnded={alTerminarVideo}
                  onError={() => setVideoError(true)}
                />
              </>
            ) : (
              /* Sin vídeo real: estado HONESTO — nada de barra ni tiempos inventados
                 (antes pintaba «4:12 / 13:40» de maqueta). Auditoría/Ann 2026-07-13. */
              <div style={{ display: 'grid', placeItems: 'center', gap: 10, minHeight: 220, textAlign: 'center', opacity: .85 }}>
                <I.IcVideo />
                <p style={{ margin: 0 }}>{t('player.noDisponible')}</p>
              </div>
            )}
          </div>
          <div className="vc-pl-head">
            {cats.length > 0 && (
              <div className="vc-cats">{cats.map((c, i) => <span key={i} className={`vc-cat ${i > 0 ? 'alt' : ''}`}>{c}</span>)}</div>
            )}
            {/* Título: designación coloreada + técnico en gris al lado (Ann 2026-07-15).
                OJO (Ann 2026-07-16): muchos títulos YA llevan el nombre del técnico
                («Suave Paralelo David») → solo se añade si NO está ya en el título. */}
            {(() => {
              const nombreClase = item?.nombre || t('player.tituloDemo');
              const prof = String(item?.profesor || '').trim();
              const norm = (x) => x.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
              const yaLleva = prof && norm(nombreClase).includes(norm(prof.split(/\s+/)[0]));
              const titulo = `${nombreClase}${prof && !yaLleva ? ` · ${prof}` : ''}`;
              return <h1 className={testi ? 'vc-cap' : ''} data-i18n-exento={testi ? 'true' : undefined}>{testi ? capitalizar(item?.nombre || '') : designacionColoreada(titulo)}</h1>;
            })()}
            <div className="vc-submeta">
              {duracionLabel && <span><I.IcClock />{duracionLabel}</span>}
              {nivelLabel && <span className={item?.nivel ? `vc-plnivel ${item.nivel}` : ''}><I.IcShield />{nivelLabel}</span>}
              {materialLabel && <span><I.IcDumbbell />{materialLabel}</span>}
            </div>
            <div className="vc-pl-actions">
              <button className={`vc-btn-ghost fav ${isFav ? 'on' : ''}`} onClick={onToggleFav}><I.IcHeart />{isFav ? t('player.guardada') : t('player.guardar')}</button>
              {!testi && (
                /* CHECK de logro (Ann: «que sea realmente marcar que has hecho algo»,
                   no un botón que parece de navegación): casilla circular que se
                   rellena en VERDE con su tic — como tachar una tarea. */
                <button type="button" className={`vcp-check ${hechaUI ? 'on' : ''}`} role="checkbox" aria-checked={hechaUI}
                  onClick={() => {
                    if (hechaUI) {
                      // desmarcar (Ann): se apaga el check y deja de contar en la rutina.
                      // doneRef se queda en true para que el automático de 2/3 no la
                      // vuelva a marcar solo en esta sesión (decisión de la clienta).
                      setHechaUI(false); setAnimo('');
                      onClaseDeshecha(item);
                      return;
                    }
                    setHechaUI(true); doneRef.current = true;
                    setAnimo(fraseAnimo(progRef.current));
                    onClaseHecha(item, { completada: true, progreso: Math.max(progRef.current, 0.01), manual: true });
                  }}>
                  <span className="vcp-check-circ" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
                  </span>
                  <span className="vcp-check-lbl">{hechaUI ? t('player.hechaMarcada') : t('player.marcarHecha')}</span>
                  {hechaUI && animo && <span className="vcp-check-msg">{animo}</span>}
                </button>
              )}
              {/* Botón "Compartir" RETIRADO del canal (Ann 2026-07-13, urgente). */}
            </div>
            {descripcion && <p className="vc-desc">{descripcion}</p>}
          </div>
        </div>
        <div className="vc-rail">
          {cp && (cp.texto || cpVideo) && (
            <div className="vc-card vc-pilar">
              <div className="vc-pilar-top">
                <div className="vc-pilar-av"><I.IcPerson /></div>
                <div><div className="vc-pilar-name">Pilar Domínguez</div><div className="vc-pilar-role">{t('player.pilarRole')}</div></div>
              </div>
              {cp.texto && <div className="vc-pilar-quote">{cp.texto}</div>}
              {cpVideo && (
                <a className="vc-pilar-video" href={cp.embedUrl || cp.video} target="_blank" rel="noopener noreferrer">
                  <I.IcPlay />{t('player.verConsejo')}
                </a>
              )}
            </div>
          )}
          {/* Vídeo-consejo de la directora (propio de la clase) */}
          {hayConsejo && (
            <div className="vc-card">
              <div className="vc-card-h"><I.IcSpark /><h3>{t('player.consejoPilar')}</h3></div>
              {consejo.texto && <p className="vc-consejo-texto">{consejo.texto}</p>}
              {consejo.video_url ? (
                <div className="vc-consejo-media">
                  <video src={consejo.video_url} controls playsInline controlsList="nodownload"
                    onContextMenu={(e) => e.preventDefault()}
                    onLoadedMetadata={(e) => { const v = e.currentTarget; if (v.videoHeight > v.videoWidth) v.parentElement.classList.add('vertical'); }} />
                </div>
              ) : consejo.embed_url ? (
                <div className="vc-consejo-media">
                  <iframe src={consejo.embed_url} title={t('player.consejoPilar')} frameBorder="0" allow="encrypted-media; fullscreen" allowFullScreen />
                </div>
              ) : null}
            </div>
          )}
          {/* Cómo hacerla: real si está activo; demo solo en preview sin datos */}
          {(item ? (ch && ch.activo) : true) && (
            <div className="vc-card">
              <div className="vc-card-h"><I.IcCheckList /><h3>{t('player.comoHacerla')}</h3></div>
              <div className="vc-reps">
                <div className="vc-rep"><div className="v">{ch?.series || '3'}</div><div className="l">{t('player.series')}</div></div>
                <div className="vc-rep"><div className="v">{ch?.repeticiones || '10'}</div><div className="l">{t('player.repeticiones')}</div></div>
                <div className="vc-rep"><div className="v">{ch?.descanso || '30s'}</div><div className="l">{t('player.descanso')}</div></div>
              </div>
              <div className="vc-steps">
                {(ch && ch.pasos && ch.pasos.length ? ch.pasos : [
                  t('player.paso1'),
                  t('player.paso2'),
                  t('player.paso3'),
                ]).map((paso, i) => (
                  <div className="vc-step" key={i}><span className="n">{i + 1}</span>{paso}</div>
                ))}
              </div>
            </div>
          )}
          {/* Para complementar: real si hay; demo solo en preview sin datos */}
          {(item ? comp.length > 0 : true) && (
            <div className="vc-card">
              <div className="vc-card-h"><I.IcSpark /><h3>{t('player.paraComplementar')}</h3></div>
              <div className="vc-rel">
                {(item ? comp : [
                  { titulo: t('player.comp1t'), subtitulo: t('player.comp1s') },
                  { titulo: t('player.comp2t'), subtitulo: t('player.comp2s') },
                ]).map((c, i) => {
                  // «Salto automático» TAMBIÉN en los complementos (Ann 2026-07-16: en las
                  // CLASES los vídeos del lado son ESTOS — la opción no se veía en prod).
                  const reproducible = item && (c.video_url || c.embed_url);
                  const comoItem = reproducible ? {
                    contenidoId: c.id || `comp-${i}`, id: c.id || `comp-${i}`,
                    nombre: c.titulo, tipo: 'complemento',
                    video: c.video_url || null, embedUrl: c.embed_url || null,
                    thumbnail: c.thumbnail || '', zona: t('player.complemento'), descripcion: c.subtitulo || '',
                  } : null;
                  const marcado = comoItem && alAcabar && (alAcabar.contenidoId || alAcabar.id) === comoItem.contenidoId;
                  return (
                    <div className={`vc-rel-item ${reproducible ? 'vc-rel-clickable' : ''}`} key={i}
                      onClick={reproducible ? () => onPlay(comoItem) : undefined}
                      style={reproducible ? { cursor: 'pointer' } : undefined}>
                      <div className={`vc-rel-thumb ${i % 2 ? 'r' : ''}`}>
                        {c.thumbnail ? <img src={c.thumbnail} alt="" loading="lazy" onError={(e) => { e.currentTarget.remove(); }} /> : <div className="p"><I.IcPlay /></div>}
                      </div>
                      <div className="vc-rel-info"><span className="vc-rel-tag">{t('player.complemento')}</span><h5>{c.titulo}</h5><div className="rm">{c.subtitulo || ''}</div></div>
                      {reproducible && (
                        <button type="button" className={`vcp-alacabar ${marcado ? 'on' : ''}`} aria-pressed={!!marcado}
                          title={t('player.alAcabarTip')}
                          onClick={(e) => { e.stopPropagation(); setAlAcabar(marcado ? null : comoItem); }}>
                          <I.IcCheck2 />{marcado ? t('player.alAcabarOn') : t('player.alAcabar')}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* También puede interesarte: solo para NO-clases (otro contenido a descubrir) */}
          {item && item.tipo !== 'clase' && sugerencias.length > 0 && (
            <div className="vc-card">
              <div className="vc-card-h"><I.IcSpark /><h3>{t('player.tambienInteresa')}</h3></div>
              <p className="vcp-sug-hint">{t('player.sugerenciasHint')}</p>
              <div className="vc-rel">
                {sugerencias.map((s, i) => {
                  const nv = vistas[s.contenidoId || s.id] || 0;
                  const meta = [s.duracion ? `${s.duracion} min` : null, nv > 0 ? t('player.reproducciones', { n: fmtVistas(nv) }) : null].filter(Boolean).join(' · ');
                  return (
                    <div className="vc-rel-item vc-rel-clickable" key={s.contenidoId || s.id || i} onClick={() => onPlay(s)}>
                      <div className="vc-rel-thumb">
                        {s.thumbnail ? <img src={s.thumbnail} alt="" /> : <div className="p"><I.IcPlay /></div>}
                      </div>
                      <div className="vc-rel-info"><span className="vc-rel-tag">{s.zona || t('player.videoTag')}</span><h5>{s.nombre}</h5><div className="rm">{meta}</div></div>
                      <button type="button"
                        className={`vcp-alacabar ${alAcabar && (alAcabar.contenidoId || alAcabar.id) === (s.contenidoId || s.id) ? 'on' : ''}`}
                        aria-pressed={!!(alAcabar && (alAcabar.contenidoId || alAcabar.id) === (s.contenidoId || s.id))}
                        title={t('player.alAcabarTip')}
                        onClick={(e) => {
                          e.stopPropagation(); // no abrir la sugerencia: solo programarla
                          const ya = alAcabar && (alAcabar.contenidoId || alAcabar.id) === (s.contenidoId || s.id);
                          setAlAcabar(ya ? null : s); // solo UNA marcada a la vez
                        }}>
                        <I.IcCheck2 />{alAcabar && (alAcabar.contenidoId || alAcabar.id) === (s.contenidoId || s.id) ? t('player.alAcabarOn') : t('player.alAcabar')}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* Placeholder simple para pantallas sin maqueta dedicada (consejos / fisio) */
/* ===== SCREEN: CLASES MPD (compuesta) ·  rutina 5 → áreas MPD (orden) → favoritas ===== */
// Claves i18n por tipo (se resuelven con t() al agrupar); 'area' cae al literal de marca.
const _ETIQUETA_TIPO = { relajacion: 'mpd.tipoRelajaciones', recomendacion: 'home.consejosErgonomicos', consejo: 'mpd.tipoConsejosPilar', clase: 'mpd.tipoClasesMetodo' };

function ScreenClasesMPD({ clasesSemana = [], areas = [], favoritos = [], onPlay, isFav = () => false, onToggleFav = () => {}, cargando = false }) {
  const { t } = useTranslation('canal');
  // Regla (Ann 2026-07-13): la rutina de esta semana en "Clases MPD" muestra LO MISMO que
  // en Home (todas las clases de la semana con contenido) — sin el filtro es_mpd que antes
  // recortaba de más. Las clases de la semana YA son del Método.
  const rutina = (clasesSemana || []).filter(c => c && (c.id || c.contenidoId || c.nombre || c.modalidad || c.video));
  // Mismo trato que la Home (encargo Guardián 2026-07-13): las FIJAS llevan la banda
  // diagonal «Selección de la semana» (TODAS, aunque el técnico no esté activo — regla
  // banda≠hero) y las EXTRA van en su propia sección, sin banda.
  const rutinaFijas = rutina.filter(c => !c.esExtra);
  const rutinaExtras = rutina.filter(c => c.esExtra);
  // Todo el contenido marcado es_mpd (clases + relajaciones + consejos + fichas de área),
  // vía el agregador del backend GET /canal-clases/mpd/agregado. ESTA es la fuente real.
  const [mpd, setMpd] = useState([]);
  useEffect(() => {
    let alive = true;
    // Cruce: el agregador trae TODO lo es_mpd, pero las clases NO PUBLICADAS no deben salir.
    // getClasesBiblioteca ya filtra por estado==='activo' (publicadas), así que filtramos
    // las de tipo 'clase' del agregador contra ese conjunto. (Los demás tipos ya vienen
    // filtrados por 'activa' en el backend.)
    Promise.all([getClasesMpdAgregado(), getClasesBiblioteca()])
      .then(([agg, bib]) => {
        if (!alive) return;
        // CINTURÓN (Guardián · Ann 2026-07-16): si la biblioteca NO llega (null por
        // error de red o del servidor), NO filtramos. Antes: bib=null → publicadas
        // vacío → el filtro borraba TODAS las clases → el canal se quedaba sin nada
        // ("no se cargan las clases del método"). Un fallo al pedir la lista de
        // control jamás puede hacer desaparecer el contenido: ante la duda, se
        // muestra lo que hay. (El 405 que lo disparaba está corregido en el backend;
        // esto es para que NUNCA vuelva a pasar por otra causa.)
        if (!Array.isArray(bib)) {
          setMpd(agg || []);
          return;
        }
        const publicadas = new Set(bib.map(c => String(c.id)));
        const filtrado = (agg || []).filter(c => c.tipo !== 'clase' || publicadas.has(String(c.id)));
        setMpd(filtrado);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  // Nombre visible del área a partir de su key (título del canal si existe).
  const nombreArea = (key) => {
    const a = (areas || []).find(x => x && x.key === key);
    return a ? (a.titulo_canal || a.label || key) : '';
  };
  // Agrupar por ÁREA (Ann 2026-07-09): enunciado del área + sus clases debajo.
  // Lo que no tiene área se agrupa por su tipo (Relajaciones, Consejos…).
  const grupos = [];
  const idxPorClave = {};
  // SIN REPETIR (Ann 2026-07-18: «arriba también salen — si son las mismas,
  // fuera de la lista»): las clases que YA están en la rutina de la semana
  // (fijas o extra, pintadas arriba) no vuelven a salir en «Clases del Método».
  const _idsRutina = new Set(rutina.map(c => String(c.contenidoId ?? c.id ?? '')));
  (mpd || []).forEach(c => {
    if (c.tipo === 'clase' && _idsRutina.has(String(c.id))) return;
    const clave = c.areaKey || `tipo:${c.tipo}`;
    if (idxPorClave[clave] === undefined) {
      idxPorClave[clave] = grupos.length;
      grupos.push({ clave, titulo: nombreArea(c.areaKey) || (_ETIQUETA_TIPO[c.tipo] ? t(_ETIQUETA_TIPO[c.tipo]) : '') || 'Método Pilar Domínguez', items: [] });
    }
    grupos[idxPorClave[clave]].items.push(c);
  });
  return (
    <>
      <div className="vc-page-head vc-reveal"><h1><b>{t('mpd.titulo')}</b></h1><p>El Método Pilar Domínguez</p></div>

      {rutinaFijas.length > 0 && (<>
        <SectionHead title={t('mpd.rutina')} sub={t('mpd.nClases', { count: rutinaFijas.length })} />
        <section className="vc-grid">{rutinaFijas.map((c, i) => <VideoCard key={c.id || i} item={c} onPlay={onPlay} isFav={isFav} onToggleFav={onToggleFav} selSemana />)}</section>
      </>)}

      {rutinaExtras.length > 0 && (<>
        <SectionHead title={t('home.tusExtras')} sub={t('home.extrasSub')} />
        <section className="vc-grid">{rutinaExtras.map((c, i) => <VideoCard key={c.id || i} item={c} onPlay={onPlay} isFav={isFav} onToggleFav={onToggleFav} />)}</section>
      </>)}

      {grupos.map(g => (
        <div key={g.clave}>
          <SectionHead title={g.titulo} sub={t('mpd.nClases', { count: g.items.length })} />
          <section className="vc-grid">{g.items.map((c, i) => <VideoCard key={c.id || i} item={c} onPlay={onPlay} isFav={isFav} onToggleFav={onToggleFav} />)}</section>
        </div>
      ))}

      {rutina.length === 0 && grupos.length === 0 && (
        <div className="vc-page-head vc-reveal" style={{ paddingTop: 8 }}>
          <p>{cargando ? t('mpd.cargandoClases') : t('mpd.vacio')}</p>
        </div>
      )}
    </>
  );
}

/* ===== HERO ROTATIVO (portada superior) · portadas editoriales + anuncios + novedades destacadas =====
   Estilo TEAL del canal (decisión Ann 2026-07-08, coherente con el canal vivo).
   Contrato backend (pendiente): GET /canal-portada/rotativo → [{ id, tipo:'editorial'|'anuncio'|'novedad',
   imagen_url, kicker, etiqueta, titulo, texto, cta_texto, cta_url, orden, patrocinado }].
   Mientras no exista, muestra estas diapositivas de MUESTRA (marcadas como placeholder). */
const SLIDES_DEMO = () => [
  { id: 'd0', tipo: 'novedad', kicker: i18n.t('canal:rotativo.d0k'), etiqueta: i18n.t('canal:rotativo.d0e'), titulo: i18n.t('canal:rotativo.d0t'), texto: i18n.t('canal:rotativo.d0x'), cta_texto: i18n.t('canal:rotativo.d0c'), cta_url: '', imagen_url: '' },
  { id: 'd1', tipo: 'editorial', kicker: i18n.t('canal:rotativo.d1k'), etiqueta: i18n.t('canal:rotativo.d1e'), titulo: i18n.t('canal:rotativo.d1t'), texto: i18n.t('canal:rotativo.d1x'), cta_texto: i18n.t('canal:rotativo.d1c'), cta_url: '', imagen_url: '' },
  { id: 'd2', tipo: 'novedad', kicker: i18n.t('canal:rotativo.d0k'), etiqueta: i18n.t('canal:rotativo.d2e'), titulo: i18n.t('canal:rotativo.d2t'), texto: i18n.t('canal:rotativo.d2x'), cta_texto: i18n.t('canal:rotativo.d2c'), cta_url: '', imagen_url: '' },
];
// (Quitado d3 «20% MEFIR»: oferta INEXISTENTE — los anuncios reales vienen del backend,
//  no se inventan ofertas en el front. Auditoría/Ann 2026-07-13.)

// Ofertas de MUESTRA por sección (estilo Netflix/Movistar: el rotativo cambia según el menú).
// En real, cada slide del backend traerá `secciones` y se filtra por la sección activa.
const SLIDES_POR_SECCION = (seccion) => {
  const demo = SLIDES_DEMO();
  return {
    mpd: [
      { id: 's-mpd', tipo: 'editorial', kicker: i18n.t('canal:rotativo.sMpdK'), etiqueta: i18n.t('canal:rotativo.sMpdE'), titulo: i18n.t('canal:rotativo.sMpdT'), texto: i18n.t('canal:rotativo.sMpdX'), cta_texto: i18n.t('canal:rotativo.sMpdC'), cta_url: '' },
    ],
    'cat:relajaciones': [
      { id: 's-rel', tipo: 'editorial', kicker: i18n.t('canal:rotativo.sRelK'), etiqueta: i18n.t('canal:rotativo.sRelE'), titulo: i18n.t('canal:rotativo.sRelT'), texto: i18n.t('canal:rotativo.sRelX'), cta_texto: i18n.t('canal:rotativo.sRelC'), cta_url: '' },
    ],
    novedades: [demo[0], demo[2]],
  }[seccion];
};

function HeroRotativo({ slides, activo = true, onCtaInterna, seccion = 'inicio' }) {
  const { t } = useTranslation('canal');
  // Datos reales del backend (/canal-anuncios/wp/rotativo). Mientras no haya, muestra.
  const [real, setReal] = useState(null);
  // Anti-parpadeo (Auditoría 2026-07-12): hasta que la carga resuelva NO se pinta la
  // MUESTRA ("Estrenamos…") — antes flasheaba contenido falso y luego saltaba al real.
  const [cargado, setCargado] = useState(false);
  useEffect(() => {
    if (Array.isArray(slides) && slides.length) return undefined;
    let alive = true;
    getRotativoPortada()
      .then(r => { if (alive) { if (r) setReal(r); setCargado(true); } })
      .catch(() => { if (alive) setCargado(true); });
    return () => { alive = false; };
  }, [slides]);
  // Datos contextuales a la sección (regla Ann 2026-07-12): si la sección tiene
  // cabeceras PROPIAS (anidadas), MANDAN ellas solas; si no tiene, rotan las
  // GENERALES (sin sección). Último recurso: todas las reales, o la muestra.
  // FIX Ann 2026-07-15: el admin guarda las ÁREAS como 'cat:<key>' pero la pantalla
  // del área se llama 'area:<key>' → nunca casaban y siempre salía la principal.
  // Se aceptan las dos formas como equivalentes (sin tocar lo ya guardado en BD).
  const claves = seccion.startsWith('area:') ? [seccion, `cat:${seccion.slice(5)}`]
    : seccion.startsWith('cat:') ? [seccion, `area:${seccion.slice(4)}`] : [seccion];
  const data = (Array.isArray(slides) && slides.length) ? slides
    : (Array.isArray(real) && real.length)
      ? (() => {
          const propias = real.filter(s => Array.isArray(s.secciones) && s.secciones.length > 0 && claves.some(k => s.secciones.includes(k)));
          if (propias.length) return propias;
          // 🔴 NORMA Ann 2026-07-25 («no la olvides»): sin cabeceras propias, la
          // sección enseña LA MISMA portada que el INICIO — SOLO las marcadas
          // «Portada (inicio)», ni generales ni todas. Una cabecera marcada
          // para una sección concreta JAMÁS sale en otra. Sin nada → sin rotativo.
          return real.filter(s => Array.isArray(s.secciones) && s.secciones.length > 0 && s.secciones.includes('inicio'));
        })()
      : cargado ? (SLIDES_POR_SECCION(seccion) || SLIDES_DEMO()) : [];
  const [idx, setIdx] = useState(0);
  // Al cambiar de sección, reinicia el rotativo a la primera diapo.
  useEffect(() => { setIdx(0); }, [seccion]);
  const n = data.length;
  const go = (i) => setIdx((i + n) % n);
  useEffect(() => {
    if (!activo || n <= 1) return undefined;
    // Tiempos: si la carátula trae SEGUNDOS configurados en el admin (Ann 2026-07-13),
    // MANDAN esos. Si no (0/ausente), automático: la 1ª diapo dura más; las de venta
    // (anuncio/patrocinado) un poco más que el resto; las normales, estándar.
    const cur = data[idx] || {};
    const esVenta = cur.tipo === 'anuncio' || cur.patrocinado;
    const delay = Number(cur.segundos) > 0
      ? Math.min(60, Number(cur.segundos)) * 1000
      : (idx === 0 ? 9000 : esVenta ? 8500 : 6000);
    const t = setTimeout(() => setIdx(p => (p + 1) % n), delay);
    return () => clearTimeout(t);
  }, [activo, n, idx]);
  if (!n) return null;
  const s = data[idx] || data[0];
  // La ACCIÓN especial (reserva_fisio / ir:<seccion>) MANDA siempre sobre la URL:
  // si la cabecera trae ambas (p. ej. plantilla vieja con enlace + acción añadida
  // después), el botón obedece a la acción — antes el enlace externo la pisaba.
  const ctaExterna = !s.accion && !!(s.cta_url && /^https?:\/\//i.test(s.cta_url));
  return (
    <section className="vch" aria-roledescription={t('rotativo.carrusel')}>
      {data.map((sl, i) => (
        <div key={sl.id || i} className={`vch-slide ${i === idx ? 'on' : ''}`} aria-hidden={i !== idx}>
          <div className="vch-img">
            {sl.imagen_url
              ? <img src={sl.imagen_url} alt="" style={sl.pos_pc ? { objectPosition: sl.pos_pc } : undefined} />
              : <div className="vch-img-ph" />}
          </div>
          <div className="vch-grad" />
        </div>
      ))}
      <div className="vch-body" key={idx}>
        <span className="vch-kicker vch-l0">
          {s.patrocinado && <em className="vch-patro">{t('rotativo.patrocinado')}</em>}
          {[s.kicker, s.etiqueta].filter(Boolean).join(' · ')}
        </span>
        <h1 className="vch-title vch-l1" style={s.color_texto ? { color: s.color_texto } : undefined}>{s.titulo}</h1>
        {s.texto && <p className="vch-text vch-l2" style={s.color_texto ? { color: s.color_texto } : undefined}>{s.texto}</p>}
        {(s.cta_texto || ctaExterna) && (
          <div className="vch-cta vch-l3">
            {ctaExterna
              ? <a className="vch-btn" href={s.cta_url} target="_blank" rel="noopener noreferrer">{s.cta_texto || t('rotativo.masInfo')}</a>
              : <button className={`vch-btn${(s.accion || '').startsWith('whatsapp:') ? ' vch-btn--whatsapp' : ''}`} onClick={() => onCtaInterna && onCtaInterna(s)}>
                  {(s.accion || '').startsWith('whatsapp:') && (
                    <svg className="vch-btn-wa-ico" viewBox="0 0 24 24" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  )}
                  {s.cta_texto || t('rotativo.verMas')}
                </button>}
          </div>
        )}
      </div>
      {n > 1 && (<>
        <button className="vch-arrow left" onClick={() => go(idx - 1)} aria-label={t('rotativo.anterior')}>‹</button>
        <button className="vch-arrow right" onClick={() => go(idx + 1)} aria-label={t('rotativo.siguiente')}>›</button>
        <div className="vch-dots">
          {data.map((_, i) => (
            <button key={i} className={`vch-dot ${i === idx ? 'on' : ''}`} onClick={() => go(i)} aria-label={t('rotativo.irA', { n: i + 1 })} />
          ))}
        </div>
      </>)}
    </section>
  );
}

/* ===== SCREEN: EN DIRECTO (clases y charlas en vivo por videollamada) =====
   Paleta teal del canal (armonizada, decisión Ann 2026-07-08). Acento rojo solo
   en el indicador "EN DIRECTO". Fuentes CRM (Inter/Manrope) vía clases vcl-. */
const PROXIMOS_DIRECTOS = () => [
  { cuando: i18n.t('canal:endirecto.d1c'), offsetDias: 0, hora: '19:00', durMin: 60, titulo: i18n.t('canal:endirecto.d1t'), tipo: i18n.t('canal:endirecto.d1p') },
  { cuando: i18n.t('canal:endirecto.d2c'), offsetDias: 1, hora: '08:00', durMin: 45, titulo: i18n.t('canal:endirecto.d2t'), tipo: i18n.t('canal:endirecto.d2p') },
  { cuando: i18n.t('canal:endirecto.d3c'), offsetDias: 3, hora: '18:30', durMin: 45, titulo: i18n.t('canal:endirecto.d3t'), tipo: i18n.t('canal:endirecto.d3p') },
  { cuando: i18n.t('canal:endirecto.d4c'), offsetDias: 5, hora: '10:00', durMin: 60, titulo: i18n.t('canal:endirecto.d4t'), tipo: i18n.t('canal:endirecto.d4p') },
];

// Construye inicio/fin (hora local) del directo a partir del offset en días + "HH:MM".
function _fechasDirecto(d) {
  const [h, m] = (d.hora || '00:00').split(':').map(Number);
  const ini = new Date();
  ini.setDate(ini.getDate() + (d.offsetDias || 0));
  ini.setHours(h, m, 0, 0);
  const fin = new Date(ini.getTime() + (d.durMin || 60) * 60000);
  return { ini, fin };
}
// Formato compacto para calendarios: YYYYMMDDTHHMMSS (hora de pared, local).
function _fmtCal(dt) {
  const p = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}${p(dt.getMonth() + 1)}${p(dt.getDate())}T${p(dt.getHours())}${p(dt.getMinutes())}00`;
}
const _DESC_DIRECTO = () => i18n.t('canal:endirecto.desc');
function _googleCalUrl(d) {
  const { ini, fin } = _fechasDirecto(d);
  const p = new URLSearchParams({
    action: 'TEMPLATE',
    text: d.titulo,
    dates: `${_fmtCal(ini)}/${_fmtCal(fin)}`,
    details: _DESC_DIRECTO(),
    location: i18n.t('canal:endirecto.lugar'),
    ctz: 'Europe/Madrid',
  });
  return `https://calendar.google.com/calendar/render?${p.toString()}`;
}
// Descarga un .ics (Calendario de Mac/Apple, iPhone, Outlook).
function _descargarIcs(d) {
  const { ini, fin } = _fechasDirecto(d);
  const ics = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Pilar Dominguez//Canal//ES', 'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `DTSTART:${_fmtCal(ini)}`, `DTEND:${_fmtCal(fin)}`,
    `SUMMARY:${d.titulo}`, `DESCRIPTION:${_DESC_DIRECTO()}`,
    `LOCATION:${i18n.t('canal:endirecto.lugar')}`,
    'END:VEVENT', 'END:VCALENDAR',
  ].join('\r\n');
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `directo-${(d.titulo || 'sesion').toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40)}.ics`;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function CalendarioMenu({ directo }) {
  const { t } = useTranslation('canal');
  const [open, setOpen] = useState(false);
  return (
    <div className="vcl-cal" onMouseLeave={() => setOpen(false)}>
      <button className="vcl-remind vcl-cal-btn" onClick={() => setOpen(o => !o)}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
        {t('endirecto.addCalendario')}
      </button>
      {open && (
        <div className="vcl-cal-pop">
          <a href={_googleCalUrl(directo)} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}>{t('endirecto.googleCal')}</a>
          <button onClick={() => { _descargarIcs(directo); setOpen(false); }}>{t('endirecto.appleCal')}</button>
        </div>
      )}
    </div>
  );
}

function ScreenEnDirecto() {
  const { t } = useTranslation('canal');
  return (
    <div className="vcl">
      <div className="vc-page-head vc-reveal">
        <div className="vc-soon-kicker">{t('nav.proximamente')}</div>
        <h1><b>{t('endirecto.titulo')}</b></h1>
        <p>{t('endirecto.intro')}</p>
      </div>

      {/* EN DESARROLLO (Ann): banner claro + contenido atenuado tipo maqueta */}
      <div className="vc-wip-banner">
        <span className="vc-wip-ic"><I.IcClock /></span>
        <div className="vc-wip-txt">
          <strong>{t('endirecto.casiListo')}</strong>
          <span>{t('endirecto.casiListoSub')}</span>
        </div>
      </div>
      <div className="vc-wip">
      {/* Directo actual · tarjeta destacada (2 columnas) */}
      <div className="vcl-hero vc-reveal">
        <div className="vcl-hero-media">
          <span className="vcl-badge"><i className="vcl-dot" />{t('endirecto.ahora')}</span>
          <button className="vcl-play" aria-label={t('endirecto.verDirecto')}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
          </button>
        </div>
        <div className="vcl-hero-body">
          <h2>{t('endirecto.heroTitulo')}</h2>
          <p className="vcl-hero-sub">{t('endirecto.heroSub')}</p>
          <p className="vcl-conectados">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13A4 4 0 0 1 16 11" /></svg>
            {t('endirecto.conectados')}
          </p>
          <button className="vcl-cta">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
            {t('endirecto.verDirecto')}
          </button>
        </div>
      </div>

      {/* Próximos directos */}
      <SectionHead title={t('endirecto.proximos')} />
      <div className="vcl-list">
        {PROXIMOS_DIRECTOS().map((d, i) => (
          <div className="vcl-row" key={i}>
            <div className="vcl-when">
              <span className="vcl-day">{d.cuando}</span>
              <span className="vcl-time">{d.hora}</span>
            </div>
            <div className="vcl-row-body">
              <div className="vcl-row-title">{d.titulo}</div>
              <div className="vcl-row-tag">{d.tipo}</div>
            </div>
            <div className="vcl-row-acc">
              <button className="vcl-remind">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>
                {t('endirecto.recordarme')}
              </button>
              <CalendarioMenu directo={d} />
            </div>
          </div>
        ))}
      </div>

      <p className="vcl-nota">{t('endirecto.nota')}</p>
      </div>
    </div>
  );
}

/* ================= MI PERFIL · suscripción y facturas =================
   Encargo de Ann (17-jul-2026), textual:
     "¿Dónde te pedí que pusieras Mi Perfil en los Club? EN EL LATERAL."
     "Has de mostrarlas en canal CON NUESTRA IDENTIDAD DE MARCA."
     "Y que pueda CANCELAR desde ahí."
     "Que sus pagos estén controlados."
     "Irremediablemente tendrán que provenir de Membership."

   EL PORQUÉ: la socia CLUB entra DIRECTA al canal (decisión de Ann, 15-jul) → su
   perfil quedaba escondido detrás del botón «Volver». Ahora vive en el lateral,
   dentro del canal. Ann: que no salga del canal ni vea WordPress.

   EL REPARTO DEL TRABAJO:
     · Esta pantalla (yo)      → la CARA, con la marca del Método.
     · Membership / UMP (ellos) → los DATOS. Sin ellos aquí no hay nada que pintar.

   ⚠️ HOY NO HAY DATOS: el CRM no tiene ni llave de Stripe ni aviso de WordPress
      (0 llamadas al webhook en toda la historia · medido 17-jul-2026). Por eso la
      pantalla nace con su ESTADO VACÍO honesto: NO se inventa un plan, NO se
      inventa una factura, y NO se dice "no tienes facturas" cuando lo que pasa es
      que no lo sabemos. Ann: "cargando ≠ no hay nada".
      Cuando el agente de Membership entregue el endpoint, se rellena `datos` y
      esta pantalla ya está.

   Encargo enviado: docs/ENCARGO_MEMBERSHIP_A_CANAL.md
==================================================================== */
function FilaDato({ etiqueta, valor }) {
  return (
    <div className="vcp-fila">
      <span className="vcp-fila-k">{etiqueta}</span>
      <span className="vcp-fila-v">{valor}</span>
    </div>
  );
}

function ScreenMiPerfil({ nombre = '', email = '', plan = '', datos = null, cargando = false, onCambiarPassword = null, onDarseDeBaja = null, bajaSolicitada = false, perfil = null, clientMode = false }) {
  const { t } = useTranslation('canal');
  // ── SU TARJETA · Ann 19-10-2026, tras el correo de Tatiana Doguet:
  //    «je voulais rajouter ma nouvelle carte bancaire mais je n'arrive pas.
  //     Il n'y a pas bouton "Añadir una tarjeta"». Y tenía razón.
  //    MEDIDO: el servidor le decía que SÍ (config y preparar, los dos
  //    `disponible: true`), y la pantalla `MiPerfil` pintaba el botón — pero
  //    una socia SOLO-CLUB nunca llega a esa pantalla: su menú abre ESTA, que
  //    no tenía NADA de tarjetas. Ninguna socia del Club podía cambiarla.
  //    NO se la manda a la otra pantalla: Ann decidió el 20-07-2026 «que se
  //    vaya a la página mi perfil DENTRO del canal» · «no puede ser que vayan
  //    al perfil premium». Así que la tarjeta viene AQUÍ.
  //    Solo en `clientMode`: esta misma pantalla la usa el ADMIN para ver el
  //    Canal como una clienta, y allí el botón tocaría SU PROPIA tarjeta.
  const [tarjeta, setTarjeta] = React.useState(null);
  const [puedeTarjeta, setPuedeTarjeta] = React.useState(false);
  const [modalTarjeta, setModalTarjeta] = React.useState(false);
  React.useEffect(() => {
    if (!clientMode) return;
    axios.get('/api/canal/mi-tarjeta').then(r => setTarjeta(r.data || null))
      .catch(() => setTarjeta(null));
    axios.get('/api/canal/mi-tarjeta/config')
      .then(r => setPuedeTarjeta(r.data?.disponible === true))
      .catch(() => setPuedeTarjeta(false));
  }, [clientMode]);
  const inicial = (nombre || email || 'C').trim().charAt(0).toUpperCase();
  // `datos` llega de Membership. Mientras no llegue: ni cifras inventadas ni "no hay".
  const sus = datos && datos.suscripcion;
  const facturas = (datos && datos.facturas) || [];
  const sinConexion = !cargando && !datos;

  return (
    <div className="vc-page-head vc-reveal" style={{ paddingTop: 34 }}>
      <h1><b>{t('perfil.titulo')}</b></h1>
      <p>{t('perfil.sub')}</p>

      {/* ── Cabecera: quién es ── */}
      <div className="vcp-cab">
        <div className="vcp-avatar">{inicial}</div>
        <div className="vcp-cab-txt">
          <div className="vcp-nombre">{nombre || email}</div>
          <div className="vcp-email">{email}</div>
        </div>
        {plan ? <span className="vcp-plan">{plan}</span> : null}
      </div>

      {/* ── Sus datos · contacto y contraseña (Ann 2026-07-20: «perfil (nombre y
             apellidos, datos de contacto registrados email, cambiar contraseña)») ── */}
      <section className="vcp-bloque">
        <h2 className="vcp-h2">{t('perfil.tusDatos')}</h2>
        <div className="vcp-card">
          {/* TODO lo que el CRM recoge en el alta Club (Ann 2026-07-20). Lo que
              no se pidió en el alta se pide luego en «Completa tu perfil», y
              aquí sale como «—» hasta que lo rellene. */}
          <FilaDato etiqueta={t('perfil.nombre')} valor={(perfil && perfil.nombre) || nombre || '—'} />
          <FilaDato etiqueta={t('perfil.apellido1')} valor={(perfil && perfil.primer_apellido) || '—'} />
          <FilaDato etiqueta={t('perfil.apellido2')} valor={(perfil && perfil.segundo_apellido) || '—'} />
          <FilaDato etiqueta={t('perfil.correo')} valor={email || '—'} />
          <FilaDato etiqueta={t('perfil.telefono')} valor={(perfil && (perfil.telefono || perfil.movil)) || '—'} />
          <FilaDato etiqueta={t('perfil.dni')} valor={(perfil && perfil.dni) || '—'} />
          <FilaDato etiqueta={t('perfil.comunicaciones')} valor={
            perfil ? ((perfil.acepta_comunicaciones_comerciales ? t('perfil.comSi') : t('perfil.comNo'))) : '—'} />
          {/* Botones con el lenguaje del canal (Ann 2026-07-20: «ha de ser un
              botón corporativo… al estilo del canal de vídeos»), no texto verde. */}
          {clientMode && puedeTarjeta && (
            <FilaDato etiqueta={t('perfil.tarjeta')} valor={
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span>{tarjeta?.tiene
                  ? `${tarjeta.marca || ''} ···${tarjeta.ultimos4 || ''}`.trim()
                  : t('perfil.tarjetaNinguna')}</span>
                <button type="button" className="vcp-accion"
                  onClick={() => setModalTarjeta(true)}>
                  {tarjeta?.tiene ? t('perfil.tarjetaCambiar') : t('perfil.tarjetaAnadir')}
                </button>
              </span>} />
          )}
          {modalTarjeta && (
            <CambiarTarjetaModal
              API="/api"
              onCerrar={() => setModalTarjeta(false)}
              onGuardada={(d) => {
                setTarjeta(prev => ({ ...(prev || {}), tiene: true, marca: d.marca,
                  ultimos4: d.ultimos4, caduca_mes: d.caduca_mes, caduca_anyo: d.caduca_anyo }));
                setModalTarjeta(false);
              }} />
          )}
          <FilaDato etiqueta={t('perfil.contrasena')} valor={
            <button type="button" className="vcp-accion"
                    onClick={() => onCambiarPassword && onCambiarPassword()}>
              {t('perfil.cambiarPass')}
            </button>} />
          <FilaDato etiqueta={t('perfil.seleccionIdioma')} valor={<div className="vcp-idioma"><SelectorIdioma compacto /></div>} />
        </div>
      </section>

      {/* ── Su suscripción ── */}
      <section className="vcp-bloque">
        <h2 className="vcp-h2">{t('perfil.suscripcionT')}</h2>
        {/* La tabla se pinta SIEMPRE: así se ven los encabezados aunque la
            plataforma de pago no conteste (Ann 2026-07-20). La fila dice la
            verdad: cargando / no se ha podido consultar / los datos. */}
        {true && (
          <div className="vcp-tabla-wrap">
            {/* Mismas columnas que Membership, en ESPAÑOL (Ann 2026-07-20), con
                «Darme de baja» a la derecha, en Acciones. Lo que no nos consta
                (detalles del plan, prueba) se pinta como «—»: nunca inventamos. */}
            <table className="vcp-tabla vcp-tabla-sus">
              <thead>
                <tr>
                  <th>{t('perfil.colMembresia')}</th>
                  <th>{t('perfil.colDetalles')}</th>
                  <th>{t('perfil.importe')}</th>
                  <th>{t('perfil.colServicio')}</th>
                  <th>{t('perfil.colPrueba')}</th>
                  <th>{t('perfil.colComienza')}</th>
                  <th>{t('perfil.colCaduca')}</th>
                  <th>{t('perfil.estado')}</th>
                  <th style={{ textAlign: 'right' }}>{t('perfil.colAcciones')}</th>
                </tr>
              </thead>
              <tbody>
                {cargando && <tr><td colSpan={9} className="vcp-vacio">{t('perfil.cargando')}</td></tr>}
                {!cargando && !sus && (
                  <tr>
                    <td colSpan={8} className="vcp-vacio">{t('perfil.sinDatos')}</td>
                    {/* Ann 2026-09-10: el botón de baja NO puede depender de que lleguen los datos
                        del plan. Antes vivía dentro de la fila con datos: si no llegaban (y no
                        llegaban), la socia se quedaba SIN NINGUNA forma de darse de baja. */}
                    <td data-eti={t('perfil.colAcciones')} style={{ textAlign: 'right' }}>
                      {bajaSolicitada
                        ? <span className="vcp-baja-ok">{t('perfil.bajaRegistrada')}</span>
                        : <button type="button" className="vcp-btn-baja" onClick={() => onDarseDeBaja && onDarseDeBaja()}>{t('perfil.darmeBaja')}</button>}
                    </td>
                  </tr>
                )}
                {sus && (
                <tr>
                  <td data-eti={t('perfil.colMembresia')}>{sus.plan || '—'}</td>
                  <td data-eti={t('perfil.colDetalles')}>{sus.detalles || sus.periodo || '—'}</td>
                  <td data-eti={t('perfil.importe')} className="vcp-num">{sus.importe ? `${sus.importe} ${sus.moneda || 'EUR'}` : '—'}</td>
                  <td data-eti={t('perfil.colServicio')}>{sus.forma_pago || '—'}</td>
                  <td data-eti={t('perfil.colPrueba')}>{sus.prueba || '—'}</td>
                  <td data-eti={t('perfil.colComienza')}>{sus.empieza || '—'}</td>
                  <td data-eti={t('perfil.colCaduca')}>{sus.caduca || '—'}</td>
                  <td><span className={`vcp-estado ${(sus.estado === 'Activa' || sus.estado === 'Active') ? 'on' : 'off'}`}>{sus.estado || '—'}</span></td>
                  <td style={{ textAlign: 'right' }}>
                    {bajaSolicitada
                      ? <span className="vcp-estado off">{t('perfil.bajaSolicitada')}</span>
                      : <button type="button" className="vcp-btn-baja" onClick={() => onDarseDeBaja && onDarseDeBaja()}>{t('perfil.darmeBaja')}</button>}
                  </td>
                </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        {bajaSolicitada && (
          <p className="vcp-vacio" style={{ marginTop: 10 }}>
            {t('perfil.bajaTexto')}
          </p>
        )}
      </section>

      {/* ── Documentación · lo que aceptó al entrar (Ann 2026-07-20:
             «las condiciones de uso de la plataforma», NO la normativa del
             centro). Se abren en pestaña nueva para no sacarla del canal. ── */}
      <section className="vcp-bloque">
        <h2 className="vcp-h2">{t('perfil.documentacion')}</h2>
        <div className="vcp-card">
          <FilaDato etiqueta={t('perfil.condiciones')} valor={
            <a className="vcp-accion" href="/condiciones" target="_blank" rel="noopener noreferrer">{t('perfil.verDoc')}</a>} />
          <FilaDato etiqueta={t('perfil.privacidad')} valor={
            <a className="vcp-accion" href="/privacidad" target="_blank" rel="noopener noreferrer">{t('perfil.verDoc')}</a>} />
        </div>
      </section>

      {/* ── Sus facturas ── */}
      <section className="vcp-bloque">
        <h2 className="vcp-h2">{t('perfil.facturasT')}</h2>
        {/* Encabezados SIEMPRE visibles (Ann 2026-07-20: «pon los enunciados de
            facturación»). La fila de dentro dice la verdad: cargando, no se ha
            podido consultar, todavía no hay facturas, o las facturas. */}
        {true && (
          <div className="vcp-tabla-wrap">
            <table className="vcp-tabla">
              <thead>
                <tr>
                  <th>{t('perfil.fNumeroLargo')}</th>
                  <th>{t('perfil.fFecha')}</th>
                  <th>{t('perfil.fPeriodo')}</th>
                  <th>{t('perfil.importe')}</th>
                  <th>{t('perfil.fForma')}</th>
                  <th>{t('perfil.estado')}</th>
                  <th style={{ textAlign: 'right' }}>{t('perfil.fPdf')}</th>
                </tr>
              </thead>
              <tbody>
                {cargando && <tr><td colSpan={7} className="vcp-vacio">{t('perfil.cargando')}</td></tr>}
                {!cargando && !datos && <tr><td colSpan={7} className="vcp-vacio">{t('perfil.sinFacturasDatos')}</td></tr>}
                {!cargando && datos && facturas.length === 0 && (
                  <tr><td colSpan={7} className="vcp-vacio">{t('perfil.sinFacturas')}</td></tr>)}
                {facturas.map((f, i) => (
                  <tr key={f.numero || i}>
                    <td>{f.numero}</td>
                    <td>{f.fecha}</td>
                    <td>{f.periodo || '—'}</td>
                    <td className="vcp-num">{f.importe} {f.moneda || 'EUR'}</td>
                    <td>{f.forma_pago || '—'}</td>
                    <td><span className={`vcp-estado ${(f.estado === 'Completed' || f.estado === 'Pagada') ? 'on' : 'off'}`}>{f.estado}</span></td>
                    <td style={{ textAlign: 'right' }}>{f.pdf_url
                      ? <a className="vcp-pdf" href={f.pdf_url} target="_blank" rel="noopener noreferrer">PDF</a>
                      : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── Cancelar · Ann: "que pueda cancelar desde ahí" ── */}
      {sus && sus.estado === 'Active' && (
        <section className="vcp-bloque">
          <button type="button" className="vcp-cancelar" disabled title={t('perfil.cancelarProx')}>
            {t('perfil.cancelar')}
          </button>
          <p className="vcp-cancelar-nota">{t('perfil.cancelarProx')}</p>
        </section>
      )}
    </div>
  );
}

function ScreenSimple({ title, text }) {
  return (
    <div className="vc-page-head vc-reveal" style={{ paddingTop: 40 }}>
      <h1><b>{title}</b></h1><p>{text}</p>
    </div>
  );
}

/* ================= COMPONENTE PRINCIPAL ================= */
// ══ SALIDAS DE LA PANTALLA «no disponible» (Ann 2026-09-26) ═══════════════
// En un solo sitio: si cambia el WhatsApp o el correo, se cambia aquí y ya.
// Son los MISMOS que el alta enseña en sus cinco pasos, para que quien pide
// ayuda hable con quien ya le estaba ayudando.
const URL_AYUDA_WHATSAPP = 'https://wa.me/34623036731?text=' +
  encodeURIComponent('Hola, tengo problemas para entrar en mi Canal');
const URL_AYUDA_EMAIL = 'mailto:mpdonline@metodopilardominguez.com' +
  '?subject=' + encodeURIComponent('No puedo entrar en mi Canal');
// Terminar el pago: la misma puerta por la que salió del alta.
// [Ann 2026-10-23] WordPress cerrado: el pago siempre lo crea el CRM en Stripe.

const VisionClienteTab = ({ clientMode = false, clienteNombre = '', clienteApellido = '', clienteEmail = '', onMiPerfil = null, onSalir = null, esClub = false, pagoPendiente = false }) => {
  const { t } = useTranslation('canal');
  useCanalFonts();
  // Nombre real del cliente (de su sesión). Vacío en la preview del admin → genérico.
  const nombreCliente = (clienteNombre || '').trim();
  const nombreCompletoCliente = [clienteNombre, clienteApellido].filter(Boolean).join(' ').trim();
  const inicialCliente = (nombreCliente || 'C').charAt(0).toUpperCase();
  const [userType, setUserType] = useState('metodo'); // 'metodo' | 'paciente'
  const [device, setDevice] = useState('desktop');     // 'desktop' | 'tablet' | 'movil'
  const [membresia, setMembresia] = useState('');      // membresía REAL del cliente (para "Mi espacio")
  const [emailReal, setEmailReal] = useState('');      // email del cliente (marca de agua del reproductor)
  const [bloqueoDeuda, setBloqueoDeuda] = useState(null);  // recibo devuelto → aviso claro
  useEffect(() => {
    if (!clientMode) return;
    let alive = true;
    getMiMembresia().then(m => { if (alive && m) setMembresia(m); }).catch(() => {});
    getMiEmail().then(e => { if (alive && e) setEmailReal(e); }).catch(() => {});
    getMiBloqueoDeuda().then(d => { if (alive && d) setBloqueoDeuda(d); }).catch(() => {});
    return () => { alive = false; };
  }, [clientMode]);
  const [accesoMetodo, setAccesoMetodo] = useState(true);
  const [screen, setScreen] = useState('inicio');
  const [menuOpen, setMenuOpen] = useState(false);
  const [miEspacioOpen, setMiEspacioOpen] = useState(false); // cajón "Mi espacio" (Netflix)

  // VALORACIÓN "Opiniones Google" (Ann 2026-07-24): al LLEGAR, 1 semana después de
  // empezar a usar el canal, una sola vez. El backend decide la elegibilidad.
  const [valoracionOpen, setValoracionOpen] = useState(false);
  const [valoracionNombre, setValoracionNombre] = useState('');
  const [valoracionReabierto, setValoracionReabierto] = useState(false); // reenvío admin: ir directo a Google
  const [valoracionEstrellas, setValoracionEstrellas] = useState(0);     // nota previa (no re-pedir estrellas)
  const valoracionVotoRef = useRef(false); // evita "ahora no" si ya envió el voto
  const valoracionManualRef = useRef(false); // abierto desde «Opinar»: cerrar no cuenta como "ahora no"
  useEffect(() => {
    if (!clientMode) return;
    let vivo = true;
    // pequeño retardo para no competir con la carga inicial ni con la bienvenida
    const tmr = setTimeout(() => {
      deboPreguntarValoracion().then(r => {
        if (vivo && r && r.preguntar) { setValoracionNombre(r.nombre || nombreCliente || ''); setValoracionReabierto(!!r.reabierto); setValoracionEstrellas(r.estrellas || 0); setValoracionOpen(true); registrarValoracionMostrada(membresia); }
      }).catch(() => {});
    }, 2500);
    return () => { vivo = false; clearTimeout(tmr); };
  }, [clientMode, nombreCliente]);

  // ENTRADA y SALIDA reales del Canal (Ann 2026-07-20: «quiero conocer el tiempo
  // de permanencia medio»). Antes la «entrada» era el primer vídeo, así que quien
  // entraba y no abría nada NO dejaba rastro. Solo en modo clienta.
  useEffect(() => {
    if (!clientMode) return undefined;
    let irse = null;
    import('./canalApi').then(m => {
      if (m.registrarEntrada) m.registrarEntrada();
      irse = () => { if (m.registrarSalida) m.registrarSalida(); };
      window.addEventListener('pagehide', irse);
      window.addEventListener('beforeunload', irse);
    }).catch(() => {});
    return () => {
      if (irse) {
        irse();   // también al salir del Canal dentro de la propia app
        window.removeEventListener('pagehide', irse);
        window.removeEventListener('beforeunload', irse);
      }
    };
  }, [clientMode]);

  // PERMANENCIA POR SECCIÓN (Ann 2026-07-24: «quiero saber dónde va el usuario y qué
  // tiempo pasa en cada sitio»). Mide el tiempo en CADA sección y lo registra al
  // cambiar de sección, al cerrar la pestaña y al salir del canal. Solo en modo clienta.
  const permaRef = useRef({ seccion: null, t0: 0 });
  const flushPermanencia = React.useCallback((beacon = false) => {
    const p = permaRef.current;
    if (!p.seccion || !p.t0) return;
    const dur = (Date.now() - p.t0) / 1000;
    p.t0 = Date.now(); // evita doble conteo si se dispara otra vez seguida
    if (dur >= 1) {
      registrarPermanencia({ seccion: p.seccion, label: labelDeSeccion(p.seccion), duracionSeg: dur, beacon });
    }
  }, []);
  // Cambio de sección: cierra la anterior (si es distinta) y arranca el reloj de la nueva.
  useEffect(() => {
    if (!clientMode) return;
    const p = permaRef.current;
    if (p.seccion && p.seccion !== screen) flushPermanencia(false);
    permaRef.current = { seccion: screen, t0: Date.now() };
  }, [clientMode, screen, flushPermanencia]);
  // Cierre de pestaña / salida del canal: vuelca la sección en curso.
  useEffect(() => {
    if (!clientMode) return undefined;
    const onHide = () => flushPermanencia(true);
    window.addEventListener('pagehide', onHide);
    return () => { window.removeEventListener('pagehide', onHide); flushPermanencia(false); };
  }, [clientMode, flushPermanencia]);

  // PAGOS REALES de Membership para «Mi perfil» (Ann 2026-07-20: «mis facturas, etc»).
  // Se piden solo al abrir la pantalla: es una llamada a WordPress, no se hace al entrar.
  const [cambiarPassOpen, setCambiarPassOpen] = useState(false);
  const [bajaSolicitada, setBajaSolicitada] = useState(false);
  // BAJA real: registra la baja en el CRM (fecha y hora = lo que vale legalmente),
  // avisa a la dirección y lleva a la gestión de la suscripción en Membership,
  // que es donde se para el cobro de Stripe.
  const pedirBaja = async () => {
    const ok = window.confirm(t('perfil.confirmBaja'));
    if (!ok) return;
    try {
      const resp = await axios.post('/api/canal/suscripcion/cancelar');
      const d = (resp && resp.data) || {};
      // Ann 2026-09-10: NUNCA decirle «hecho» si no está hecho, y no mandarla a otra página a
      // rematar la baja: allí no hay ninguna acción y la baja se perdía.
      if (d.completada) {
        setBajaSolicitada(true);
        window.alert(d.acceso_hasta ? t('perfil.bajaHechaHasta', { hasta: d.acceso_hasta }) : t('perfil.bajaHecha'));
      } else {
        window.alert(t('perfil.bajaNoCompletada'));
      }
    } catch (e) {
      window.alert(t('perfil.bajaError'));
    }
  };
  const [perfilDatos, setPerfilDatos] = useState(null);
  useEffect(() => {
    if (screen !== 'perfil' || perfilDatos) return;
    axios.get('/api/client/mi-perfil')
      .then(r => setPerfilDatos((r && r.data) || null))
      .catch(() => setPerfilDatos(null));
  }, [screen]); // eslint-disable-line
  const [misPagos, setMisPagos] = useState(null);
  const [misPagosCargando, setMisPagosCargando] = useState(false);
  useEffect(() => {
    if (screen !== 'perfil' || misPagos || misPagosCargando) return;
    setMisPagosCargando(true);
    import('./canalApi').then(m => (m.getMisPagos ? m.getMisPagos() : Promise.resolve(null)))
      .then(r => {
        // ⚠️ SOLO EN LOCAL (localhost): datos de EJEMPLO para poder ver el diseño
        // acabado mientras el Mongo local no tiene conexión con Membership.
        // En producción NUNCA entra aquí: si no hay datos, la pantalla lo dice.
        const esLocal = typeof window !== 'undefined'
          && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
        if (!r && esLocal) {
          return setMisPagos({
            suscripcion: {
              plan: 'Club', detalles: 'Mensual', importe: '63.00', moneda: 'EUR',
              forma_pago: 'Stripe', prueba: 'Sin periodo de prueba',
              empieza: '18/07/2026', caduca: '18/08/2026', estado: 'Activa',
            },
            facturas: [
              { numero: 'MPD-2026-0184', fecha: '18/07/2026', importe: '63.00', moneda: 'EUR',
                forma_pago: 'Stripe', estado: 'Pagada', pdf_url: '#' },
              { numero: 'MPD-2026-0151', fecha: '18/06/2026', importe: '63.00', moneda: 'EUR',
                forma_pago: 'Stripe', estado: 'Pagada', pdf_url: '#' },
            ],
          });
        }
        return setMisPagos(r || null);
      })
      .catch(() => setMisPagos(null))
      .finally(() => setMisPagosCargando(false));
  }, [screen]); // eslint-disable-line
  const [reservaFisioOpen, setReservaFisioOpen] = useState(false); // botón especial cabecera: formulario cita fisio
  const [showRewards] = useState(true); // feature flag (hoy activado)
  const [data, setData] = useState({}); // datos reales del canal (read-only, Fase 1)
  const [dataLoaded, setDataLoaded] = useState(false); // true una vez que loadCanalData ha terminado
  // Config del área «Aplicaciones terapéuticas» (apps agrupadas por sub-enunciado).
  // Si es null (no hay o falla), RutinasMefir cae a su home fijo. (Ann 2026-07-25)
  const [appsConfig, setAppsConfig] = useState(null);
  useEffect(() => {
    let vivo = true;
    getCanalApps().then(c => { if (vivo) setAppsConfig(c); }).catch(() => {});
    return () => { vivo = false; };
  }, []);
  const [playingItem, setPlayingItem] = useState(null); // ítem que se reproduce (vídeo real)
  const [favSet, setFavSet] = useState(new Set()); // claves `${tipo}:${contenidoId}` favoritas
  const [favMeta, setFavMeta] = useState({});      // clave -> título (para el aviso de sustituir)
  const [replacePrompt, setReplacePrompt] = useState(null); // { nuevo: item } cuando ya hay 3 favoritos
  const [actividad, setActividad] = useState(null); // { ultima, conteo_modalidades } para la clase sugerida
  // SEMANA DE CELEBRACIÓN de lanzamiento (Ann 2026-07-13): la bienvenida y el botón de
  // confeti se mantienen una semana entera (hasta el 2026-07-21 incl.). Después, se apagan solos.
  const celebracionActiva = clientMode && (() => {
    try { return new Date() < new Date('2026-07-22T00:00:00'); } catch (e) { return false; }
  })();
  // Bienvenida con confetti: 1 vez por sesión mientras dure la semana de celebración.
  const [bienvenida, setBienvenida] = useState(null); // null | 'card' (al llegar) | 'confeti' (botón cabecera)
  // FUNDAMENTAL (Ann 2026-07-13): la bienvenida sale UNA sola vez a la persona (su PRIMERA
  // entrada) y NUNCA más. Se marca en localStorage (persiste entre sesiones/logins), no en
  // sessionStorage (que reaparecía en cada sesión nueva).
  useEffect(() => {
    if (!clientMode || !dataLoaded) return;
    // FUNDAMENTAL (Ann 2026-07-16): la bienvenida/confeti sale UNA sola vez POR PERSONA y
    // nunca más — ni en otro login, ni otro móvil, ni incógnito. Antes solo se guardaba en
    // localStorage (por navegador) → volvía a salir «todo el tiempo». Ahora se comprueba en
    // el SERVIDOR (evento bienvenida_vista, único por usuario) y se registra al mostrarla.
    // El botón de la cabecera sigue toda la semana para relanzar confeti a voluntad.
    let cancel = false;
    (async () => {
      try { if (localStorage.getItem('vc_bienvenida_vista')) return; } catch (e) { /* noop */ }
      // Comprobación en el servidor (por persona). Si ya la vio → no sale y se marca local.
      const yaVista = await getBienvenidaVista();
      if (cancel) return;
      if (yaVista) { try { localStorage.setItem('vc_bienvenida_vista', '1'); } catch (e) { /* noop */ } return; }
      try { localStorage.setItem('vc_bienvenida_vista', '1'); } catch (e) { /* noop */ }
      track('bienvenida_vista'); // registra en el servidor → nunca más a esta persona
      setBienvenida('card'); // se cierra con "Entrar" o clic fuera
    })();
    return () => { cancel = true; };
  }, [clientMode, dataLoaded]);

  // Carga datos reales del backend; cada pieza es null si no hay sesión/backend.
  useEffect(() => {
    let alive = true;
    loadCanalData().then(d => {
      if (!alive) return;
      setData(d);
      setDataLoaded(true);
      const set = new Set((d.favoritos || []).map(f => `${f.tipo}:${f.contenido_id}`));
      setFavSet(set);
      const meta = {};
      (d.favoritos || []).forEach(f => { meta[`${f.tipo}:${f.contenido_id}`] = f.titulo; });
      setFavMeta(meta);
      setActividad(d.actividad || null);
    }).catch(() => { if (alive) setDataLoaded(true); });
    return () => { alive = false; };
  }, []);

  // ── Favoritos (máximo 3 clases por cliente; Ann 2026-07-13) ──
  const MAX_FAVS = 3;
  const favKey = (item) => item && item.contenidoId ? `${item.tipo}:${item.contenidoId}` : null;
  // ── EL CORAZÓN VA POR CONTENIDO, no por (tipo:contenido) ──────────────────
  // Ann 2026-07-18: «si le doy al corazón no se marca ni se desmarca; debería
  // ser así de fácil: aparece o no en favoritos, chin pum». CAUSA: el mismo
  // vídeo llega con `tipo` distinto según la sección (relajacion / area /
  // complemento…) y el corazón miraba la llave entera → parecía sin marcar,
  // marcaba duplicados y no desmarcaba. AHORA: marcado = el CONTENIDO está en
  // favoritos con cualquier tipo; desmarcar = quitar TODAS sus llaves.
  const _clavesDe = (cid) => Array.from(favSet).filter(k => k.endsWith(':' + String(cid)));
  const isFav = (item) => !!(item && item.contenidoId) && _clavesDe(item.contenidoId).length > 0;

  // Añade un favorito (optimista, con revert si el backend falla).
  const doAddFav = async (item) => {
    const k = favKey(item);
    setFavSet(prev => { const n = new Set(prev); n.add(k); return n; });
    setFavMeta(prev => ({ ...prev, [k]: item.nombre }));
    const ok = await addFavorito({ tipo: item.tipo, contenidoId: item.contenidoId, titulo: item.nombre });
    if (!ok) {
      setFavSet(prev => { const n = new Set(prev); n.delete(k); return n; });
      setFavMeta(prev => { const m = { ...prev }; delete m[k]; return m; });
    }
    return ok;
  };

  // Quita un favorito por su clave `${tipo}:${contenidoId}`.
  const doRemoveFavByKey = async (key, titulo) => {
    const i = key.indexOf(':');
    const tipo = key.slice(0, i);
    const contenidoId = key.slice(i + 1);
    setFavSet(prev => { const n = new Set(prev); n.delete(key); return n; });
    setFavMeta(prev => { const m = { ...prev }; delete m[key]; return m; });
    const ok = await removeFavorito(tipo, contenidoId);
    if (!ok) { // revertir
      setFavSet(prev => { const n = new Set(prev); n.add(key); return n; });
      setFavMeta(prev => ({ ...prev, [key]: titulo }));
    }
    return ok;
  };

  const toggleFav = async (item) => {
    const k = favKey(item);
    if (!k) return; // sin id estable (mock/demo) → no persiste
    // Desmarcar = fuera TODAS las llaves de ese contenido (chin pum).
    const claves = _clavesDe(item.contenidoId);
    if (claves.length) { for (const kk of claves) { await doRemoveFavByKey(kk, item.nombre); } return; }
    // Límite SOLO para CLASES del Método (máx 3). Relajaciones, vida diaria y
    // 'Conoce el método' son ILIMITADAS.
    if (item.tipo === 'clase') {
      const clasesFav = Array.from(favSet).filter(key => key.startsWith('clase:'));
      if (clasesFav.length >= MAX_FAVS) { setReplacePrompt({ nuevo: item }); return; }
    }
    await doAddFav(item);
  };

  // El cliente eligió en el aviso qué favorito sustituir por el nuevo.
  const confirmReplace = async (oldKey) => {
    const nuevo = replacePrompt?.nuevo;
    setReplacePrompt(null);
    if (!nuevo || !oldKey) return;
    const okDel = await doRemoveFavByKey(oldKey, favMeta[oldKey]);
    if (okDel) await doAddFav(nuevo);
  };

  // ── Actividad: visionado de una clase (completada >2/3 o a medias) ──
  const marcarClaseHecha = (item, opts = {}) => {
    if (!item || item.tipo !== 'clase' || !item.contenidoId) return;
    const completada = opts.completada !== false; // por defecto true (llamada del 2/3)
    const progreso = typeof opts.progreso === 'number' ? opts.progreso : (completada ? 0.67 : 0.3);
    const nowIso = new Date().toISOString();
    const evento = {
      contenido_id: item.contenidoId,
      nivel: (item.nivel || '').toLowerCase(),
      modalidad: item.modalidad,
      titulo: item.nombre,
      progreso,
      completada,
      fecha: nowIso,
    };
    // Optimista: refleja el visionado ya (sugerencia + saludo) sin recargar.
    setActividad(prev => {
      const eventos = [evento, ...((prev && prev.eventos) || [])];
      if (!completada) {
        return { ...(prev || {}), eventos };
      }
      const conteo = { Apertura: 0, Paralelo: 0, Mixta: 0, ...((prev && prev.conteo_modalidades) || {}) };
      if (item.modalidad in conteo) conteo[item.modalidad] += 1;
      return {
        ...(prev || {}),
        ultima: { contenido_id: item.contenidoId, nivel: evento.nivel, modalidad: item.modalidad, titulo: item.nombre },
        conteo_modalidades: conteo,
        eventos,
      };
    });
    registrarClaseHecha({
      contenidoId: item.contenidoId, nivel: item.nivel, modalidad: item.modalidad,
      titulo: item.nombre, progreso, completada,
    });
  };

  // La clienta DESMARCA una clase realizada (Ann 2026-07-15): anula el último
  // evento completado de ESA clase (API) y lo refleja al momento en local
  // (rutina/racha/sugerencia dejan de contarla, sin recargar).
  const desmarcarClase = (item) => {
    if (!item || !item.contenidoId) return;
    setActividad(prev => {
      if (!prev) return prev;
      const eventos = [...(prev.eventos || [])];
      const i = eventos.findIndex(e => e.contenido_id === item.contenidoId && e.completada);
      if (i >= 0) eventos[i] = { ...eventos[i], completada: false, desmarcada: true };
      const conteo = { ...(prev.conteo_modalidades || {}) };
      if (item.modalidad in conteo && conteo[item.modalidad] > 0) conteo[item.modalidad] -= 1;
      const ultima = prev.ultima && prev.ultima.contenido_id === item.contenidoId
        ? (eventos.find(e => e.completada) || null)
        : prev.ultima;
      return { ...prev, eventos, conteo_modalidades: conteo, ultima };
    });
    desmarcarClaseHecha(item.contenidoId);
  };

  // ÚNICA VERDAD: la preview del admin muestra EXACTAMENTE lo mismo que el cliente
  // real (solo contenido publicado de verdad). Se eliminó la DEMO/mock para que no
  // haya "verdad paralela": si no hay nada publicado, se ve vacío, igual que el
  // cliente. (Antes el admin veía datos de ejemplo que el cliente nunca tenía.)
  const clasesSemana = data.semana?.clases || [];
  const extras = data.extras || [];
  const relajaciones = data.relajaciones || [];
  const areas = data.areas || [];
  const charlas = data.charlas || [];
  const clasesBiblioteca = data.clases || [];
  // Pool de clases para resolver favoritos y para la biblioteca: librería suelta
  // + clases de la semana publicada (deduplicadas por id). Así una clase marcada
  // favorita desde la semana SIEMPRE se resuelve aunque la librería suelta esté
  // vacía, y la biblioteca muestra al menos las clases reales de la semana.
  const clasesPool = (() => {
    const map = new Map();
    [...clasesBiblioteca, ...clasesSemana].forEach((c) => {
      if (!c) return;
      const id = String(c.contenidoId ?? c.id);
      if (id && !map.has(id)) map.set(id, c);
    });
    return Array.from(map.values());
  })();
  // Favoritos EN VIVO: derivados de favSet/favMeta (optimistas) en lugar de
  // data.favoritos, para que "Tus favoritas" se actualice al instante al marcar
  // un corazón, SIN recargar la página (recargar saca del canal).
  // video_url/thumbnail_url vienen resueltos del backend (aunque la clase no esté
  // publicada) y deben SOBREVIVIR a esta reconstrucción optimista → se reinyectan
  // desde data.favoritos por clave (encargo Guardián 2026-07-13).
  const _favSrv = {};
  (data.favoritos || []).forEach(f => { _favSrv[`${f.tipo}:${f.contenido_id}`] = f; });
  const favoritosLive = Array.from(favSet).map((k) => {
    const i = k.indexOf(':');
    const srv = _favSrv[k] || {};
    return {
      tipo: k.slice(0, i), contenido_id: k.slice(i + 1),
      titulo: favMeta[k] || srv.titulo || '',
      video_url: srv.video_url || null, thumbnail_url: srv.thumbnail_url || null,
    };
  });
  const comentarioSemanal = data.semana?.comentarioSemanal || '';
  const consejoVideo = data.semana?.consejoVideo || null;
  const consejoPersona = data.semana?.consejoPersona || null;
  const semanaInicio = data.semana?.fechaInicio || null;
  const semanaFin = data.semana?.fechaFin || null;
  const hayDatosReales = !!(data.semana || data.clases || data.relajaciones || data.extras);
  const anuncio = data.anuncio || null;
  const botones = data.botones || [];
  // Sin contenido real (cliente o preview): mensaje honesto, jamás demo.
  const clienteSinContenido = dataLoaded && !hayDatosReales;

  // Al cambiar de tipo de usuario, reset a su pantalla de inicio
  const switchUser = (t) => {
    setUserType(t);
    setScreen(t === 'metodo' ? 'inicio' : 'pautas');
  };

  // Reproduce el ítem clicado (con su vídeo real si lo tiene)
  const goPlayer = async (item) => {
    let toPlay = item;
    // Los ítems del menú "Clases MPD" (agregador /mpd/agregado) son TARJETAS sin
    // vídeo: traen id + tipo + área, pero NO la URL. Antes, al abrir uno que NO
    // fuese 'clase' (una ficha de ÁREA como «Aplicaciones Terapéuticas», una
    // relajación, un consejo…), el reproductor se quedaba sin vídeo y caía al DEMO
    // ("Apertura torácica y movilidad de cadera"). Se resuelve su vídeo real contra
    // los pools ya cargados (que SÍ lo traen), casando por id. Las clases siguen
    // resolviéndose por play-url (abajo). Fix Ann 2026-07-25.
    if (item && item.esMpd && item.tipo !== 'clase' && !(item.video || item.embedUrl)) {
      const sameId = (x) => x && String(x.contenidoId ?? x.id) === String(item.contenidoId ?? item.id);
      let real = null;
      for (const a of (areas || [])) { real = (a.items || []).find(sameId); if (real) break; }
      if (!real) real = [...(relajaciones || []), ...(extras || []), ...(charlas || []), ...clasesPool].find(sameId);
      if (real) toPlay = { ...item, video: real.video || null, embedUrl: real.embedUrl || null, thumbnail: item.thumbnail || real.thumbnail || null };
    }
    // CLASES: pedir SIEMPRE el enlace fresco por play-url (TTL 2h), aunque el ítem del
    // pool no traiga vídeo precargado (p. ej. una favorita cuya clase no está en la
    // biblioteca cargada). Así toda clase favorita se abre. Fix Ann 2026-07-13.
    if (toPlay && toPlay.tipo === 'clase' && toPlay.contenidoId) {
      const fresh = await getClasePlayUrl(toPlay.contenidoId);
      if (fresh) toPlay = { ...toPlay, video: fresh };
    }
    const reproducible = !!(toPlay && (toPlay.video || toPlay.embedUrl));
    setPlayingItem(reproducible ? toPlay : null);
    setScreen('player');
    // Contador de vistas: solo cliente real (no la preview del admin).
    if (clientMode && reproducible) {
      registrarReproduccion({ contenidoId: item.contenidoId || item.id, tipo: item.tipo || '', titulo: item.nombre || '' });
    }
  };
  const isPlayer = screen === 'player';

  // "También puede interesarte": otro contenido (relajaciones, extras, charlas)
  // para sugerir debajo del vídeo cuando NO es una clase. Excluye el que se ve.
  const sugerenciasBase = [...relajaciones, ...extras, ...charlas]
    .filter(s => s && (s.video || s.embedUrl) && (!playingItem || (s.contenidoId || s.id) !== (playingItem.contenidoId || playingItem.id)));
  // Si el vídeo activo es un testimonio, los testimonios van primero (los 3 de arriba).
  const sugerencias = (playingItem && esTestimonio(playingItem)
    ? [...sugerenciasBase].sort((a, b) => (esTestimonio(b) ? 1 : 0) - (esTestimonio(a) ? 1 : 0))
    : sugerenciasBase
  ).slice(0, 6);

  // Menú dinámico: Inicio + Clases MPD + LAS ÁREAS REALES del admin (/canal-areas/cliente) +
  // En directo + Testimonios + Novedades. Antes el menú estaba hardcodeado y no reflejaba
  // lo configurado en el administrador de áreas (por eso faltaban "30 min", etc.).
  const navItems = useMemo(() => {
    // El NOMBRE DEL MENÚ de cada área es su `label` (editable en el admin), distinto del
    // título de la sección (titulo_canal) que se pinta en la cabecera de la pantalla.
    // Regla Ann: un área SOLO sale en el menú si tiene vídeos publicados (con contenido).
    // La membresía ya la filtra el backend (/canal-areas/cliente); aquí añadimos el
    // filtro de contenido para no colar áreas vacías (que darían "no disponible").
    const areaItems = (areas || [])
      .filter(a => a && a.key && (a.items || []).length > 0)
      .map(a => ({ id: `area:${a.key}`, label: a.label || a.titulo_canal || a.key }));
    return [
      { id: 'inicio', labelKey: 'nav.inicio' },
      { id: 'mpd', labelKey: 'nav.clasesMpd' },
      ...areaItems,
      // Aplicaciones terapéuticas (Instituto MEFIR): índice de apps guiadas.
      // De momento una sola entrada ("Respiraciones terapéuticas" → RutinasMefir).
      { id: 'apps', label: (appsConfig?.nombre_menu || 'Aplicaciones terapéuticas') },
      { id: 'cat:endirecto', labelKey: 'nav.enDirecto', soon: true },
      // { id: 'cat:testimonios', label: 'Testimonios' },  // desactivado (Ann 2026-07-14): fuera del menú — ya salen abajo del dashboard; volverá cuando haya testimonios de otro tipo
      // { id: 'novedades', label: 'Nuevo en Pilar Domínguez' },  // desactivado (Ann): Nuevo en Pilar Domínguez fuera del menú
    ];
  }, [areas, appsConfig]);

  const renderScreen = () => {
    if (isPlayer) return <ScreenReproductor item={playingItem} isFav={isFav(playingItem)} onToggleFav={() => toggleFav(playingItem)} onClaseHecha={marcarClaseHecha} onClaseDeshecha={desmarcarClase} onBack={() => setScreen(userType === 'metodo' ? 'inicio' : 'pautas')} sugerencias={sugerencias} onPlay={goPlayer} vistas={data.vistas || {}} marcaAgua={nombreCompletoCliente || nombreCliente || emailReal || clienteEmail || ''} />;
    // Área seleccionada desde el menú (id 'area:<key>'). Reutiliza VideoCard + reproductor
    // protegido (goPlayer → ScreenReproductor): nada descargable, sin exponer URL cruda.
    if (typeof screen === 'string' && screen.startsWith('area:')) {
      const _area = areas.find(a => a.key === screen.slice(5));
      return <ScreenArea area={_area} cargando={!dataLoaded} onPlay={goPlayer} isFav={isFav} onToggleFav={toggleFav} />;
    }
    // ── Aplicaciones terapéuticas (Instituto MEFIR) ──
    // Índice de apps guiadas. De momento UNA entrada: "Respiraciones terapéuticas",
    // que abre el módulo React nativo RutinasMefir (Kegel + 4-7-8 + suspiro +
    // coherencia). Pensado para crecer con más apps sin tocar el menú.
    // Aplicaciones terapéuticas: MIENTRAS SOLO HAYA UNA app (respiraciones), se entra
    // DIRECTO a las respiraciones (Ann 23-jul) — sin fila/índice intermedio. La cabecera
    // + lema quedan a la vista y la app se abre debajo.
    // 🔴 Cuando haya MÁS apps: recuperar el índice en listado (fila con flecha que
    //    desplegaba 'apps:rutinas' por debajo). El código anterior está en el historial.
    if (screen === 'apps' || screen === 'apps:rutinas') {
      return (
        <>
          <div className="vc-page-head vc-reveal" style={{ paddingTop: 24 }}>
            <h1><b>{appsConfig?.titulo_canal || 'Aplicaciones terapéuticas'}</b></h1>
            {appsConfig?.subtitulo_canal ? <p>{appsConfig.subtitulo_canal}</p> : null}
          </div>
          <section style={{ maxWidth: 720, margin: '8px auto 0 0', padding: '0 0 24px' }}>
            <RutinasMefir config={appsConfig} />
          </section>
        </>
      );
    }
    switch (screen) {
      case 'inicio':
      case 'metodo':
        return <ScreenInicioMetodo cargando={!dataLoaded} onPlay={goPlayer} onNavigate={setScreen}
          clasesSemana={clasesSemana} extras={extras} relajaciones={relajaciones} comentarioSemanal={comentarioSemanal} anuncio={anuncio} consejoVideo={consejoVideo} consejoPersona={consejoPersona}
          favoritos={favoritosLive} clasesData={clasesPool} relajData={relajaciones} extrasData={extras} actividad={actividad} semanaInicio={semanaInicio} semanaFin={semanaFin} charlas={charlas} nombre={nombreCliente} areas={areas} isFav={isFav} onToggleFav={toggleFav} />;
      case 'pautas': return <ScreenPautas onPlay={goPlayer} accesoMetodo={accesoMetodo} nombre={nombreCliente} />;
      case 'biblioteca': return <ScreenBiblioteca onPlay={goPlayer}
        clasesData={clasesPool} extrasData={extras} relajData={relajaciones} charlasData={charlas} />;
      case 'mpd': return <ScreenClasesMPD cargando={!dataLoaded} clasesSemana={clasesSemana} favoritos={favoritosLive} areas={areas}
        clasesData={clasesPool} relajData={relajaciones} extrasData={extras} charlasData={charlas}
        onPlay={goPlayer} isFav={isFav} onToggleFav={toggleFav} />;
      // `datos={null}` a propósito: los pagos vienen de Membership y AÚN NO LLEGAN
      // (encargo en docs/ENCARGO_MEMBERSHIP_A_CANAL.md). La pantalla muestra su
      // estado vacío honesto en vez de inventarse un plan o una factura.
      case 'perfil': return <ScreenMiPerfil nombre={nombreCompletoCliente || nombreCliente} email={emailReal || clienteEmail} plan={membresia} datos={misPagos} cargando={misPagosCargando} onCambiarPassword={() => setCambiarPassOpen(true)} onDarseDeBaja={pedirBaja} bajaSolicitada={bajaSolicitada} perfil={perfilDatos} clientMode={clientMode} />;
      case 'progreso': return <ScreenProgreso />;
      case 'favoritos': return <ScreenFavoritos onPlay={goPlayer} favoritos={favoritosLive}
        clasesData={clasesPool} relajData={relajaciones} extrasData={extras} charlasData={charlas}
        areas={areas} isFav={isFav} onToggleFav={toggleFav} />;
      case 'novedades': return <ScreenNovedades novedades={data.novedades} cargando={!dataLoaded} />;
      case 'recompensas': return showRewards ? <ScreenRecompensas nombre={nombreCliente} /> : <ScreenInicioMetodo onPlay={goPlayer} />;
      case 'consejos': return <ScreenSimple title={t('prox.consejosT')} text={t('prox.consejosX')} />;
      case 'fisio': return <ScreenSimple title={t('prox.fisioT')} text={t('prox.fisioX')} />;
      // ── Categorías de la barra superior (Netflix) ──
      case 'cat:relajaciones': return <ScreenArea area={{ label: t('cats.relajLabel'), descripcion: t('cats.relajDesc'), items: relajaciones }} cargando={!dataLoaded} onPlay={goPlayer} isFav={isFav} onToggleFav={toggleFav} />;
      case 'cat:consejos': return <ScreenArea area={{ label: t('cats.consejosLabel'), descripcion: t('cats.consejosDesc'), items: extras }} cargando={!dataLoaded} onPlay={goPlayer} isFav={isFav} onToggleFav={toggleFav} />;
      case 'cat:testimonios': return <ScreenArea area={{ label: t('cats.testiLabel'), descripcion: t('cats.testiDesc'), items: (charlas || []).filter(esTestimonio) }} cargando={!dataLoaded} onPlay={goPlayer} isFav={isFav} onToggleFav={toggleFav} />;
      case 'cat:30min': return <ScreenSimple title={t('prox.c30T')} text={t('prox.c30X')} />;
      case 'cat:crecimiento': return <ScreenSimple title={t('prox.crecT')} text={t('prox.crecX')} />;
      case 'cat:corporativo': return <ScreenSimple title={t('prox.corpT')} text={t('prox.corpX')} />;
      // Cliente REAL: nada de maqueta («42 conectados» era inventado) → Próximamente honesto.
      // La maqueta ScreenEnDirecto queda SOLO en la preview del admin (staff), marcada WIP.
      case 'cat:endirecto': return clientMode
        ? <ScreenSimple title={t('prox.dirT')} text={t('prox.dirX')} />
        : <ScreenEnDirecto />;
      case 'mi:bonificaciones': return <ScreenSimple title={t('prox.bonifT')} text={t('prox.bonifX')} />;
      default: return <ScreenInicioMetodo cargando={!dataLoaded} onPlay={goPlayer} onNavigate={setScreen}
        clasesSemana={clasesSemana} extras={extras} relajaciones={relajaciones} comentarioSemanal={comentarioSemanal} consejoVideo={consejoVideo} consejoPersona={consejoPersona}
        favoritos={favoritosLive} clasesData={clasesPool} relajData={relajaciones} extrasData={extras} actividad={actividad} semanaInicio={semanaInicio} semanaFin={semanaFin} charlas={charlas} nombre={nombreCliente} areas={areas} isFav={isFav} onToggleFav={toggleFav} />;
    }
  };

  // Aviso "ya tienes 3 favoritas · ¿cuál sustituyes?" (se muestra en cliente y en preview admin)
  const replaceModal = replacePrompt ? (
    <div className="vc-modal-overlay" onClick={() => setReplacePrompt(null)}>
      <div className="vc-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="vc-modal-title">{t('modal.titulo')}</h3>
        <p className="vc-modal-text">
          <Trans t={t} i18nKey="modal.texto" values={{ nombre: catalogoCa(replacePrompt.nuevo?.nombre) }} components={{ b: <b /> }} />
          <br />{t('modal.texto2')}
        </p>
        <div className="vc-modal-options">
          {Array.from(favSet).filter((key) => key.startsWith('clase:')).map((key) => (
            <button key={key} className="vc-modal-opt" onClick={() => confirmReplace(key)}>
              <I.IcHeart /><span>{favMeta[key] || t('modal.claseGuardada')}</span>
            </button>
          ))}
        </div>
        <button className="vc-modal-cancel" onClick={() => setReplacePrompt(null)}>{t('modal.cancelar')}</button>
      </div>
    </div>
  ) : null;

  // ── Modo cliente: canal completo sin barra admin, fullscreen ──
  if (clientMode) {
    // 🔴 DEUDOR (Ann 2026-08-04 · Fase 1 del embudo de cobro): si la clienta tiene
    // un recibo devuelto, ve un aviso CLARO con el mes y qué hacer (consultar con
    // su banco). Manda sobre el contenido: para eso está bloqueada.
    if (bloqueoDeuda && bloqueoDeuda.bloqueado) {
      return (
        <div className="vc-shell vc-shell--client">
          <main className="vc-main">
            <div className="vc-page-head vc-reveal" style={{ textAlign: 'center', padding: '56px 20px', maxWidth: 620, margin: '0 auto' }}>
              <h1 style={{ marginBottom: 14, color: '#077069' }}>{t('perfil.pausaT')}</h1>
              <p style={{ fontSize: 16, lineHeight: 1.55 }}>{bloqueoDeuda.mensaje}</p>
              <p style={{ fontSize: 14, color: '#6b7280', marginTop: 16 }}>
                {t('perfil.pausaTexto')}
              </p>
            </div>
          </main>
        </div>
      );
    }
    // Cliente real sin contenido (sin acceso al canal o aún vacío): mensaje honesto,
    // JAMÁS la demo. La demo solo existe para la previsualización del admin.
    // Excepción SOLO en desarrollo local (npm start): permite ver el canal para maquetar.
    // Doble candado: en el build de producción NODE_ENV==='production' → SIEMPRE false
    // (código muerto), y además el hostname de prod nunca coincide. El gate honesto queda intacto.
    const esDesarrolloLocal = process.env.NODE_ENV !== 'production' &&
      typeof window !== 'undefined' &&
      /^(localhost|127\.|192\.168\.|10\.)/.test(window.location.hostname);
    if (clienteSinContenido && !esDesarrolloLocal) {
      // ══ NADIE SE QUEDA ENCERRADO EN SU PROPIA CUENTA (Ann 2026-09-26) ══════
      // MEDIDO haciendo un alta real: quien se cae al ir a pagar entra y solo ve
      // «Canal no disponible… póngase en contacto», SIN botón, SIN enlace y sin
      // forma de terminar. Es el caso de Anna y Marta (22-09), que lo intentaron
      // seis veces y acabaron escribiendo ellas.
      // Ahora hay DOS pantallas, porque son dos situaciones distintas:
      //   · le falta pagar  → se le ofrece TERMINAR, que es lo único que la
      //                       desatasca (y de paso es una venta que se salva)
      //   · cualquier otra  → el mensaje honesto, pero con enlaces de VERDAD
      //                       en los que se puede pinchar, no un «contacte con».
      // 🔴 Esto NO abre el canal a nadie: quien decide el acceso sigue siendo el
      //    servidor (`cliente_es_miembro_canal`). Aquí solo cambia el MENSAJE.
      // MEDIDO EN PRODUCCIÓN (26-09, tras el despliegue): las clases `vc-btn`
      // NO pintan nada en esta pantalla — fondo transparente, borde 0px,
      // relleno 0px. Los tres salían como texto blanco suelto sobre negro, y
      // el más importante («Terminar mi pago») no parecía pulsable.
      // Una clienta atascada no va a probar a pinchar un texto por si acaso.
      // Los estilos van aquí dentro para que no puedan volver a faltar.
      const estilosSalidas = `
        .nd-btn { display:inline-block; padding:13px 26px; border-radius:10px;
                  font-weight:700; font-size:15px; text-decoration:none;
                  min-width:240px; text-align:center; }
        .nd-btn--pagar { background:#0f766e; color:#fff; border:1px solid #0f766e; }
        .nd-btn--pagar:hover { background:#0b5f59; }
        .nd-btn--ayuda { background:transparent; color:#e6f2f0;
                         border:1px solid rgba(230,242,240,.45); }
        .nd-btn--ayuda:hover { background:rgba(230,242,240,.10); }
      `;
      const ayuda = (
        <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column',
                      alignItems: 'center', gap: 10 }}>
          <a className="nd-btn nd-btn--ayuda"
             href={URL_AYUDA_WHATSAPP} target="_blank" rel="noreferrer">
            {t('nodisp.whatsapp')}
          </a>
          <a className="nd-btn nd-btn--ayuda"
             href={URL_AYUDA_EMAIL}>
            {t('nodisp.email')}
          </a>
        </div>
      );
      return (
        <div className="vc-shell vc-shell--client">
          <main className="vc-main">
            <div className="vc-page-head vc-reveal" style={{ textAlign: 'center', padding: '48px 16px' }}>
              <style>{estilosSalidas}</style>
              {pagoPendiente ? (
                <>
                  <h1><Trans t={t} i18nKey="nodisp.pagoTitulo" components={{ b: <b /> }} /></h1>
                  <p>{t('nodisp.pagoTexto')}</p>
                  {/* [Ann 2026-10-23] el pago lo crea el CRM en Stripe sobre SU ficha (quien vuelve ya no choca con
                      «ya existe» en la web). Si el CRM no puede, la web sigue como respaldo. */}
                  <a className="nd-btn nd-btn--pagar"
                     style={{ marginTop: 18 }}
                     href="#pago"
                     data-testid="btn-terminar-pago"
                     onClick={async (ev) => {
                       ev.preventDefault();
                       try {
                         const r = await crearCheckoutReingreso();
                         if (r?.data?.url) { window.location.href = r.data.url; return; }
                       } catch (e) { /* sin respaldo en WordPress: se le dice cómo pedir ayuda */ }
                       window.alert(t('nodisp.pagoAyuda'));
                     }}>
                    {t('nodisp.pagoBoton')}
                  </a>
                  <p style={{ marginTop: 26, opacity: 0.75 }}>{t('nodisp.pagoAyuda')}</p>
                  {ayuda}
                </>
              ) : (
                <>
                  <h1><Trans t={t} i18nKey="nodisp.titulo" components={{ b: <b /> }} /></h1>
                  <p>{t('nodisp.texto')}</p>
                  <p style={{ marginTop: 26 }}><b>{t('nodisp.ayudaTitulo')}</b></p>
                  <p style={{ opacity: 0.75 }}>{t('nodisp.ayudaTexto')}</p>
                  {ayuda}
                </>
              )}
            </div>
          </main>
        </div>
      );
    }
    return (
      <div className="vc-shell vc-shell--client vcn">
        {bienvenida && <VcBienvenida nombre={nombreCliente} modo={bienvenida} onDone={() => setBienvenida(null)} />}
        {/* PROVISIONAL (revisión): conmutador PC · tablet · móvil. Mete el canal
            en un marco del ancho del dispositivo (container query → dispara el
            responsive real, hamburguesa incluida). Quitar antes de publicar. */}
        {isPlayer ? (
          renderScreen()
        ) : (
          <div className={`vcn-frame vcn-dev-${device}`}>
            {/* Botón de CELEBRACIÓN flotando SOBRE la cabecera rotativa (Ann 2026-07-14):
                sin fila propia — 0px de alto añadido. Durante la semana de lanzamiento,
                el usuario puede lanzar confeti cuando quiera. */}
            {/* El bloque rotativo (portada) va arriba del menú en TODAS las secciones, y su
                contenido CAMBIA según la sección seleccionada (estilo Netflix/Movistar). */}
            {/* Botones ESPECIALES de la cabecera: acción preconfigurada ('reserva_fisio' abre
                el formulario de cita) o 'ir:<seccion>' (navega a esa sección). Sin acción → MPD. */}
            <div className="vc-rotativo-wrap">
              {celebracionActiva && (
                <button type="button" className="vc-fiesta-btn vc-fiesta-btn--flot" onClick={() => { setBienvenida('confeti'); track('confeti'); }}>
                  <I.IcSpark /> {t('bienvenida.fiesta')}
                </button>
              )}
              <HeroRotativo activo seccion={screen} onCtaInterna={(s) => {
                const acc = (s && s.accion) || '';
                if (acc === 'reserva_fisio') { setReservaFisioOpen(true); return; }
                if (acc.startsWith('whatsapp:')) {
                  const num = acc.slice('whatsapp:'.length).replace(/\D/g, '');
                  if (num) window.open(`https://wa.me/${num}`, '_blank', 'noopener,noreferrer');
                  return;
                }
                if (acc.startsWith('ir:')) { setScreen(acc.slice(3)); return; }
                setScreen('mpd');
              }} />
            </div>
            <TopNav screen={screen} setScreen={setScreen} onMiEspacio={() => setMiEspacioOpen(true)} inicial={inicialCliente} items={navItems} />
            <main className="vc-main vcn-main">
              {renderScreen()}
            </main>
            <MiEspacioDrawer
              open={miEspacioOpen} onClose={() => setMiEspacioOpen(false)}
              screen={screen} setScreen={setScreen}
              nombre={nombreCompletoCliente} inicial={inicialCliente} membresia={membresia}
              onMiPerfil={onMiPerfil} onSalir={onSalir} esClub={esClub}
              onOpinar={() => { valoracionVotoRef.current = false; valoracionManualRef.current = true; setValoracionReabierto(false); setValoracionEstrellas(0); setValoracionNombre(nombreCliente || ''); setValoracionOpen(true); }}
            />
            {reservaFisioOpen && <ReservaServicioModal onClose={() => setReservaFisioOpen(false)} />}
            {cambiarPassOpen && <ChangePasswordModal onClose={() => setCambiarPassOpen(false)} />}
            {valoracionOpen && (
              <ValoracionPopup
                nombre={valoracionNombre}
                reabierto={valoracionReabierto}
                estrellasPrevias={valoracionEstrellas}
                onGuardar={(v) => { valoracionVotoRef.current = true; enviarVotoValoracion({ estrellas: v.estrellas, comentario: v.comentario, fueAGoogle: v.fue_a_google, membresia }); }}
                onClose={() => { if (!valoracionVotoRef.current && !valoracionManualRef.current) valoracionAhoraNo(); valoracionManualRef.current = false; setValoracionOpen(false); }}
              />
            )}
          </div>
        )}
        {replaceModal}
      </div>
    );
  }

  // ── Modo admin: preview con barra de control y selector de dispositivo ──
  return (
    <div className="vc-shell">
      {/* Barra de control del preview (chrome de admin) */}
      <div className="vc-toolbar">
        <span className="vc-seg-label">Vista</span>
        <div className="vc-seg">
          <button className={userType === 'metodo' ? 'on' : ''} onClick={() => switchUser('metodo')}>Cliente Método</button>
          <button className={userType === 'paciente' ? 'on' : ''} onClick={() => switchUser('paciente')}>Paciente fisio</button>
        </div>
        <div className="vc-seg">
          <button className={device === 'desktop' ? 'on' : ''} onClick={() => setDevice('desktop')}><I.IcDesktop />Escritorio</button>
          <button className={device === 'tablet' ? 'on' : ''} onClick={() => setDevice('tablet')}><I.IcTablet />Tablet</button>
          <button className={device === 'movil' ? 'on' : ''} onClick={() => setDevice('movil')}><I.IcMobile />Móvil</button>
        </div>
        <div className="vc-spacer" />
        <span className={`vc-datatag ${hayDatosReales ? 'real' : ''}`} title={hayDatosReales ? 'Mostrando contenido real del canal' : 'Sin sesión/backend: datos de demostración'}>
          <span className="vc-datadot" />{hayDatosReales ? 'Datos del canal' : 'Demo'}
        </span>
        {userType === 'paciente' && (
          <div className={`vc-switch ${accesoMetodo ? 'on' : ''}`} onClick={() => setAccesoMetodo(v => !v)} title="Lo decide el fisio (flag accesoMetodo)">
            <span className="tg"><i /></span>Acceso al Método
          </div>
        )}
      </div>

      <div className="vc-stage">
        <div className={`vc-frame ${device === 'tablet' ? 'tablet' : device === 'movil' ? 'movil' : ''}`}>
          {isPlayer ? (
            renderScreen()
          ) : (
            <div className="vc-layout">
              <Sidebar userType={userType} accesoMetodo={accesoMetodo} screen={screen} setScreen={setScreen} botones={botones} areas={areas} nombre={nombreCompletoCliente} inicial={inicialCliente} />
              <main className="vc-main">
                <MobileTop userType={userType} inicial={inicialCliente} onMenu={() => setMenuOpen(true)} />
                {renderScreen()}
              </main>
            </div>
          )}
          {!isPlayer && (
            <MobileMenu
              open={menuOpen} onClose={() => setMenuOpen(false)}
              userType={userType} accesoMetodo={accesoMetodo}
              screen={screen} setScreen={setScreen} botones={botones} areas={areas}
              nombre={nombreCompletoCliente} inicial={inicialCliente} membresia={membresia}
            />
          )}
          {replaceModal}
        </div>
      </div>
    </div>
  );
};

// ── PREVIEW QA del reproductor propio (localhost / QA staff, sin datos reales) ──
export function ReproductorPreview() {
  const demo = {
    contenidoId: 'demo-player',
    nombre: 'Suave · Apertura',
    tipo: 'video', // no-clase → en la preview se ven las sugerencias con «Al acabar»
    nivel: 'suave',
    duracion: 10,
    material: 'Sin material',
    descripcion: 'Vídeo de DEMOSTRACIÓN del reproductor propio (contenido de prueba, no es una clase real).',
    video: 'https://download.blender.org/peach/bigbuckbunny_movies/BigBuckBunny_320x180.mp4',
    thumbnail: '',
    categorias: ['Método P. Domínguez', 'QA'],
  };
  const sugerencias = [
    { contenidoId: 'demo-sug-1', nombre: 'Estándar · Paralelo', zona: 'Método', duracion: 8, video: 'https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4' },
    { contenidoId: 'demo-sug-2', nombre: 'Relajación guiada', zona: 'Relajación', duracion: 5, video: 'https://www.w3schools.com/html/mov_bbb.mp4' },
  ];
  const [actual, setActual] = useState(demo);
  const [fav, setFav] = useState(false); // favorito de mentira, solo para la QA
  return (
    <div className="vc-shell" style={{ minHeight: '100vh' }}>
      <ScreenReproductor
        item={actual}
        onBack={() => setActual(demo)}
        isFav={fav}
        onToggleFav={() => setFav(f => !f)}
        sugerencias={sugerencias.filter((x) => x.contenidoId !== actual.contenidoId)}
        onPlay={(s) => setActual({ ...demo, ...s, tipo: 'otro' })}
        marcaAgua="Maria Prova"
      />
    </div>
  );
}

export default VisionClienteTab;
